import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Capacitor } from '@capacitor/core';
import { useClickOutside } from '../utils/useClickOutside';
import { collection, onSnapshot, doc, getDoc, serverTimestamp, deleteDoc, query, getDocs, where, updateDoc } from '../firebase.config';
import { enforcedAddDoc as addDoc, enforcedSetDoc as setDoc, setGlobalCompanyId } from '../utils/enforcedTracking';
import { useLanguage } from '../contexts/LanguageContext';
import { LimitReachedModal } from './UsageAlerts';

import InvoiceTemplate from './BillTemplates/InvoiceTemplate';
import InvoiceTemplateA5 from './BillTemplates/InvoiceTemplateA5';
import SimpleSalesTemplate from './BillTemplates/SimpleSalesTemplate';
import ModernSalesTemplate from './BillTemplates/ModernSalesTemplate';
import ChallanTemplate from './BillTemplates/ChallanTemplate';
import QuotationTemplate from './BillTemplates/QuotationTemplate';
import QuotationTemplateColored from './BillTemplates/QuotationTemplateColored';
import ReceiptTemplate from './BillTemplates/ReceiptTemplate';
import SalesReturnTemplate from './BillTemplates/SalesReturnTemplate';
import DebitNoteTemplate from './BillTemplates/DebitNoteTemplate';
import CreditNoteTemplate from './BillTemplates/CreditNoteTemplate';
import ActionButtons from './ActionButtons';
import { Wallet, ChevronDown, ClipboardCopy } from 'lucide-react';

import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, app } from '../firebase.config';
import { useEnhancedCompany } from '../contexts/EnhancedCompanyContext';
import WorkOrderLinkSelect from './WorkOrders/WorkOrderLinkSelect';
import { appendWorkOrderFields, appendLinkedDocument } from '../utils/workOrderLedger';
import { getCollectionForCompany, resolveCompanySignUrl, resolveCompanySealUrl, normalizeCompanyDocSnapshot } from '../utils/dataAdapter';
import { useCompanySwitchGuard } from '../hooks/useCompanySwitchGuard';
import {
  getBillLineItems,
  getBillLineItemId,
  resolveBillLineItemName,
  enrichBillRowForSave,
  enrichBillRowForPrint,
  findItemInMaster,
  getItemDisplayName,
  matchItemBySearchText,
  filterItemsForPicker,
  resolveRowItemBeforeSave,
} from '../utils/itemDisplayUtils';
import { useCompanyCurrency } from '../hooks/useCompanyCurrency';
import { formatCurrency, getCurrencySymbol } from '../utils/currencyUtils';
import { EmployeeCashManager } from '../utils/employeeCashManager';
import { useNotification } from '../contexts/NotificationContext';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { formatDateToCompanyFormat, getCompanyDateFormat } from '../utils/dateUtils';
import { useTableSort, SortableHeader } from '../utils/tableSort';
import { useTablePagination } from '../utils/tablePagination';
import PaginationControls from '../utils/PaginationControls';
import CompactFilterBar from './CompactFilterBar';
import AppSelect, { APP_SELECT_FILTER_TRIGGER } from './shared/AppSelect';
import LoadingScreen from './LoadingScreen';
import { useComponentLoading } from '../contexts/LoadingContext';
import {
  getPartyAdvance,
  allocateAdvanceToBill,
  markAdvanceUsed,
  applyAdvanceToBills as applyAdvanceToBillsUtil
} from '../utils/advanceUtils';
import { convertImageToDataUrl, preloadBillImages, inlineImagesForPrint, fetchImageAsDataUrlForPrint } from '../utils/imageUtils';
import { roundTo2Decimals, calculateGSTAmount, calculateLineTotal, roundToNearestInteger } from '../utils/numberFormat';
import {
  normalizeTagName,
  tagDocIdFromName,
  getServiceTagDocId,
  collectLegacyTagsFromRowsAndBills,
  buildServiceEntryForTag,
  getVendorsForServiceTag,
  findServiceTagMeta,
  calculateInternalCostFromTag,
  buildPreferredVendorPatch,
  promotePreferredVendorId,
  normalizePreferredVendorIds,
  isPreferredVendorForTag,
} from '../utils/serviceTagRegistry';
import { formatLineMeasurement } from '../utils/services';
import { 
  StandardModal, 
  StandardButton,
  PreviewModal,
  ConfirmationModal,
  FormModal,
  MODAL_SECTIONS,
} from './Modal';
import InfoHoverTip from './InfoHoverTip';
import {
  SUB_PARTY_MODAL_HELP,
  SUB_PARTY_NO_LINES_HELP,
  SUB_PARTY_AMOUNT_HELP,
  SUB_PARTY_AMOUNT_DETAIL_HELP,
  SUB_PARTY_SYNC_HELP,
  SUB_PARTY_ASSIGN_LINES_HELP,
  SUB_PARTY_NO_AVAILABLE_LINES_HELP,
  SUB_PARTY_ROUND_OFF_HELP,
  SUB_PARTY_TOTAL_MISMATCH_HELP,
  subPartyHiddenLinesHelp,
} from '../constants/subPartyHelpText';
import {
  SUB_PARTY_MODAL_SHELL_CLASS,
  SUB_PARTY_SCROLL_LIST_CLASS,
  SUB_PARTY_CARD_CLASS,
  SUB_PARTY_ROW_LAYOUT_CLASS,
  SUB_PARTY_NAME_FIELD_CLASS,
  SUB_PARTY_AMOUNT_FIELD_CLASS,
  SUB_PARTY_ACTIONS_CLASS,
  SUB_PARTY_SYNC_ROW_CLASS,
  SUB_PARTY_LINE_LIST_CLASS,
  SUB_PARTY_TOTALS_ROW_CLASS,
} from '../constants/subPartyModalLayout';
import imageManager from '../utils/imageManager';
import ShareButton from './Reports/ShareButton';
import {
  BILL_PREVIEW_MODAL_MAX_WIDTH,
  BILL_PREVIEW_INNER_CLASS,
  BILL_PREVIEW_SCREEN_CSS,
  BILL_PREVIEW_SHELL_CLASS,
  BILL_PREVIEW_SHELL_STYLE,
  getBillPreviewInitialZoom,
} from './BillTemplates/billPreviewScreenCss';
// Digital Signature URL Handler
const getDigitalSignatureUrl = async (signUrl, companyId) => {
  if (!signUrl || !companyId) return signUrl;
  
  // If it's already a cloud storage URL, return as is
  if (signUrl.startsWith('https://firebasestorage.googleapis.com/') || signUrl.startsWith('http')) {
    return signUrl;
  }
  
  // If it's base64 data, upload to cloud storage
  if (signUrl.startsWith('data:image/')) {
    try {
      const storage = getStorage();
      const base64Data = signUrl.split(',')[1];
      const binaryData = atob(base64Data);
      const bytes = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        bytes[i] = binaryData.charCodeAt(i);
      }
      
      const fileName = `signatures/${companyId}/digital_signature_${Date.now()}.png`;
      const fileRef = storageRef(storage, fileName);
      await uploadBytes(fileRef, bytes);
      const downloadURL = await getDownloadURL(fileRef);
      
      // Update company data with cloud URL
      const companyRef = doc(db, 'companies', companyId);
      await updateDoc(companyRef, {
        'branding.signatureUrl': downloadURL,
        'details.signUrl': downloadURL,
        'signUrl': downloadURL
      });
      
      console.log('✅ Digital signature uploaded to cloud storage:', downloadURL);
      return downloadURL;
    } catch (error) {
      console.error('❌ Failed to upload digital signature:', error);
      return signUrl; // Return original if upload fails
    }
  }
  
  return signUrl;
};

/** Offscreen html2canvas: match modal readability without CSS transform (transform blurs + clips in raster). */
const ACCTOO_BILL_CAPTURE_SHARP_CSS = `
@media screen {
  [data-acctoo-bill-capture="true"] #invoice-template {
    font-size: 116% !important;
    min-height: auto !important;
    overflow-x: visible !important;
  }
  [data-acctoo-bill-capture="true"] #invoice-template .invoice-print-inner {
    transform: none !important;
    width: 100% !important;
    max-width: 100% !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  [data-acctoo-bill-capture="true"] #quotation-template,
  [data-acctoo-bill-capture="true"] #challan-template,
  [data-acctoo-bill-capture="true"] #modern-sales-template,
  [data-acctoo-bill-capture="true"] #simple-sales-template,
  [data-acctoo-bill-capture="true"] #invoice-a5-template {
    min-height: auto !important;
    overflow: visible !important;
  }
  [data-acctoo-bill-capture="true"] #quotation-template .quotation-table-wrap {
    overflow: visible !important;
  }
  [data-acctoo-bill-capture="true"] #quotation-template th,
  [data-acctoo-bill-capture="true"] #quotation-template td {
    padding-top: 6px !important;
    padding-bottom: 6px !important;
    vertical-align: middle !important;
    line-height: 1.45 !important;
    overflow: visible !important;
  }
}
`;

/** Quotation print/PDF: ~90% A4 min height, details directly after table. */
const QUOTATION_PDF_EXPORT_CSS = `
@media screen {
  #quotation-template.invoice-pdf-export {
    width: 210mm !important;
    min-width: 210mm !important;
    max-width: 210mm !important;
    min-height: 267mm !important;
    height: auto !important;
    padding: 5mm !important;
    box-sizing: border-box !important;
    overflow: visible !important;
    display: block !important;
  }
  #quotation-template.invoice-pdf-export .quotation-print-inner,
  #quotation-template.invoice-pdf-export .quotation-table-wrap {
    display: block !important;
    min-height: auto !important;
    flex: none !important;
  }
  #quotation-template.invoice-pdf-export .quotation-post-items {
    margin-top: 4mm !important;
  }
  #quotation-template.invoice-pdf-export .quotation-bank-box,
  #quotation-template.invoice-pdf-export .quotation-terms-box {
    padding: 12px 14px !important;
    font-size: 14px !important;
  }
  #quotation-template.invoice-pdf-export .quotation-section-title {
    font-size: 16px !important;
    font-weight: 700 !important;
  }
  #quotation-template.invoice-pdf-export .quotation-bank-qr {
    width: 22mm !important;
    height: 22mm !important;
  }
  #quotation-template.invoice-pdf-export.quotation-sparse-items .quotation-bank-box,
  #quotation-template.invoice-pdf-export.quotation-sparse-items .quotation-terms-box {
    min-height: 55mm !important;
    padding: 16px 18px !important;
  }
  #quotation-template.invoice-pdf-export.quotation-sparse-items .quotation-section-title {
    font-size: 18px !important;
  }
  #quotation-template.invoice-pdf-export.quotation-sparse-items .quotation-bank-line {
    font-size: 15px !important;
  }
  #quotation-template.invoice-pdf-export.quotation-sparse-items .quotation-bank-qr {
    width: 30mm !important;
    height: 30mm !important;
  }
}
@media print {
  #quotation-template {
    min-height: 267mm !important;
    display: block !important;
  }
  #quotation-template .quotation-post-items { margin-top: 4mm !important; }
}
`;
/** Quotation copy image: content height only. */
const QUOTATION_IMAGE_EXPORT_CSS = `
@media screen {
  #quotation-template.invoice-image-export {
    width: 210mm !important;
    max-width: 210mm !important;
    min-height: auto !important;
    height: auto !important;
    padding: 8px !important;
    display: block !important;
  }
  #quotation-template.invoice-image-export .quotation-print-inner,
  #quotation-template.invoice-image-export .quotation-table-wrap,
  #quotation-template.invoice-image-export .quotation-post-items {
    display: block !important;
    min-height: auto !important;
    margin-top: 0 !important;
    flex: none !important;
  }
  #quotation-template.invoice-image-export .quotation-post-items {
    margin-top: 8px !important;
  }
}
`;

const getInvoiceHtml2CanvasScale = (forRasterExport = false) => {
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
  if (!forRasterExport) return 2;
  return Math.min(4, Math.max(3, Math.round(2 * dpr)));
};

const INVOICE_TEMPLATE_SELECTOR =
  '#invoice-template, #quotation-template, #challan-template, #modern-sales-template, #simple-sales-template, #invoice-a5-template';

const INVOICE_PRINT_WINDOW_CHROME_CSS = `
html,body{margin:0;padding:0;background:#fff;}
@media print{html,body{width:210mm!important;max-width:210mm!important;box-sizing:border-box!important;}}
@page{size:A4;margin:8mm;}
.print\\:hidden{display:none!important;}
@media print{.print\\:hidden{display:none!important;}}
.print\\:grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))!important;}
.print\\:bg-white{background-color:white!important;}
.print\\:border{border-width:1px!important;}
.print\\:border-gray-300{border-color:#d1d5db!important;}
.print\\:flex-row{flex-direction:row!important;}
.print\\:bg-yellow-100{background-color:#fef3c7!important;}
.print\\:bg-green-100{background-color:#dcfce7!important;}
.print\\:transform-none{transform:none!important;}
@media print{.grid{display:grid!important;}}
@media print{.grid-cols-2{grid-template-columns:repeat(2,minmax(0,1fr))!important;}}
@media print{.flex{display:flex!important;}}
@media print{.flex-row{flex-direction:row!important;}}
@media print{[class*="print:hidden"]{display:none!important;}}
@media print{button{display:none!important;}}
@media print{.sticky{position:static!important;}}
@media print{img{-webkit-print-color-adjust:exact;print-color-adjust:exact;}}
${QUOTATION_PDF_EXPORT_CSS}
${QUOTATION_IMAGE_EXPORT_CSS}
`;

function applyBillExportCanvasFixes(clonedDoc, { contentHeightOnly = false } = {}) {
  if (contentHeightOnly) {
    clonedDoc.querySelectorAll('#quotation-template').forEach((el) => {
      el.style.minHeight = 'auto';
      el.style.height = 'auto';
      el.style.display = 'block';
      el.style.padding = '8px';
    });
    clonedDoc.querySelectorAll('#quotation-template .quotation-print-inner, #quotation-template .quotation-table-wrap, #quotation-template .quotation-post-items').forEach((el) => {
      el.style.minHeight = 'auto';
      el.style.display = 'block';
      el.style.flex = 'none';
      el.style.marginTop = el.classList.contains('quotation-post-items') ? '8px' : '0';
    });
  }

  // Prevent html2canvas from clipping: short iframes / overflow:hidden crop bottoms and sides.
  clonedDoc.querySelectorAll('html, body').forEach((el) => {
    el.style.overflow = 'visible';
    el.style.height = 'auto';
    el.style.maxHeight = 'none';
  });
  clonedDoc.querySelectorAll(INVOICE_TEMPLATE_SELECTOR).forEach((el) => {
    el.style.overflow = 'visible';
    el.style.overflowX = 'visible';
    el.style.overflowY = 'visible';
    el.style.height = 'auto';
    el.style.maxHeight = 'none';
    el.style.minHeight = 'auto';
    el.style.boxSizing = 'border-box';
  });
  clonedDoc.querySelectorAll('.invoice-table-wrap, .quotation-table-wrap, [class*="overflow-x-auto"]').forEach((el) => {
    if (el.querySelector('table') || el.closest('table')) {
      el.style.overflow = 'visible';
      el.style.maxHeight = 'none';
    }
  });
  clonedDoc.querySelectorAll('tr').forEach((tr) => {
    tr.style.breakInside = 'avoid';
    tr.style.pageBreakInside = 'avoid';
  });
  clonedDoc.querySelectorAll('td, th').forEach((cell) => {
    cell.style.overflow = 'visible';
    cell.style.textOverflow = 'clip';
    cell.style.wordWrap = 'break-word';
    cell.style.wordBreak = 'break-word';
    cell.style.whiteSpace = 'normal';
    cell.style.textRendering = 'auto';
    cell.style.webkitFontSmoothing = 'subpixel-antialiased';
    cell.style.verticalAlign = 'middle';
    cell.style.lineHeight = '1.45';
    cell.style.paddingTop = '6px';
    cell.style.paddingBottom = '6px';
    if (!cell.style.paddingLeft) cell.style.paddingLeft = '8px';
    if (!cell.style.paddingRight) cell.style.paddingRight = '8px';
    // Do not force minWidth on fixed invoice tables — it overflows 210mm and gets clipped.
    const inFixedInvoiceTable = !!cell.closest('.invoice-items-table, .invoice-pdf-export table');
    const cellIndex = Array.from(cell.parentElement?.children || []).indexOf(cell);
    if (!inFixedInvoiceTable && cellIndex === 1) {
      cell.style.width = 'auto';
      cell.style.minWidth = '120px';
      cell.style.maxWidth = 'none';
    }
    cell.querySelectorAll('div, span, p').forEach((div) => {
      div.style.overflow = 'visible';
      div.style.textOverflow = 'clip';
      div.style.wordWrap = 'break-word';
      div.style.wordBreak = 'break-word';
      div.style.whiteSpace = 'normal';
      div.style.textRendering = 'auto';
      div.style.webkitFontSmoothing = 'subpixel-antialiased';
      if (div.style.height && div.style.height !== 'auto') {
        div.style.height = 'auto';
      }
    });
  });
  clonedDoc.querySelectorAll('table').forEach((table) => {
    if (!table.closest('.invoice-pdf-export')) {
      table.style.tableLayout = 'auto';
    }
    table.style.width = '100%';
    table.style.borderCollapse = 'collapse';
  });
  clonedDoc.querySelectorAll('img').forEach((img) => {
    img.style.imageRendering = 'auto';
  });
}

/** Expand offscreen export iframe so html2canvas can see full bill height. */
function sizeInvoiceExportIframe(iframe, exportRoot) {
  if (!iframe || !exportRoot) return;
  const doc = iframe.contentDocument;
  const contentHeight = Math.max(
    exportRoot.scrollHeight || 0,
    exportRoot.offsetHeight || 0,
    doc?.body?.scrollHeight || 0,
    doc?.documentElement?.scrollHeight || 0,
    400
  );
  iframe.style.height = `${contentHeight + 48}px`;
  iframe.style.minHeight = `${contentHeight + 48}px`;
  iframe.style.width = '210mm';
  if (doc?.body) {
    doc.body.style.overflow = 'visible';
    doc.body.style.height = 'auto';
  }
  if (doc?.documentElement) {
    doc.documentElement.style.overflow = 'visible';
    doc.documentElement.style.height = 'auto';
  }
  exportRoot.style.overflow = 'visible';
  exportRoot.style.height = 'auto';
  exportRoot.style.maxHeight = 'none';
}

function getInvoiceTemplateRoot(container) {
  if (!container) return null;
  return (
    container.querySelector('#invoice-template') ||
    container.querySelector('#quotation-template') ||
    container.querySelector('#challan-template') ||
    container.querySelector('#modern-sales-template') ||
    container.querySelector('#simple-sales-template') ||
    container.querySelector('#invoice-a5-template') ||
    container.firstElementChild
  );
}

async function waitDocumentImages(doc) {
  const imgs = Array.from(doc?.images || []);
  if (!imgs.length) return;
  await Promise.all(
    imgs.map(
      (img) =>
        new Promise((resolve) => {
          if (img.complete && img.naturalWidth > 0) return resolve();
          img.addEventListener('load', resolve, { once: true });
          img.addEventListener('error', resolve, { once: true });
        })
    )
  );
}

async function prepareInvoicePrintBodyHtml(invoiceContainer, { forPdfExport = false, forImageExport = false } = {}) {
  const templateRoot = getInvoiceTemplateRoot(invoiceContainer);
  if (!templateRoot) {
    throw new Error('Invoice template not found');
  }
  const temp = document.createElement('div');
  temp.innerHTML = templateRoot.outerHTML;
  await inlineImagesForPrint(temp);
  temp.querySelectorAll(INVOICE_TEMPLATE_SELECTOR).forEach((el) => {
    el.classList.remove('invoice-screen-preview-boost');
    el.classList.remove('quotation-screen-preview-boost');
    el.classList.remove('invoice-pdf-export');
    el.classList.remove('invoice-image-export');
    if (forImageExport) {
      el.classList.add('invoice-image-export');
    } else if (forPdfExport) {
      el.classList.add('invoice-pdf-export');
    }
  });
  temp.querySelectorAll('.invoice-print-inner').forEach((el) => {
    el.style.transform = 'none';
    el.style.width = '100%';
    el.style.maxWidth = '100%';
    el.style.marginLeft = '0';
    el.style.marginRight = '0';
  });
  temp.querySelectorAll('tr.invoice-filler-row').forEach((tr) => {
    tr.style.display = 'none';
  });
  return temp.innerHTML;
}

async function mountInvoicePrintIframe(bodyHtml) {
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.cssText =
    'position:fixed;left:-10000px;top:0;width:210mm;height:1200px;min-height:1200px;border:0;visibility:hidden;';
  document.body.appendChild(iframe);
  const doc = iframe.contentDocument;
  doc.open();
  doc.write(
    '<!DOCTYPE html><html><head><meta charset="utf-8"><title>Invoice</title>' +
      '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">' +
      '<style>' +
      INVOICE_PRINT_WINDOW_CHROME_CSS +
      'html,body{overflow:visible!important;height:auto!important;}' +
      '</style></head><body style="margin:0;background:#fff;overflow:visible;">' +
      bodyHtml +
      '</body></html>'
  );
  doc.close();
  await new Promise((resolve) => {
    if (iframe.contentDocument?.readyState === 'complete') resolve();
    else iframe.addEventListener('load', resolve, { once: true });
  });
  await waitDocumentImages(doc);
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  const exportRoot = getInvoiceTemplateRoot(doc.body);
  sizeInvoiceExportIframe(iframe, exportRoot || doc.body);
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  return iframe;
}

// Custom ShareButton component for sales (only PDF and Image)
const SalesShareButton = ({ onExportPDF, onExportImage, disabled = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Close dropdown on ESC key
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showDropdown) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showDropdown]);

  const handleShareClick = () => {
    setShowDropdown(!showDropdown);
  };

  const handleOptionClick = async (action) => {
    if (!action || isProcessing) return;
    
    setShowDropdown(false);
    setIsProcessing(true);
    
    try {
      if (typeof action === 'function') {
        await action();
      }
    } catch (error) {
      // Silent error handling to prevent console spam
    } finally {
      setIsProcessing(false);
    }
  };

  const shareOptions = [
    { 
      label: '📄 Share as PDF', 
      action: onExportPDF,
      disabled: !onExportPDF
    },
    { 
      label: '🖼️ Share as Image', 
      action: onExportImage,
      disabled: !onExportImage
    }
  ].filter(option => !option.disabled);

  return (
    <div className="relative">
      <button
        ref={buttonRef}
        onClick={handleShareClick}
        disabled={disabled || isProcessing}
        className="bg-green-600 hover:bg-green-700 disabled:bg-gray-300 text-white px-4 py-2 rounded-md transition duration-300 text-sm relative"
      >
        {isProcessing ? '⏳' : '📱'} Share
      </button>
      
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 sm:w-48 w-40"
          style={{ zIndex: 9999 }}
        >
          {shareOptions.length === 0 ? (
            <div className="p-2 text-gray-500 text-xs">No sharing options available</div>
          ) : (
            shareOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => handleOptionClick(option.action)}
                disabled={option.disabled || isProcessing}
                className="w-full text-left px-3 py-2 text-xs sm:text-sm text-gray-700 hover:bg-gray-100 first:rounded-t-md last:rounded-b-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {option.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// Make imageManager globally available for PDF generation
if (typeof window !== 'undefined') {
  window.imageManager = imageManager;
}

const initialItemRow = {
  item: '',
  nos: 1,
  length: 0,
  height: 0,
  qty: 0,
  qtyExpr: '',
  qtyDisplay: '',
  unit: '',
  quantityMeasurement: '',
  lineDiscountType: 'amount',
  lineDiscountValue: 0,
  rate: 0,
  rateIncludingGST: false, // New field for GST calculation option
  amount: 0,
  sgst: 0,
  cgst: 0,
  igst: 0,
  total: 0,
  tags: [],
  services: [],
};

const SERVICE_STATUS_OPTIONS = ['pending', 'done', 'paid'];

const createDefaultServiceEntry = (serviceName = '') => ({
  id: `svc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  serviceName: String(serviceName || '').trim().toLowerCase(),
  vendorId: '',
  vendorName: '',
  internalCost: '',
  customerCharge: '',
  showOnInvoice: false,
  status: 'pending',
  createdAt: new Date().toISOString(),
});

const normalizeServiceEntry = (entry = {}) => {
  const status = SERVICE_STATUS_OPTIONS.includes(entry.status) ? entry.status : 'pending';
  return {
    ...createDefaultServiceEntry(entry.serviceName || ''),
    ...entry,
    serviceName: String(entry.serviceName || '').trim().toLowerCase(),
    status,
    internalCost: entry.internalCost === '' || entry.internalCost === null || entry.internalCost === undefined
      ? ''
      : parseFloat(entry.internalCost) || 0,
    customerCharge: entry.customerCharge === '' || entry.customerCharge === null || entry.customerCharge === undefined
      ? ''
      : parseFloat(entry.customerCharge) || 0,
    showOnInvoice: Boolean(entry.showOnInvoice),
  };
};

function isMeaningfulInvoiceRow(r = {}) {
  const hasItem =
    !!(r.item && String(r.item).trim()) || !!(r.itemId && String(r.itemId).trim());
  const hasNumbers =
    (parseFloat(r.qty) || 0) > 0 ||
    (parseFloat(r.rate) || 0) > 0 ||
    (parseFloat(r.amount) || 0) > 0 ||
    (parseFloat(r.total) || 0) > 0 ||
    (parseFloat(r.length) || 0) > 0 ||
    (parseFloat(r.height) || 0) > 0 ||
    ((parseFloat(r.nos) || 0) > 1);
  return hasItem || hasNumbers;
}

/** Meaningful lines in order; cleanedIdx 0..n-1 matches index in saved bill.rows */
function getSubPartyLineRefsForRows(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .map((row, rowIndex) => ({ row, rowIndex }))
    .filter(({ row }) => isMeaningfulInvoiceRow(row))
    .map((o, cleanedIdx) => ({ ...o, cleanedIdx }));
}

function sanitizeSubPartyLineIndices(raw, maxExclusive) {
  const cap = Math.max(0, parseInt(maxExclusive, 10) || 0);
  if (!Array.isArray(raw) || cap === 0) return [];
  const seen = new Set();
  const out = [];
  for (const v of raw) {
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n < 0 || n >= cap) continue;
    if (seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out.sort((a, b) => a - b);
}

/** Normalize modal draft rows for Firestore: name, amount, lineIndices (into saved rows order) */
function normalizeSubPartyBreakdownForSave(draftRows, meaningfulLineCount) {
  if (!Array.isArray(draftRows)) return [];
  const cap = Math.max(0, parseInt(meaningfulLineCount, 10) || 0);
  return draftRows
    .map((r) => ({
      name: String(r.name || '').trim(),
      amount: roundTo2Decimals(parseFloat(String(r.amount ?? '').replace(/,/g, '')) || 0),
      lineIndices: sanitizeSubPartyLineIndices(r.lineIndices, cap),
    }))
    .filter((r) => r.name.length > 0 && r.amount > 0);
}

function sumSubPartyBreakdownAmounts(rows) {
  if (!Array.isArray(rows)) return 0;
  return roundTo2Decimals(rows.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0));
}

function getLineTotalForSubParty(row, gstinType) {
  if (gstinType === 'Regular' && row && row.total !== undefined && row.total !== '') {
    return parseFloat(row.total) || 0;
  }
  return parseFloat(row?.amount) || 0;
}

/** Sum line totals for cleaned line indices (used as default sub party amount) */
function sumLineTotalsForSubPartyIndices(lineIndices, lineRefs, gstinType) {
  if (!Array.isArray(lineIndices) || !Array.isArray(lineRefs)) return 0;
  let sum = 0;
  lineIndices.forEach((ci) => {
    const ref = lineRefs.find((l) => l.cleanedIdx === ci);
    if (ref) sum += getLineTotalForSubParty(ref.row, gstinType);
  });
  return roundTo2Decimals(sum);
}

function collectAssignedSubPartyLineIndices(draftRows) {
  const assigned = new Set();
  if (!Array.isArray(draftRows)) return assigned;
  draftRows.forEach((r) => {
    (r.lineIndices || []).forEach((ci) => {
      const n = parseInt(ci, 10);
      if (Number.isFinite(n)) assigned.add(n);
    });
  });
  return assigned;
}

function areAllInvoiceLinesAssignedInSubPartyDraft(draftRows, lineRefCount) {
  if (!lineRefCount) return false;
  return collectAssignedSubPartyLineIndices(draftRows).size === lineRefCount;
}

/**
 * When every meaningful line is assigned across sub-parties, line sums match pre–round-off grand total.
 * Apply invoice integer round-off to one sub-party row so draft totals match the displayed Grand Total.
 * `_invoiceRoundOffAdj` is UI-only (stripped by normalizeSubPartyBreakdownForSave).
 */
function applySubPartyDraftInvoiceRoundOff(nextDraft, lineRefs, gstinType, grandTotalRounded, grandTotalBeforeRounded) {
  if (!Array.isArray(nextDraft) || !Array.isArray(lineRefs) || lineRefs.length === 0) return nextDraft;
  const copy = nextDraft.map((r) => ({
    ...r,
    lineIndices: [...(r.lineIndices || [])],
    _invoiceRoundOffAdj: undefined,
  }));
  copy.forEach((r) => {
    r.amount = String(sumLineTotalsForSubPartyIndices(r.lineIndices, lineRefs, gstinType));
  });
  if (!areAllInvoiceLinesAssignedInSubPartyDraft(copy, lineRefs.length)) return copy;
  const totalsFromLines = roundTo2Decimals(
    copy.reduce((s, r) => s + sumLineTotalsForSubPartyIndices(r.lineIndices, lineRefs, gstinType), 0)
  );
  const expected = roundTo2Decimals(grandTotalBeforeRounded);
  if (Math.abs(totalsFromLines - expected) > 0.05) return copy;
  const delta = roundTo2Decimals(grandTotalRounded - totalsFromLines);
  if (Math.abs(delta) < 0.001) return copy;
  let absorbIdx = -1;
  for (let i = copy.length - 1; i >= 0; i--) {
    if ((copy[i].lineIndices || []).length > 0) {
      absorbIdx = i;
      break;
    }
  }
  if (absorbIdx < 0) return copy;
  const baseAmt = roundTo2Decimals(parseFloat(String(copy[absorbIdx].amount ?? '').replace(/,/g, '')) || 0);
  copy[absorbIdx].amount = String(roundTo2Decimals(baseAmt + delta));
  copy[absorbIdx]._invoiceRoundOffAdj = delta;
  return copy;
}

// Predefined units for new items
const predefinedUnits = [
  'Pieces', 'Piece', 'Kg', 'Gram', 'Ton', 'Quintal', 'Meter', 'Centimeter', 'Millimeter', 'Yard', 'Foot', 'Inch',
  'Litre', 'ML', 'Gallon', 'Drum', 'Cylinder',
  'Box', 'Packet', 'Carton', 'Bag', 'Bottle', 'Can', 'Jar', 'Tube',
  'Sheet', 'Strip', 'Roll', 'Ream', 'Bundle', 'Panel', 'Board', 'Plate', 'Slab', 'Block',
  'Sq. Ft.', 'Sq. Inch', 'Sq. Yard', 'Square Meter', 'Cubic Feet', 'Cubic Meter', 'Cu. Inch', 'Cu. Yard',
  'Hour', 'Day', 'Month', 'Year',
  'Lot', 'Job', 'Impression', 'Run',
  'Set', 'Pair',
  'Other'
];

function getFinancialYear(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = d.getMonth() + 1;
  if (month >= 4) {
    return `${year}-${(year + 1).toString().slice(-2)}`;
  } else {
    return `${year - 1}-${year.toString().slice(-2)}`;
  }
}

// Utility function to determine GST split type
function getGstTypeAndSplit(sellerGstin, buyerGstin, gstPercent) {
  if (!sellerGstin || !buyerGstin || sellerGstin.length < 2 || buyerGstin.length < 2) {
    return { type: 'CGST_SGST', cgst: gstPercent / 2, sgst: gstPercent / 2, igst: 0 };
  }
  const sellerState = sellerGstin.substring(0, 2);
  const buyerState = buyerGstin.substring(0, 2);
  if (sellerState === buyerState) {
    return { type: 'CGST_SGST', cgst: gstPercent / 2, sgst: gstPercent / 2, igst: 0 };
  } else {
    return { type: 'IGST', cgst: 0, sgst: 0, igst: gstPercent };
  }
}


// Parse a quantity expression like "5x3x2" or "10*2" into a numeric product and a normalized display string
function parseQtyExpression(input) {
  const s = String(input || '').trim().replace(/\s+/g, '');
  if (!s) return { ok: false, value: 0, display: '' };
  const parts = s.split(/[xX\*]/).filter(Boolean);
  if (parts.length === 0) return { ok: false, value: 0, display: '' };
  let product = 1;
  for (const p of parts) {
    const n = Number(p);
    if (!isFinite(n) || n <= 0) return { ok: false, value: 0, display: '' };
    product *= n;
  }
  return { ok: true, value: product, display: parts.join('×') };
}




// Dropdown Form Component for Desktop Table Rows
const DropdownFormRow = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle, currentCompany, onSelectForDeletion, isSelectedForDeletion, employees = [], onQuickEmployeeCashFlow, onConvertToInvoice }) => {
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState('cash'); // 'cash' or 'upi'
    const [customPaymentAmount, setCustomPaymentAmount] = useState('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const employeeDropdownRef = useRef(null);
    const walletButtonRef = useRef(null);
    
    // Close dropdown when clicking outside (excluding wallet button)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showEmployeeDropdown &&
                employeeDropdownRef.current &&
                !employeeDropdownRef.current.contains(event.target) &&
                walletButtonRef.current &&
                !walletButtonRef.current.contains(event.target)
            ) {
                setShowEmployeeDropdown(false);
                setSelectedPaymentMode('cash'); // Reset on close
                setCustomPaymentAmount(''); // Reset custom amount
                setSelectedEmployeeId(null); // Reset selected employee
            }
        };

        if (showEmployeeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showEmployeeDropdown]);
    
    // Format date according to company settings
    const date = formatDateToCompanyFormat(bill.invoiceDate || bill.date, currentCompany);
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    const paidAmount = formatCurrency(bill.paid || 0, companyCurrency);
    const outstandingAmount = formatCurrency(bill.outstanding || 0, companyCurrency);

    // Get bill items for this invoice — rows are canonical (saved source of truth)
    const billItems = getBillLineItems(bill);

    // Helper function to get item name from line + master list
    const getLineItemDisplayName = (line) => resolveBillLineItemName(line, items);

    return (
        <div className="bg-white border-b border-gray-200 rounded-none shadow-none mb-0 transform transition-all duration-150 ease-out overflow-visible">
            {/* Main Row - Always Visible */}
            <div 
                className="py-2.5 px-3 lg:py-3 lg:px-4 cursor-pointer hover:bg-slate-50 transition-colors overflow-visible"
                onClick={() => onToggle(index)}
            >
                <div className="flex items-center justify-between overflow-visible">
                    <div className="flex-1 grid grid-cols-[32px_minmax(120px,1.2fr)_minmax(92px,0.9fr)_minmax(140px,1.5fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(110px,1fr)] gap-2 lg:gap-4 items-center justify-items-center text-center overflow-visible leading-relaxed min-w-0">
                        <div className="flex justify-center w-8">
                            <input
                                type="checkbox"
                                checked={isSelectedForDeletion}
                                onChange={(e) => {
                                    e.stopPropagation();
                                    onSelectForDeletion(bill.id);
                                }}
                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                        </div>
                        <div className="text-sm font-semibold text-slate-800 truncate min-w-0" title={bill.number || bill.invoiceNumber}>
                            {bill.number || bill.invoiceNumber}
                        </div>
                        <div className="text-sm text-slate-600 truncate min-w-0" title={date}>{date}</div>
                        <div className="text-sm text-slate-700 truncate font-medium min-w-0" title={bill.partyName}>
                            {bill.partyName}
                        </div>
                        <div className="text-sm font-semibold text-blue-700 bg-blue-50 rounded-md px-2 py-1 w-fit max-w-full truncate justify-self-center min-w-0" title={amount}>{amount}</div>
                        <div 
                            className="text-sm font-semibold text-green-700 cursor-pointer hover:text-green-800 transition-colors hover:bg-green-50 px-2 py-1 rounded-md truncate min-w-0"
                            onClick={(e) => {
                                e.stopPropagation();
                                onReceipts(bill);
                            }}
                            title="Click to view receipt details"
                        >
                            {paidAmount}
                        </div>
                        <div className="flex items-center gap-1 overflow-visible min-w-0">
                            <div 
                                className="text-sm font-semibold text-red-700 cursor-pointer hover:text-red-800 transition-colors hover:bg-red-50 px-2 py-1 rounded-md truncate min-w-0"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onPayment(bill);
                                }}
                                title="Click to add payment"
                            >
                                {outstandingAmount}
                            </div>
                            {bill.outstanding > 0 && employees && employees.length > 0 && (
                                <div className="relative employee-wallet-dropdown-container">
                                    <button
                                        ref={walletButtonRef}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (walletButtonRef.current) {
                                                const rect = walletButtonRef.current.getBoundingClientRect();
                                                setDropdownPosition({
                                                    top: rect.bottom + window.scrollY + 4,
                                                    left: rect.right + window.scrollX - 256 // 256 = w-64 (256px)
                                                });
                                            }
                                            setShowEmployeeDropdown(!showEmployeeDropdown);
                                        }}
                                        className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                                        title="Quick record in employee cash flow"
                                    >
                                        <Wallet size={14} />
                                    </button>
                                    {showEmployeeDropdown && createPortal(
                                        <div 
                                            ref={employeeDropdownRef}
                                            className="employee-wallet-dropdown fixed w-72 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-96 overflow-y-auto z-[99999]"
                                            style={{
                                                top: `${dropdownPosition.top}px`,
                                                left: `${dropdownPosition.left}px`
                                            }}
                                        >
                                            <div className="p-3 border-b border-gray-200 bg-blue-50">
                                                <div className="text-xs font-semibold text-gray-700 mb-2">Quick Record Cash Flow</div>
                                                <div className="text-xs text-gray-500 mb-2">Outstanding: {outstandingAmount}</div>
                                                
                                                {/* Custom Amount Input */}
                                                <div className="mb-2">
                                                    <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount</label>
                                                    <input
                                                        type="number"
                                                        value={customPaymentAmount}
                                                        onChange={(e) => {
                                                            e.stopPropagation();
                                                            setCustomPaymentAmount(e.target.value);
                                                        }}
                                                        placeholder={outstandingAmount}
                                                        min="0"
                                                        step="0.01"
                                                        className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <div className="text-xs text-gray-500 mt-0.5">
                                                        Leave empty to use full outstanding amount
                                                    </div>
                                                </div>
                                                
                                                <div className="flex gap-2 mt-2">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedPaymentMode('cash');
                                                        }}
                                                        className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                            selectedPaymentMode === 'cash'
                                                                ? 'bg-blue-600 text-white font-medium'
                                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        Cash
                                                    </button>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedPaymentMode('upi');
                                                        }}
                                                        className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                            selectedPaymentMode === 'upi'
                                                                ? 'bg-blue-600 text-white font-medium'
                                                                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        UPI
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="py-1 max-h-48 overflow-y-auto">
                                                {employees.filter(emp => emp.status === 'active').map((employee) => {
                                                    const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                                                    // For Sales, this is an inflow (employee receives money) - no balance check needed
                                                    const isSelected = selectedEmployeeId === employee.id;
                                                    return (
                                                        <div
                                                            key={employee.id}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedEmployeeId(isSelected ? null : employee.id);
                                                            }}
                                                            className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-sm border-b border-gray-100 last:border-b-0 cursor-pointer flex items-center gap-2"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedEmployeeId(e.target.checked ? employee.id : null);
                                                                }}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                            />
                                                            <div className="flex-1">
                                                                <div className="font-medium text-gray-900">{employee.name}</div>
                                                                <div className="text-xs text-gray-600">
                                                                    {employee.employeeId || 'N/A'} • Cash: ₹{balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹{balance.upi?.toLocaleString('en-IN') || 0}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                            <div className="p-3 border-t border-gray-200 bg-gray-50">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (selectedEmployeeId && onQuickEmployeeCashFlow) {
                                                            const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
                                                            if (selectedEmployee) {
                                                                const amountToUse = customPaymentAmount.trim() !== '' ? customPaymentAmount : null;
                                                                onQuickEmployeeCashFlow(bill, selectedEmployee, selectedPaymentMode, amountToUse);
                                                                setShowEmployeeDropdown(false);
                                                                setSelectedPaymentMode('cash'); // Reset
                                                                setCustomPaymentAmount(''); // Reset custom amount
                                                                setSelectedEmployeeId(null); // Reset selected employee
                                                            }
                                                        }
                                                    }}
                                                    disabled={!selectedEmployeeId}
                                                    className={`w-full px-4 py-2 text-sm font-medium rounded transition-colors ${
                                                        selectedEmployeeId
                                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    }`}
                                                >
                                                    Record Transaction
                                                </button>
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-center space-x-2 ml-2 lg:ml-4 w-24 lg:w-28 flex-shrink-0">
                        <ActionButtons
                            enableDropdown={true}
                            billData={{
                                ...bill,
                                partyName: bill.partyName
                            }}
                            items={items}
                            actions={[
                                { type: 'edit', onClick: () => onEdit(bill) },
                                { type: 'delete', onClick: () => onDelete(bill.id) },
                                ...(docType === 'quotation' && onConvertToInvoice ? [{ 
                                    type: 'convert_to_invoice', 
                                    onClick: () => onConvertToInvoice(bill) 
                                }] : []),
                                {
                                    type: docType === 'invoice' ? 'invoice' : docType === 'challan' ? 'challan' : 'quotation',
                                    onClick: () => onPrint(bill)
                                }
                            ]}
                            size="sm"
                        />
                        <button
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 px-3 py-2 sm:p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-80 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        {/* Single Row Layout - Item Name on Left, Details on Right */}
                                        <div className="flex items-center justify-between">
                                            {/* Item Name - Left Side */}
                                            <div className="flex-1 min-w-0">
                                                <span className="text-sm font-medium text-gray-900 block truncate" title={getLineItemDisplayName(item)}>
                                                    {getLineItemDisplayName(item)}
                                                </span>
                                                {(item.description || item.desc) && (
                                                    <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                        {item.description || item.desc}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Item Details - Right Side */}
                                            <div className="flex items-center gap-x-4 text-sm text-gray-600 ml-4">
                                                <span className="whitespace-nowrap">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                                <span className="whitespace-nowrap">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                                <span className="whitespace-nowrap font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// Collapsible Sales Card Component for Mobile View
const SalesCard = ({ bill, docType, docTypeOptions, companyCurrency, onView, onEdit, onDelete, onPrint, onPayment, onReceipts, receiptsCount, receiptNumbers, items = [], index = 0, isExpanded, onToggle, currentCompany, employees = [], onQuickEmployeeCashFlow, onConvertToInvoice }) => {
    const [showEmployeeDropdown, setShowEmployeeDropdown] = useState(false);
    const [selectedPaymentMode, setSelectedPaymentMode] = useState('cash');
    const [customPaymentAmount, setCustomPaymentAmount] = useState('');
    const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const employeeDropdownRef = useRef(null);
    const walletButtonRef = useRef(null);
    
    // Format date according to company settings
    const date = formatDateToCompanyFormat(bill.invoiceDate || bill.date, currentCompany);
    
    // Format amount with currency
    const amount = typeof bill.amount === "number"
        ? formatCurrency(bill.amount, companyCurrency)
        : bill.amount;

    // Get bill items for this invoice — rows are canonical (saved source of truth)
    const billItems = getBillLineItems(bill);
    
    // Close dropdown when clicking outside (excluding wallet button)
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                showEmployeeDropdown &&
                employeeDropdownRef.current &&
                !employeeDropdownRef.current.contains(event.target) &&
                walletButtonRef.current &&
                !walletButtonRef.current.contains(event.target)
            ) {
                setShowEmployeeDropdown(false);
                setSelectedPaymentMode('cash'); // Reset on close
                setCustomPaymentAmount(''); // Reset custom amount
                setSelectedEmployeeId(null); // Reset selected employee
            }
        };

        if (showEmployeeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('touchstart', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showEmployeeDropdown]);

    // Helper function to get item name from line + master list
    const getLineItemDisplayName = (line) => resolveBillLineItemName(line, items);

    return (
        <div 
            className="bg-white border border-gray-200 rounded-lg shadow-sm mb-0 transform transition-all duration-300 ease-in-out hover:shadow-md hover:scale-[1.02]"
            style={{
                animationDelay: `${index * 50}ms`,
                animation: 'slideInUp 0.4s ease-out forwards'
            }}
        >
            {/* Header Section */}
            <div 
                className="px-3 py-2 sm:p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => onToggle(index)}
            >
                <div className="flex justify-between items-center">
                    <div className="flex-1 min-w-0">
                        <h4 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{bill.number || bill.invoiceNumber}</h4>
                        <p className="text-xs sm:text-sm text-gray-600 truncate">{bill.partyName || 'No Party'}</p>
                    </div>
                    <div className="flex items-center space-x-2 sm:space-x-3 ml-2">
                        <div className="text-right">
                            <div className="text-sm sm:text-lg font-semibold text-blue-600">{amount}</div>
                            <div className="text-xs text-gray-500">{date}</div>
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="px-1 sm:px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800 mb-2">
                                {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Payment Status Row - Single Row Layout */}
                <div className="mt-2 sm:mt-3 flex justify-between items-center">
                    <div 
                        className="text-xs sm:text-sm font-medium text-green-600 cursor-pointer hover:text-green-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-green-50"
                        onClick={(e) => {
                            e.stopPropagation();
                            onReceipts(bill);
                        }}
                        title="Click to view receipt details"
                    >
                        <span className="hidden sm:inline">Paid: </span>
                        <span className="sm:hidden">💰 </span>
                        {formatCurrency(bill.paid || 0, companyCurrency)}
                    </div>
                    <div className="flex items-center gap-1">
                        <div 
                            className="text-xs sm:text-sm font-medium text-red-600 cursor-pointer hover:text-red-800 transition-colors px-1 sm:px-2 py-1 rounded hover:bg-red-50"
                            onClick={(e) => {
                                e.stopPropagation();
                                onPayment(bill);
                            }}
                            title="Click to add payment"
                        >
                            <span className="hidden sm:inline">Outstanding: </span>
                            <span className="sm:hidden">💳 </span>
                            {formatCurrency(bill.outstanding || 0, companyCurrency)}
                        </div>
                        {bill.outstanding > 0 && employees && employees.length > 0 && (
                            <div className="relative employee-wallet-dropdown-container">
                                <button
                                    ref={walletButtonRef}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (walletButtonRef.current) {
                                            const rect = walletButtonRef.current.getBoundingClientRect();
                                            setDropdownPosition({
                                                top: rect.bottom + window.scrollY + 4,
                                                left: rect.right + window.scrollX - 256 // 256 = w-64 (256px)
                                            });
                                        }
                                        setShowEmployeeDropdown(!showEmployeeDropdown);
                                    }}
                                    className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                                    title="Quick record in employee cash flow"
                                >
                                    <Wallet size={14} />
                                </button>
                                {showEmployeeDropdown && createPortal(
                                    <div 
                                        ref={employeeDropdownRef}
                                        className="employee-wallet-dropdown fixed w-72 bg-white border border-gray-200 rounded-lg shadow-2xl max-h-96 overflow-y-auto z-[99999]"
                                        style={{
                                            top: `${dropdownPosition.top}px`,
                                            left: `${dropdownPosition.left}px`
                                        }}
                                    >
                                        <div className="p-3 border-b border-gray-200 bg-blue-50">
                                            <div className="text-xs font-semibold text-gray-700 mb-2">Quick Record Cash Flow</div>
                                            <div className="text-xs text-gray-500 mb-2">Outstanding: {formatCurrency(bill.outstanding || 0, companyCurrency)}</div>
                                            
                                            {/* Custom Amount Input */}
                                            <div className="mb-2">
                                                <label className="block text-xs font-medium text-gray-700 mb-1">Payment Amount</label>
                                                <input
                                                    type="number"
                                                    value={customPaymentAmount}
                                                    onChange={(e) => {
                                                        e.stopPropagation();
                                                        setCustomPaymentAmount(e.target.value);
                                                    }}
                                                    placeholder={formatCurrency(bill.outstanding || 0, companyCurrency)}
                                                    min="0"
                                                    step="0.01"
                                                    className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    onClick={(e) => e.stopPropagation()}
                                                />
                                                <div className="text-xs text-gray-500 mt-0.5">
                                                    Leave empty to use full outstanding amount
                                                </div>
                                            </div>
                                            
                                            <div className="flex gap-2 mt-2">
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPaymentMode('cash');
                                                    }}
                                                    className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                        selectedPaymentMode === 'cash'
                                                            ? 'bg-blue-600 text-white font-medium'
                                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    Cash
                                                </button>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedPaymentMode('upi');
                                                    }}
                                                    className={`flex-1 px-2 py-1 text-xs rounded transition-colors ${
                                                        selectedPaymentMode === 'upi'
                                                            ? 'bg-blue-600 text-white font-medium'
                                                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    UPI
                                                </button>
                                            </div>
                                        </div>
                                        <div className="py-1 max-h-48 overflow-y-auto">
                                            {employees.filter(emp => emp.status === 'active').map((employee) => {
                                                const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                                                // For Sales, this is an inflow (employee receives money) - no balance check needed
                                                const isSelected = selectedEmployeeId === employee.id;
                                                return (
                                                    <div
                                                        key={employee.id}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedEmployeeId(isSelected ? null : employee.id);
                                                        }}
                                                        className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors text-sm border-b border-gray-100 last:border-b-0 cursor-pointer flex items-center gap-2"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedEmployeeId(e.target.checked ? employee.id : null);
                                                            }}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <div className="flex-1">
                                                            <div className="font-medium text-gray-900">{employee.name}</div>
                                                                <div className="text-xs text-gray-600">
                                                                    {employee.employeeId || 'N/A'} • Cash: ₹{balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹{balance.upi?.toLocaleString('en-IN') || 0}
                                                                </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <div className="p-3 border-t border-gray-200 bg-gray-50">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (selectedEmployeeId && onQuickEmployeeCashFlow) {
                                                        const selectedEmployee = employees.find(emp => emp.id === selectedEmployeeId);
                                                        if (selectedEmployee) {
                                                            const amountToUse = customPaymentAmount.trim() !== '' ? customPaymentAmount : null;
                                                            onQuickEmployeeCashFlow(bill, selectedEmployee, selectedPaymentMode, amountToUse);
                                                            setShowEmployeeDropdown(false);
                                                            setSelectedPaymentMode('cash'); // Reset
                                                            setCustomPaymentAmount(''); // Reset custom amount
                                                            setSelectedEmployeeId(null); // Reset selected employee
                                                        }
                                                    }
                                                }}
                                                disabled={!selectedEmployeeId}
                                                className={`w-full px-4 py-2 text-sm font-medium rounded transition-colors ${
                                                    selectedEmployeeId
                                                        ? 'bg-blue-600 text-white hover:bg-blue-700'
                                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                }`}
                                            >
                                                Record Transaction
                                            </button>
                                        </div>
                                    </div>,
                                    document.body
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons with Dropdown */}
                <div className="mt-2 sm:mt-4 pt-2 sm:pt-3 border-t border-gray-100 relative">
                    <div className="flex justify-between items-center">
                    <ActionButtons
                        enableDropdown={true}
                        billData={{
                            ...bill,
                            partyName: bill.partyName
                        }}
                        items={items}
                        actions={[
                            { type: 'edit', onClick: () => onEdit(bill) },
                            { type: 'delete', onClick: () => onDelete(bill.id) },
                            ...(docType === 'quotation' && onConvertToInvoice ? [{ 
                                type: 'convert_to_invoice', 
                                onClick: () => onConvertToInvoice(bill) 
                            }] : []),
                            {
                                type: docType === 'invoice' ? 'invoice' : docType === 'challan' ? 'challan' : 'quotation',
                                onClick: () => onPrint(bill)
                            }
                        ]}
                            className="justify-start"
                        size="md"
                    />
                        <button
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            onClick={(e) => {
                                e.stopPropagation();
                                onToggle(index);
                            }}
                        >
                            <svg 
                                className={`w-5 h-5 transform transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                        </button>
                </div>
            </div>
            </div>

            {/* Dropdown Content - Items List */}
            {isExpanded && (
                <div className="border-t border-gray-200 bg-blue-50 px-3 py-2 sm:p-4">
                    <div className="mb-3">
                        <h4 className="text-sm font-semibold text-gray-700 mb-3">Items in this {docType}:</h4>
                        {billItems.length > 0 ? (
                            <div className="space-y-1 max-h-80 overflow-y-auto custom-scrollbar">
                                {billItems.map((item, itemIdx) => (
                                    <div key={itemIdx} className="bg-white p-2 rounded border border-gray-200 hover:bg-gray-50 transition-colors">
                                        {/* Item Name - Prominent on Mobile */}
                                        <div className="mb-1">
                                            <span className="text-sm sm:text-base font-medium text-gray-900 block" title={getLineItemDisplayName(item)}>
                                                {getLineItemDisplayName(item)}
                                            </span>
                                        </div>
                                        
                                        {/* Item Details - Responsive Layout */}
                                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-gray-600">
                                            <span className="whitespace-nowrap">Qty: {item.qty || item.quantity || item.nos || 0}</span>
                                            <span className="whitespace-nowrap">Rate: {formatCurrency(item.rate || 0, companyCurrency)}</span>
                                            <span className="whitespace-nowrap font-medium text-gray-900">Total: {formatCurrency(item.amount || item.total || 0, companyCurrency)}</span>
                                        </div>
                                        {(item.description || item.desc) && (
                                            <div className="text-xs text-gray-500 mt-1 truncate" title={item.description || item.desc}>
                                                {item.description || item.desc}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500 italic text-center py-4">No items found for this {docType}</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
// Order: Receipt Modal → Payment Details Modal → Payment Modal → Invoice Modal → View Modal → Success Modal → Add Item Modal
function Sales({ db, userId, isAuthReady, appId }) {
  const { currentCompany, employees, loadCompanyInfo } = useEnhancedCompany();
  const lastSyncedSignUrlRef = useRef(null);
  const companyId = currentCompany?.companyId || currentCompany?.id || null;
  const companyCtx = useMemo(() => ({ companyId, appId, userId }), [companyId, appId, userId]);
  const { currency: companyCurrency } = useCompanyCurrency();
  const [workOrderLink, setWorkOrderLink] = useState({ workOrderId: '', phaseId: '', costCategory: '' });
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { success, error, warning, info, paymentSuccess } = useNotification();
  const { handleError, handleValidationError, showWarning } = useErrorHandler();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [party, setParty] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('Pending');
  const [notes, setNotes] = useState('');
  // Fields for returns and notes
  const [originalBillId, setOriginalBillId] = useState('');
  const [originalBillNumber, setOriginalBillNumber] = useState('');
  const [returnReason, setReturnReason] = useState('');
  const [rows, setRows] = useState(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
  // Local text for item combobox per row (do NOT persist)
  const [itemSearchTextByRow, setItemSearchTextByRow] = useState({});
  
  // Bill-wise GST calculation type
  const [gstCalculationType, setGstCalculationType] = useState('excluding'); // 'including' or 'excluding'
  
  // Dropdown form state for expanded rows
  const [expandedRows, setExpandedRows] = useState({});
 
  // Modal management


  // Live data states
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [itemsFetchError, setItemsFetchError] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [company, setCompany] = useState({});
  const [bills, setBills] = useState([]);
  
  // Loading state - set to TRUE immediately on mount, before any data operations
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isSavingInvoice, setIsSavingInvoice] = useState(false);
  const isSavingInvoiceRef = useRef(false);
  
  // Track if we've shown loading screen at least once (minimum display time)
  const [loadingStartTime, setLoadingStartTime] = useState(null);
  const MIN_LOADING_TIME = 500; // Minimum 500ms to show loading screen
  
  // Use loading context for better UX - MUST be defined before checkAndStopInitialLoading
  const { startLoading, stopLoading } = useComponentLoading('sales', 'Loading sales data...');
  
  // Track which data sources have loaded
  const dataLoadedRef = useRef({
    parties: false,
    items: false,
    purchases: false,
    company: false
  });
  
  // Function to check if all required data is loaded and stop initial loading
  const checkAndStopInitialLoading = useCallback(() => {
    const { parties, items, purchases, company } = dataLoadedRef.current;
    if (parties && items && purchases && company) {
      const elapsed = loadingStartTime ? Date.now() - loadingStartTime : 0;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);
      
      if (remainingTime > 0) {
        console.log(`🔄 [LOADING SCREEN] Sales: All data loaded, waiting ${remainingTime}ms more for minimum display time`);
        setTimeout(() => {
          console.log('🔄 [LOADING SCREEN] Sales: Stopping initial loading - all data loaded and minimum time elapsed');
          setIsInitialLoading(false);
          stopLoading();
        }, remainingTime);
      } else {
        console.log('🔄 [LOADING SCREEN] Sales: Stopping initial loading - all data loaded');
        setIsInitialLoading(false);
        stopLoading();
      }
    }
  }, [loadingStartTime, stopLoading]);
  
  // Bulk delete states
  const [selectedForDeletion, setSelectedForDeletion] = useState(new Set());
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // 🚦 Subscription limit states
  const [limitReached, setLimitReached] = useState(false);
  const [limitCheckData, setLimitCheckData] = useState(null);



  // ADD ITEM Modal states
  const [showAddItemModal, setShowAddItemModal] = useState(false);
  
  // ADD PARTY Modal states
  const [showAddPartyModal, setShowAddPartyModal] = useState(false);
  const [newPartyName, setNewPartyName] = useState('');
  const [newPartyGstin, setNewPartyGstin] = useState('');
  const [newPartyAddress, setNewPartyAddress] = useState('');
  const [newPartyPhone, setNewPartyPhone] = useState('');
  const [newPartyEmail, setNewPartyEmail] = useState('');
  const [newPartyGstType, setNewPartyGstType] = useState('Unregistered');
  const [addPartyMessage, setAddPartyMessage] = useState('');
  
  // Additional party fields
  const [newPartyOpeningBalance, setNewPartyOpeningBalance] = useState('');
  const [newPartyCreditLimit, setNewPartyCreditLimit] = useState('');
  const [newPartyCreditPeriod, setNewPartyCreditPeriod] = useState('');
  const [showAdvancedFields, setShowAdvancedFields] = useState(false);
  
  // Mobile view carousel state
  const [currentMobileItemIndex, setCurrentMobileItemIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [mobileItemPickerOpen, setMobileItemPickerOpen] = useState(false);
  const mobileItemPickerRef = useRef(null);
  
  // Payment Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [paymentReference, setPaymentReference] = useState('');
  const [paymentNotes, setPaymentNotes] = useState('');
  const [receiptNumber, setReceiptNumber] = useState('');
  
  // Employee cash flow integration state
  const [recordInEmployeeCashFlow, setRecordInEmployeeCashFlow] = useState(false);
  const [selectedEmployeeForCashFlow, setSelectedEmployeeForCashFlow] = useState('');
  const [payments, setPayments] = useState([]);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState(null);
  
  // FIFO Payment Modal states
  const [showFIFOPaymentModal, setShowFIFOPaymentModal] = useState(false);
  const [selectedPartyForFIFO, setSelectedPartyForFIFO] = useState('');
  const [fifoPaymentAmount, setFifoPaymentAmount] = useState('');
  const [fifoPaymentDate, setFifoPaymentDate] = useState(new Date().toISOString().split('T')[0]);
  const [fifoPaymentMode, setFifoPaymentMode] = useState('Cash');
  const [fifoPaymentReference, setFifoPaymentReference] = useState('');
  const [fifoPaymentNotes, setFifoPaymentNotes] = useState('');
  const [fifoReceiptNumber, setFifoReceiptNumber] = useState('');
  
  // Payment Receipt Preview states
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [selectedPaymentForReceipt, setSelectedPaymentForReceipt] = useState(null);
  const [receiptZoom, setReceiptZoom] = useState(1);
  const receiptRef = useRef();
  
  // Image management states
  const [selectedImages, setSelectedImages] = useState({
    logo: null,
    seal: null,
    signature: null,
    qr: null
  });
  
  // Payment Details Modal states (for multiple receipts)
  const [showPaymentDetailsModal, setShowPaymentDetailsModal] = useState(false);
  const [selectedBillForReceipts, setSelectedBillForReceipts] = useState(null);
  const [newItemName, setNewItemName] = useState('');
  const [newItemMeasurement, setNewItemMeasurement] = useState('');
  const [newItemRate, setNewItemRate] = useState('');
  const [newItemType, setNewItemType] = useState('Service');
  const [newItemHsnCode, setNewItemHsnCode] = useState('');
  const [newItemGstPercentage, setNewItemGstPercentage] = useState('');
  const [newItemDescription, setNewItemDescription] = useState('');
  const [newItemPurchasePrice, setNewItemPurchasePrice] = useState('0');
  const [newItemSalePrice, setNewItemSalePrice] = useState('0');
  const [newItemCurrentStock, setNewItemCurrentStock] = useState('0');
  const [newItemIsActive, setNewItemIsActive] = useState(true);
  const [newItemCustomUnit, setNewItemCustomUnit] = useState('');
  const [addItemMessage, setAddItemMessage] = useState('');
  
  // Filter states for sales bill list
  const [filterSearch, setFilterSearch] = useState(''); // Combined search for bill number and party name
  const [filterDate, setFilterDate] = useState('');
  const [filterAmount, setFilterAmount] = useState('');
  const [filterBillNo, setFilterBillNo] = useState('');
  const [filterParty, setFilterParty] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [prioritizeCurrentFY, setPrioritizeCurrentFY] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sales_prioritizeCurrentFY');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [showFilters, setShowFilters] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('sales_showFilters');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  
  // Add state for document type (Invoice, Challan, Quotation, Sales Return, Credit Note, Debit Note)
  const [docType, setDocType] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const t = params.get('type');
      return ['invoice','challan','quotation','salesReturn','creditNote','debitNote'].includes(t) ? t : 'invoice';
    } catch { return 'invoice'; }
  }); // 'invoice', 'challan', 'quotation', 'salesReturn', 'creditNote', 'debitNote'
  const docTypeOptions = [
    { value: 'invoice', label: 'INVOICE', collection: 'salesBills', numberLabel: 'Invoice Number' },
    { value: 'challan', label: 'CHALLAN', collection: 'challans', numberLabel: 'Challan Number' },
    { value: 'quotation', label: t('quotation'), collection: 'quotations', numberLabel: 'Quotation Number' },
    { value: 'salesReturn', label: 'SALES RETURN', collection: 'salesReturns', numberLabel: 'Return Number' },
    { value: 'creditNote', label: 'CREDIT NOTE', collection: 'creditNotes', numberLabel: 'Credit Note Number' },
    { value: 'debitNote', label: 'DEBIT NOTE', collection: 'debitNotes', numberLabel: 'Debit Note Number' },
  ];



  // Add state to track GST type for each row (for UI feedback)
  const [gstTypes, setGstTypes] = useState(rows.map(() => ''));

  // Add state for editing bill
  const [editingBillId, setEditingBillId] = useState(null);

  // Quick items state variables
  const [mostUsedItems, setMostUsedItems] = useState([]);
  const [partyWiseItems, setPartyWiseItems] = useState([]);
  const [selectedPartyForQuickItems, setSelectedPartyForQuickItems] = useState('');
  const [showQuickItemsModal, setShowQuickItemsModal] = useState(false);
  const [quickItemModalPosition, setQuickItemModalPosition] = useState({ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' });
  
  // Mobile edit modal states
  const [showMobileEditModal, setShowMobileEditModal] = useState(false);
  const [partySearchText, setPartySearchText] = useState('');
  const [showPartyDropdown, setShowPartyDropdown] = useState(false);
  const partyDropdownRef = useRef(null);
  const [showTagPicker, setShowTagPicker] = useState({});
  const tagPickerContainerRefs = useRef({});
  const activeTagPickerAnchors = useRef({});
  const [tagPickerPosition, setTagPickerPosition] = useState({});
  const [tagSearchText, setTagSearchText] = useState({});
  const [showServiceEditorModal, setShowServiceEditorModal] = useState(false);
  const [serviceEditorContext, setServiceEditorContext] = useState({ rowIndex: null, serviceName: '' });
  const [serviceEditorForm, setServiceEditorForm] = useState(createDefaultServiceEntry());
  const [serviceTagMaster, setServiceTagMaster] = useState([]);
  const [showManageTagsModal, setShowManageTagsModal] = useState(false);
  const [subPartyBreakdown, setSubPartyBreakdown] = useState([]);
  const [showSubPartyModal, setShowSubPartyModal] = useState(false);
  const [subPartyDraft, setSubPartyDraft] = useState([]);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [savedBillId, setSavedBillId] = useState(null);
  const [manageTagsNewName, setManageTagsNewName] = useState('');
  const [pinTagForItem, setPinTagForItem] = useState({});

  const openServiceOperations = () => {
    const params = new URLSearchParams();
    const firstTaggedRow = rows.find((row) => Array.isArray(row.tags) && row.tags.length > 0);
    if (firstTaggedRow?.tags?.[0]) params.set('tag', firstTaggedRow.tags[0]);
    params.set('status', 'pending');
    const query = params.toString();
    navigate(`/service-operations${query ? `?${query}` : ''}`);
  };
  
  // Filtered parties for mobile search
  const filteredParties = parties.filter(p => 
    p.firmName.toLowerCase().includes(partySearchText.toLowerCase())
  );
  
  // Close party dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPartyDropdown && partyDropdownRef.current && !partyDropdownRef.current.contains(event.target)) {
        setShowPartyDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPartyDropdown]);

  // Close tag picker when clicking outside (button + dropdown container)
  useEffect(() => {
    const hasOpenPicker = Object.values(showTagPicker).some(Boolean);
    if (!hasOpenPicker) return;

    const handleClickOutside = (event) => {
      const clickedInside = event.target instanceof Element
        && Boolean(event.target.closest('[data-tag-picker-container="true"]'));
      if (!clickedInside) setShowTagPicker({});
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showTagPicker]);

  // Sync docType and view bill to URL for deep links
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    if (params.get('type') !== docType) params.set('type', docType);
    if (editingBillId) params.set('edit', editingBillId); else params.delete('edit');
    const base = window.location.hash.split('?')[0];
    const next = `${base}?${params.toString()}`;
    if (window.location.hash !== next) window.location.hash = next;
  }, [docType, editingBillId]);

  // On mount, open view/edit if URL has ids
  useEffect(() => {
    const params = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const viewId = params.get('view');
    const editId = params.get('edit');
    if (viewId) {
      const selected = docTypeOptions.find(opt => opt.value === docType);
      if (selected) {
        getDoc(doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), viewId)).then(snap => {
          if (snap.exists()) { setViewBill({ id: snap.id, ...snap.data() }); setShowViewModal(true); }
        }).catch(() => {});
      }
    }
    if (editId) setEditingBillId(editId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add state for custom fields
  const [customFields, setCustomFields] = useState({
    ewayBillNo: '',
    ewayQr: '',
    ewayDate: ''
  });
  const [showEwayBill, setShowEwayBill] = useState(() => {
    // Get user's last preference from localStorage, default to true
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('showEwayBill');
      return saved !== null ? JSON.parse(saved) : true;
    }
    return true;
  });
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showEwayBillModal, setShowEwayBillModal] = useState(false);

  // Ref for scrolling to the invoice items table
  const tableRef = useRef(null);
  const quickItemButtonRef = useRef(null);

  // Add at the top:
  const [errorMessage, setErrorMessage] = useState('');
  const [showDocTypeDropdown, setShowDocTypeDropdown] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDocTypeDropdown && !event.target.closest('.relative')) {
        setShowDocTypeDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showDocTypeDropdown]);

  // Legacy bill-level discount (kept for backward compatibility in saved bills)
  const [discountType, setDiscountType] = useState('amount');
  const [discountValue, setDiscountValue] = useState('');

  const [quotationTermsOverride, setQuotationTermsOverride] = useState('');

  // Table sorting hook with default sort by bill number descending (LIFO)
  const { sortConfig, handleSort, getSortedData } = useTableSort([], { key: 'number', direction: 'desc' });

  // Helper function to get the default quotation template
  const getQuotationTemplate = () => {
    const savedDefaults = localStorage.getItem('defaultBillTemplates');
    if (savedDefaults) {
      try {
        const parsed = JSON.parse(savedDefaults);
        if (parsed.quotation === 'QuotationTemplateColored') {
          return QuotationTemplateColored;
        }
      } catch (e) {
        console.error('Error reading default templates:', e);
      }
    }
    return QuotationTemplate;
  };

  // Helper function to get the default invoice template
  const getInvoiceTemplate = () => {
    const savedDefaults = localStorage.getItem('defaultBillTemplates');
    if (savedDefaults) {
      try {
        const parsed = JSON.parse(savedDefaults);
        if (parsed.invoice === 'ModernSalesTemplate') {
          return ModernSalesTemplate;
        } else if (parsed.invoice === 'SimpleSalesTemplate') {
          return SimpleSalesTemplate;
        } else if (parsed.invoice === 'InvoiceTemplateA5') {
          return InvoiceTemplateA5;
        }
      } catch (e) {
        console.error('Error reading default templates:', e);
      }
    }
    return InvoiceTemplate;
  };

  // Start loading IMMEDIATELY on mount and track start time - BEFORE any data operations
  useEffect(() => {
    console.log('🔄 [LOADING SCREEN] Sales: Component mounted - starting loading IMMEDIATELY (before any data operations)');
    const startTime = Date.now();
    setLoadingStartTime(startTime);
    setIsInitialLoading(true);
    startLoading('Loading Sales...', 'Fetching your sales transactions and related data');
  }, [startLoading]);

  // Reset data tracking when companyId changes and we're starting a new load
  const prevCompanyIdRef = useRef(null);
  useEffect(() => {
    if (companyId && companyId !== prevCompanyIdRef.current && isInitialLoading) {
      console.log('🔄 [LOADING SCREEN] Sales: Resetting data tracking for new company:', companyId, 'previous:', prevCompanyIdRef.current);
      prevCompanyIdRef.current = companyId;
      dataLoadedRef.current = {
        parties: false,
        items: false,
        purchases: false,
        company: false
      };
    } else if (companyId) {
      prevCompanyIdRef.current = companyId;
    }
  }, [companyId, isInitialLoading]);

  // Fetch parties
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      if (!partiesCollectionRef) {
        // Mark as loaded even if collection ref is null to prevent infinite loading
        dataLoadedRef.current.parties = true;
        checkAndStopInitialLoading();
        return;
      }
      
      const unsubscribe = onSnapshot(
        partiesCollectionRef,
        (snapshot) => {
          const allParties = [];
          snapshot.forEach((doc) => {
            allParties.push({ id: doc.id, ...doc.data() });
          });
          allParties.sort((a, b) => (a.firmName || '').localeCompare(b.firmName || ''));
          setParties(allParties);
          
          // Mark parties as loaded and check if all data is ready
          console.log('🔄 [LOADING SCREEN] Sales: Parties snapshot received', {
            count: allParties.length,
            isEmpty: snapshot.empty,
            alreadyMarked: dataLoadedRef.current.parties,
            allDataLoaded: dataLoadedRef.current
          });
          
          if (!dataLoadedRef.current.parties) {
            dataLoadedRef.current.parties = true;
            console.log('🔄 [LOADING SCREEN] Sales: Parties data loaded, checking if all data is ready', {
              dataLoaded: dataLoadedRef.current
            });
            checkAndStopInitialLoading();
          } else {
            console.log('🔄 [LOADING SCREEN] Sales: Parties already marked as loaded, skipping');
          }
        },
        (error) => {
          console.error('Error fetching parties:', error);
          // Mark as loaded even on error to prevent infinite loading
          if (!dataLoadedRef.current.parties) {
            dataLoadedRef.current.parties = true;
            checkAndStopInitialLoading();
          }
        }
      );
      return () => {
        unsubscribe();
      };
    }
  }, [db, userId, isAuthReady, appId, companyId, checkAndStopInitialLoading]);

  // Fetch items
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      if (!itemsCollectionRef) {
        // Mark as loaded even if collection ref is null to prevent infinite loading
        dataLoadedRef.current.items = true;
        checkAndStopInitialLoading();
        return;
      }
      
      const unsubscribe = onSnapshot(itemsCollectionRef, (snapshot) => {
        const itemsArr = [];
        snapshot.forEach((doc) => {
          itemsArr.push({ id: doc.id, ...doc.data() });
        });
        itemsArr.sort((a, b) =>
          getItemDisplayName(a).localeCompare(getItemDisplayName(b), undefined, { sensitivity: 'base' })
        );
        setItems(itemsArr);
        setItemsFetchError(null);
        
        // Mark items as loaded and check if all data is ready
        if (!dataLoadedRef.current.items) {
          dataLoadedRef.current.items = true;
          console.log('🔄 [LOADING SCREEN] Sales: Items data loaded, checking if all data is ready');
          checkAndStopInitialLoading();
        }
      }, (error) => {
        console.error('Error fetching items:', error);
        setItemsFetchError(error?.message || 'Failed to load items');
        // Mark as loaded even on error to prevent infinite loading — but keep prior items if any
        if (!dataLoadedRef.current.items) {
          dataLoadedRef.current.items = true;
          checkAndStopInitialLoading();
        }
      });
      return () => {
        unsubscribe();
      };
    }
  }, [db, userId, isAuthReady, appId, companyId, checkAndStopInitialLoading]);

  useEffect(() => {
    if (!db || !userId || !isAuthReady || !companyId) return;
    const ref = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!ref) return;
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = [];
        snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setServiceTagMaster(list);
      },
      (err) => console.error('serviceTags listener', err)
    );
    return () => unsub();
  }, [db, userId, isAuthReady, appId, companyId]);

  // 🚦 Set global company ID for subscription tracking
  useEffect(() => {
    if (companyId) {
      setGlobalCompanyId(companyId);
      console.log('🚦 Subscription tracking enabled for:', companyId);
    }
  }, [companyId]);


  // Fetch purchases for stock calculation
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const purchasesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'purchaseBills');
      if (!purchasesCollectionRef) {
        // Mark as loaded even if collection ref is null to prevent infinite loading
        dataLoadedRef.current.purchases = true;
        checkAndStopInitialLoading();
        return;
      }
      
      const unsubscribe = onSnapshot(purchasesCollectionRef, (snapshot) => {
        const bills = [];
        snapshot.forEach((doc) => {
          bills.push({ id: doc.id, ...doc.data() });
        });
        setPurchases(bills);
        
        // Mark purchases as loaded and check if all data is ready
        if (!dataLoadedRef.current.purchases) {
          dataLoadedRef.current.purchases = true;
          console.log('🔄 [LOADING SCREEN] Sales: Purchases data loaded, checking if all data is ready');
          checkAndStopInitialLoading();
        }
      }, (error) => {
        console.error('Error fetching purchases:', error);
        // Mark as loaded even on error to prevent infinite loading
        if (!dataLoadedRef.current.purchases) {
          dataLoadedRef.current.purchases = true;
          checkAndStopInitialLoading();
        }
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId, checkAndStopInitialLoading]);

  // Fetch company details
  useEffect(() => {
    if (db && isAuthReady && companyId) {
      const unsubscribe = onSnapshot(doc(db, 'companies', companyId), (docSnap) => {
        if (docSnap.exists()) {
          const d = docSnap.data() || {};
          const companyData = normalizeCompanyDocSnapshot(d, companyId);
          
          // Process digital signature if it's base64 data - AUTOMATIC CONVERSION
          if (companyData.signUrl && companyData.signUrl.startsWith('data:image/')) {
            console.log('🔄 Auto-converting base64 digital signature to cloud storage...');
            getDigitalSignatureUrl(companyData.signUrl, companyId).then(cloudUrl => {
              if (cloudUrl !== companyData.signUrl) {
                console.log('✅ Digital signature auto-converted to cloud storage:', cloudUrl);
                // Update company data with new cloud URL
                companyData.signUrl = cloudUrl;
                setCompany({...companyData});
                // Show user notification
                console.log('🎉 Digital signature automatically converted to cloud storage for better performance!');
                success('Digital signature automatically converted to cloud storage for better performance!', {
                  title: 'Signature Optimized',
                  duration: 5000
                });
              }
            }).catch(error => {
              console.error('❌ Failed to auto-convert digital signature:', error);
              // Keep the base64 version as fallback
              console.log('⚠️ Keeping base64 signature as fallback');
            });
          } else if (companyData.signUrl) {
            console.log('📄 Digital signature URL found (already in cloud storage):', companyData.signUrl);
          } else {
            console.log('⚠️ No digital signature found in company data');
          }
          setCompany(companyData);

          const resolvedSign = resolveCompanySignUrl(companyData);
          if (resolvedSign && resolvedSign !== lastSyncedSignUrlRef.current && loadCompanyInfo) {
            lastSyncedSignUrlRef.current = resolvedSign;
            loadCompanyInfo(companyId).catch((err) => {
              console.warn('Failed to refresh company context after signature update:', err);
            });
          }
          
          // Mark company as loaded and check if all data is ready
          if (!dataLoadedRef.current.company) {
            dataLoadedRef.current.company = true;
            console.log('🔄 [LOADING SCREEN] Sales: Company data loaded, checking if all data is ready');
            checkAndStopInitialLoading();
          }
          
          // Debug company data for seal/sign and GST
          console.log('Company data loaded:', {
            rawData: d,
            detailsData: d.details,
            brandingData: d.branding,
            finalCompanyData: companyData,
            sealUrl: companyData.sealUrl,
            signUrl: companyData.signUrl,
            gstinType: companyData.gstinType,
            gstinTypeFromRaw: d.gstinType,
            gstinTypeFromDetails: d.details?.gstinType,
            allGstinTypeSources: {
              rootGstinType: d.gstinType,
              detailsGstinType: d.details?.gstinType,
              rootGstType: d.gstType,
              basicInfoGstType: d.basicInfo?.gstType,
              detailsGstType: d.details?.gstType,
              branding: d.branding?.gstinType,
              final: companyData.gstinType
            },
            signUrlSources: {
              rootSignUrl: d.signUrl,
              detailsSignUrl: d.details?.signUrl,
              brandingSignatureUrl: d.branding?.signatureUrl,
              finalSignUrl: companyData.signUrl,
              isBase64: companyData.signUrl && companyData.signUrl.startsWith('data:image/'),
              isCloudUrl: companyData.signUrl && companyData.signUrl.startsWith('https://firebasestorage.googleapis.com/')
            }
          });
          
          // Set GST calculation type from company settings
          const gstCalcSetting = d.gstCalculation || d.details?.gstCalculation || 'excluding';
          setGstCalculationType(gstCalcSetting);
        } else {
          // If company doc doesn't exist, still mark as loaded
          if (!dataLoadedRef.current.company) {
            dataLoadedRef.current.company = true;
            checkAndStopInitialLoading();
          }
        }
      }, (error) => {
        console.error('Error fetching company:', error);
        // Mark as loaded even on error to prevent infinite loading
        if (!dataLoadedRef.current.company) {
          dataLoadedRef.current.company = true;
          checkAndStopInitialLoading();
        }
      });
      return () => unsubscribe();
    }
  }, [db, isAuthReady, companyId, checkAndStopInitialLoading]);

  // Fetch payments
  useEffect(() => {
    if (db && userId && isAuthReady && companyId) {
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      if (!paymentsRef) return;
      
      const paymentsQuery = query(paymentsRef);
      const unsubscribe = onSnapshot(paymentsQuery, (snapshot) => {
        const paymentsData = [];
        snapshot.forEach((doc) => {
          paymentsData.push({ id: doc.id, ...doc.data() });
        });
        setPayments(paymentsData);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId]);

  // Generate receipt number for sales payments
  const generateReceiptNumber = async () => {
    const currentYear = new Date().getFullYear();
    const financialYear = `${currentYear.toString().slice(-2)}-${(currentYear + 1).toString().slice(-2)}`;
    const paymentType = docType === 'invoice' ? 'I' : docType === 'challan' ? 'C' : docType === 'quotation' ? 'Q' : docType === 'salesReturn' ? 'SR' : docType === 'creditNote' ? 'CN' : docType === 'debitNote' ? 'DN' : 'I';
    
    try {
      const paymentsQuery = query(getCollectionForCompany(db, { companyId, appId, userId }, 'payments'));
      const paymentsSnapshot = await getDocs(paymentsQuery);
      
      let highestSequence = 0;
      
      paymentsSnapshot.forEach((doc) => {
        const payment = doc.data();
        if (payment.receiptNumber) {
          const regexPattern = new RegExp(`PR${paymentType}${financialYear.replace('-', '\\-')}\\/(\\d+)`);
          const match = payment.receiptNumber.match(regexPattern);
          
          if (match) {
            const sequence = parseInt(match[1]);
            if (sequence > highestSequence) {
              highestSequence = sequence;
            }
          }
        }
      });
      
      const nextSequence = highestSequence + 1;
      return `PR${paymentType}${financialYear}/${nextSequence.toString().padStart(3, '0')}`;
    } catch (error) {
      console.error('Error generating receipt number:', error);
      return `PR${paymentType}${financialYear}/001`;
    }
  };

  // Get party name by ID
  const getPartyName = (partyId) => {
    const party = parties.find(p => p.id === partyId);
    return party ? (party.firmName || party.name) : 'Unknown Party';
  };

  // Calculate outstanding amount for a bill
  const getBillOutstanding = (bill) => {
    const billPayments = payments.filter(p => 
      p.allocations && p.allocations.some(a => a.billId === bill.id && a.billType === docType)
    );
    
    const totalPaid = billPayments.reduce((sum, payment) => {
      const allocation = payment.allocations.find(a => a.billId === bill.id && a.billType === docType);
      return sum + (allocation ? allocation.allocatedAmount : 0);
    }, 0);
    
    const totalAmount = parseFloat(bill.totalAmount || bill.amount) || 0;
    return totalAmount - totalPaid;
  };

  // Fetch bills for the selected type
  useEffect(() => {
    if (db && userId && isAuthReady) {
      const selected = docTypeOptions.find(opt => opt.value === docType);
      if (!selected) return;
      const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, selected.collection);
      if (!collectionRef) return;
      
      const unsubscribe = onSnapshot(collectionRef, (snapshot) => {
        const arr = [];
        snapshot.forEach((doc) => {
          arr.push({ id: doc.id, ...doc.data() });
        });
        // LIFO sorting is now handled by the pagination utility
        setBills(arr);
      }, (error) => {
        console.error('Error fetching bills:', error);
        setBills([]);
      });
      return () => unsubscribe();
    }
  }, [db, userId, isAuthReady, appId, companyId, docType]);

  // Fetch most used items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && bills.length > 0) {
      // Aggregate item usage from sales bills
      const itemUsage = {};
      
      bills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = getBillLineItems(bill);
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.invoiceDate || bill.date ? new Date(bill.invoiceDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 most used items
      
      // Add item names to the quick items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setMostUsedItems(itemsWithNames);
    } else {
      setMostUsedItems([]);
    }
  }, [db, userId, isAuthReady, bills, items]);

  // Fetch party-wise items for quick access
  useEffect(() => {
    if (db && userId && isAuthReady && selectedPartyForQuickItems && bills.length > 0) {
      // Filter bills by selected party
      const partyBills = bills.filter(bill => bill.party === selectedPartyForQuickItems);
      
      // Aggregate item usage from party-specific bills
      const itemUsage = {};
      
      partyBills.forEach(bill => {
        // Check both bill.items and bill.rows for item data
        const billItems = getBillLineItems(bill);
        if (Array.isArray(billItems)) {
          billItems.forEach(item => {
            if (item.item && (item.qty || item.nos) && item.rate) {
              if (!itemUsage[item.item]) {
                itemUsage[item.item] = {
                  itemId: item.item, // Use itemId for consistency
                  id: item.item,
                  count: 0,
                  lastUsed: null,
                  lastRate: 0,
                  lastQty: 0
                };
              }
              
              itemUsage[item.item].count += 1;
              const billDate = bill.invoiceDate || bill.date ? new Date(bill.invoiceDate || bill.date) : new Date();
              
              if (!itemUsage[item.item].lastUsed || billDate > itemUsage[item.item].lastUsed) {
                itemUsage[item.item].lastUsed = billDate;
                itemUsage[item.item].lastRate = parseFloat(item.rate) || 0;
                itemUsage[item.item].lastQty = parseFloat(item.qty || item.nos) || 0;
              }
            }
          });
        }
      });
      
      // Convert to array and sort by usage count, then by last used date
      const sortedItems = Object.values(itemUsage)
        .sort((a, b) => {
          if (b.count !== a.count) return b.count - a.count;
          return new Date(b.lastUsed) - new Date(a.lastUsed);
        })
        .slice(0, 20); // Top 20 party-specific items
      
      // Add item names to the party-wise items data
      const itemsWithNames = sortedItems.map(item => {
        const itemDetails = items.find(it => it.id === item.itemId);
        return {
          ...item,
          itemName: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item',
          name: itemDetails ? (itemDetails.itemName || itemDetails.name || 'Unknown Item') : 'Unknown Item'
        };
      });
      
      setPartyWiseItems(itemsWithNames);
    } else if (!selectedPartyForQuickItems) {
      setPartyWiseItems([]);
    } else {
      setPartyWiseItems([]);
    }
  }, [db, userId, isAuthReady, selectedPartyForQuickItems, bills, items]);

  // Auto-generate number for the selected type
  useEffect(() => {
    if (editingBillId) return; // Do not auto-generate when editing
    const prefixMap = { 
      invoice: 'INV', 
      challan: 'CHA', 
      quotation: 'QUO',
      salesReturn: 'SR',
      creditNote: 'CN',
      debitNote: 'DN'
    };
    const prefix = prefixMap[docType] || 'INV';
    const fy = getFinancialYear(invoiceDate);
    const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
    const serials = bills
      .filter(bill => (bill.number || '').startsWith(prefix + fyShort))
      .map(bill => parseInt((bill.number || '').split('/')[1], 10))
      .filter(n => !isNaN(n));
    const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
    setInvoiceNumber(`${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`);
  }, [invoiceDate, bills, docType, editingBillId]);

  // When party changes, update GST for all rows
  useEffect(() => {
    // Recalculate GST split for all rows when party changes
    const partyObj = parties.find(p => p.id === party);
    const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';
    
    const updatedRows = rows.map((row, idx) => {
      let itemObj = findItemInMaster(row.item, items);
      const gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
      const sellerGstin = company.gstin || '';
      const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
      
      // Only calculate GST if party is Regular
      if (partyGstType === 'Regular') {
        const split = getGstTypeAndSplit(sellerGstin, buyerGstin, gstPercent);
        return {
          ...row,
          sgst: split.sgst,
          cgst: split.cgst,
          igst: split.igst,
        };
      } else {
        // For Composition/Unregistered parties, set GST to 0
        return {
          ...row,
          sgst: 0,
          cgst: 0,
          igst: 0,
        };
      }
    });
    // Update GST type for UI feedback
    const updatedGstTypes = rows.map((row, idx) => {
      let itemObj = findItemInMaster(row.item, items);
      const gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
      const sellerGstin = company.gstin || '';
      const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
      
      if (partyGstType === 'Regular') {
        const split = getGstTypeAndSplit(sellerGstin, buyerGstin, gstPercent);
        return split.type;
      } else {
        return 'NO_GST';
      }
    });
    setRows(updatedRows);
    setGstTypes(updatedGstTypes);
    
    // Update party search text when party changes
    if (party) {
      const selectedParty = parties.find(p => p.id === party);
      if (selectedParty) {
        setPartySearchText(selectedParty.firmName);
      }
    } else {
      setPartySearchText('');
    }
    // eslint-disable-next-line
  }, [party]);

  const getRowServices = (row) => Array.isArray(row?.services) ? row.services.map(normalizeServiceEntry) : [];
  const getTagDisplayText = (_row, tag) => String(tag || '').trim() || 'Tag';

  const openServiceEditor = (rowIndex, tag) => {
    const row = rows[rowIndex];
    if (!row) return;
    const serviceName = normalizeTagName(tag);
    const existing = getRowServices(row).find((service) => service.serviceName === serviceName);
    const built = buildServiceEntryForTag({
      serviceName,
      row,
      serviceTagMaster,
      parties,
      existingEntry: existing || null,
    });
    setServiceEditorContext({ rowIndex, serviceName });
    setServiceEditorForm(existing ? normalizeServiceEntry(existing) : normalizeServiceEntry(built));
    setShowServiceEditorModal(true);
  };

  const rememberPreferredVendorForTag = async (serviceName, vendorId) => {
    if (!vendorId || !db || !companyId) return;
    const vendor = parties.find((p) => p.id === vendorId);
    if (!vendor) return;
    const normalized = normalizeTagName(serviceName);
    if (!normalized) return;
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!col) return;
    const tagMeta = findServiceTagMeta(serviceTagMaster, normalized);
    const ids = promotePreferredVendorId(normalizePreferredVendorIds(tagMeta), vendor.id);
    try {
      await setDoc(
        doc(col, getServiceTagDocId(serviceTagMaster, normalized)),
        {
          normalizedName: normalized,
          name: normalized,
          ...buildPreferredVendorPatch(ids, parties),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
    } catch (e) {
      console.error('rememberPreferredVendorForTag', e);
    }
  };

  const getServiceEditorVendorOptions = (serviceName) => {
    const tagMeta = findServiceTagMeta(serviceTagMaster, serviceName);
    const tagged = getVendorsForServiceTag(parties, serviceName, tagMeta);
    if (tagged.length > 0) return tagged;
    return parties.filter((p) => p.partyType === 'Seller' || p.partyType === 'Both');
  };

  const closeServiceEditor = () => {
    setShowServiceEditorModal(false);
    setServiceEditorContext({ rowIndex: null, serviceName: '' });
    setServiceEditorForm(createDefaultServiceEntry());
  };

  const saveServiceEditor = () => {
    const rowIndex = serviceEditorContext.rowIndex;
    const serviceName = String(serviceEditorForm.serviceName || serviceEditorContext.serviceName || '').trim().toLowerCase();
    if (rowIndex === null || rowIndex === undefined || !serviceName) {
      closeServiceEditor();
      return;
    }

    const selectedVendor = parties.find((p) => p.id === serviceEditorForm.vendorId);
    const normalizedService = normalizeServiceEntry({
      ...serviceEditorForm,
      serviceName,
      vendorName: selectedVendor?.firmName || serviceEditorForm.vendorName || '',
      internalCost: serviceEditorForm.internalCost === '' ? '' : parseFloat(serviceEditorForm.internalCost) || 0,
      customerCharge: serviceEditorForm.customerCharge === '' ? '' : parseFloat(serviceEditorForm.customerCharge) || 0,
    });

    setRows((currentRows) => currentRows.map((row, idx) => {
      if (idx !== rowIndex) return row;
      const normalizedTags = Array.isArray(row.tags) ? row.tags : [];
      const normalizedServices = getRowServices(row);
      const updatedTags = normalizedTags.some((t) => String(t).toLowerCase() === serviceName)
        ? normalizedTags
        : [...normalizedTags, serviceName];
      const existingIndex = normalizedServices.findIndex((service) => service.serviceName === serviceName);
      let updatedServices = [...normalizedServices];
      if (existingIndex >= 0) {
        updatedServices[existingIndex] = { ...updatedServices[existingIndex], ...normalizedService };
      } else {
        updatedServices.push(normalizedService);
      }
      return { ...row, tags: updatedTags, services: updatedServices };
    }));

    if (serviceEditorForm.vendorId) {
      void rememberPreferredVendorForTag(serviceName, serviceEditorForm.vendorId);
    }
    closeServiceEditor();
  };

  const getItemDefaultServiceTags = (row) => {
    if (!row?.item) return [];
    const itemObj = items.find((it) => it.id === row.item);
    const arr = itemObj?.defaultServiceTags;
    if (!Array.isArray(arr)) return [];
    return Array.from(new Set(arr.map((t) => normalizeTagName(t)).filter(Boolean)));
  };

  const getAllKnownTags = () => {
    const tagSet = collectLegacyTagsFromRowsAndBills(rows, bills);
    serviceTagMaster.forEach((t) => {
      if (t.isActive === false) return;
      const n = t.normalizedName ? normalizeTagName(t.normalizedName) : normalizeTagName(t.name);
      if (n) tagSet.add(n);
    });
    return Array.from(tagSet).sort();
  };

  const getTagPickerBuckets = (rowIndex) => {
    const row = rows[rowIndex];
    const search = (tagSearchText[rowIndex] || '').toLowerCase().trim();
    const all = getAllKnownTags();
    const itemDefaults = getItemDefaultServiceTags(row);
    const rowTagsLower = new Set((row?.tags || []).map((t) => String(t).toLowerCase()));
    const suggested = itemDefaults.filter((t) => !rowTagsLower.has(t) && (!search || t.includes(search)));
    const suggestedSet = new Set(suggested);
    const others = all.filter((t) => !suggestedSet.has(t) && (!search || t.includes(search)));
    return { suggested, others };
  };

  const upsertServiceTagToFirestore = async (rawName) => {
    const normalized = normalizeTagName(rawName);
    if (!normalized || !db || !companyId) return;
    const id = getServiceTagDocId(serviceTagMaster, normalized);
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!col) return;
    const ref = doc(col, id);
    const snap = await getDoc(ref);
    const payload = {
      normalizedName: normalized,
      name: normalized,
      isActive: true,
      source: 'sales',
      updatedAt: serverTimestamp(),
    };
    if (!snap.exists()) payload.createdAt = serverTimestamp();
    await setDoc(ref, payload, { merge: true });
  };

  const pinTagToItemDefault = async (row, tagLower) => {
    if (!row?.item) return false;
    const itemObj = items.find((it) => it.id === row.item);
    const existing = Array.isArray(itemObj?.defaultServiceTags)
      ? itemObj.defaultServiceTags.map(normalizeTagName).filter(Boolean)
      : [];
    if (existing.includes(tagLower)) return false;
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
    if (!col) return false;
    const itemRef = doc(col, row.item);
    await updateDoc(itemRef, { defaultServiceTags: [...existing, tagLower] });
    return true;
  };

  const handleAddTag = async (rowIndex, tag, openDetails = false) => {
    if (!tag || !tag.trim()) return;
    const tagLower = tag.toLowerCase().trim();
    const rowBefore = rows[rowIndex];
    const shouldPin = !!pinTagForItem[rowIndex] && !!rowBefore?.item;

    let addedOrExists = false;
    setRows((currentRows) =>
      currentRows.map((row, i) => {
        if (i !== rowIndex) return row;
        const existingTags = row.tags || [];
        const existingServices = getRowServices(row);
        const hasServiceForTag = existingServices.some((service) => service.serviceName === tagLower);
        const newEntry = normalizeServiceEntry(
          buildServiceEntryForTag({
            serviceName: tagLower,
            row,
            serviceTagMaster,
            parties,
          })
        );
        const ensuredServices = hasServiceForTag
          ? existingServices.map((svc) =>
              svc.serviceName === tagLower && (svc.internalCost === '' || svc.internalCost == null)
                ? { ...svc, ...newEntry, id: svc.id, createdAt: svc.createdAt }
                : svc
            )
          : [...existingServices, { ...newEntry, id: `svc_${Date.now()}_${Math.random().toString(36).slice(2, 8)}` }];
        if (existingTags.some((t) => t.toLowerCase() === tagLower)) {
          addedOrExists = true;
          return { ...row, services: ensuredServices };
        }
        addedOrExists = true;
        return { ...row, tags: [...existingTags, tagLower], services: ensuredServices };
      })
    );
    setTagSearchText((prev) => ({ ...prev, [rowIndex]: '' }));
    setShowTagPicker((prev) => ({ ...prev, [rowIndex]: false }));
    setPinTagForItem((prev) => ({ ...prev, [rowIndex]: false }));

    try {
      await upsertServiceTagToFirestore(tagLower);
      if (shouldPin) {
        const pinned = await pinTagToItemDefault(rowBefore, tagLower);
        if (pinned) success('Tag saved to item defaults for quick entry.');
      }
    } catch (e) {
      console.error(e);
      error('Could not sync tag to registry');
    }

    if (addedOrExists && openDetails) {
      openServiceEditor(rowIndex, tagLower);
    }
  };

  const handleRemoveTag = (rowIndex, tagIndex) => {
    const row = rows[rowIndex];
    const removedTag = row?.tags?.[tagIndex];
    setRows((currentRows) =>
      currentRows.map((r, i) => {
        if (i !== rowIndex) return r;
        const existingTags = r.tags || [];
        const nextTags = existingTags.filter((_, idx) => idx !== tagIndex);
        const removedTagLower = String(removedTag || '').trim().toLowerCase();
        const services = getRowServices(r).filter((service) => service.serviceName !== removedTagLower);
        return { ...r, tags: nextTags, services };
      })
    );
  };

  const updateTagPickerPosition = useCallback((rowIndex) => {
    const anchor = activeTagPickerAnchors.current[rowIndex] || tagPickerContainerRefs.current[rowIndex];
    if (!anchor) return false;
    const rect = anchor.getBoundingClientRect();
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    const margin = 12;
    const preferredWidth = viewportWidth < 640
      ? Math.min(rect.width || viewportWidth - margin * 2, viewportWidth - margin * 2)
      : 320;

    let left = rect.left;
    if (left + preferredWidth > viewportWidth - margin) left = viewportWidth - margin - preferredWidth;
    if (left < margin) left = margin;

    // Always anchor below the clicked button/container for predictable placement.
    const top = Math.min(viewportHeight - margin, rect.bottom + 8);

    setTagPickerPosition((prev) => ({
      ...prev,
      [rowIndex]: { top, left, width: preferredWidth, maxHeight: viewportHeight - margin * 2 },
    }));
    return true;
  }, []);

  const toggleTagPicker = (rowIndex, triggerEl = null) => {
    const willOpen = !showTagPicker[rowIndex];
    setShowTagPicker((prev) => ({ ...prev, [rowIndex]: !prev[rowIndex] }));
    if (willOpen) {
      if (triggerEl instanceof Element) {
        activeTagPickerAnchors.current[rowIndex] =
          triggerEl.closest('[data-tag-picker-container="true"]') || triggerEl;
      }
      const positionedNow = updateTagPickerPosition(rowIndex);
      if (!positionedNow) {
        requestAnimationFrame(() => {
          updateTagPickerPosition(rowIndex);
        });
      }
      setTagSearchText((prev) => ({ ...prev, [rowIndex]: '' }));
      setPinTagForItem((prev) => ({ ...prev, [rowIndex]: false }));
    } else {
      delete activeTagPickerAnchors.current[rowIndex];
      setTagPickerPosition((prev) => {
        if (!(rowIndex in prev)) return prev;
        const next = { ...prev };
        delete next[rowIndex];
        return next;
      });
    }
  };

  useEffect(() => {
    const openRowIndexes = Object.entries(showTagPicker)
      .filter(([, isOpen]) => Boolean(isOpen))
      .map(([key]) => Number(key))
      .filter((idx) => !Number.isNaN(idx));
    if (openRowIndexes.length === 0) return undefined;

    const recalcOpenTagPickers = () => {
      openRowIndexes.forEach((rowIndex) => {
        updateTagPickerPosition(rowIndex);
      });
    };

    recalcOpenTagPickers();
    window.addEventListener('resize', recalcOpenTagPickers);
    window.addEventListener('scroll', recalcOpenTagPickers, true);
    return () => {
      window.removeEventListener('resize', recalcOpenTagPickers);
      window.removeEventListener('scroll', recalcOpenTagPickers, true);
    };
  }, [showTagPicker, updateTagPickerPosition]);

  const deactivateServiceTag = async (tagDoc) => {
    if (!db || !companyId || !tagDoc?.id) return;
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!col) return;
    try {
      await updateDoc(doc(col, tagDoc.id), { isActive: false, updatedAt: serverTimestamp() });
      success('Tag deactivated.');
    } catch (e) {
      console.error(e);
      error('Could not deactivate tag');
    }
  };

  const restoreServiceTag = async (tagDoc) => {
    if (!db || !companyId || !tagDoc?.id) return;
    const col = getCollectionForCompany(db, { companyId, appId, userId }, 'serviceTags');
    if (!col) return;
    try {
      await updateDoc(doc(col, tagDoc.id), { isActive: true, updatedAt: serverTimestamp() });
      success('Tag restored.');
    } catch (e) {
      console.error(e);
      error('Could not restore tag');
    }
  };

  const handleManageAddTag = async () => {
    const raw = manageTagsNewName.trim();
    if (!raw) return;
    try {
      await upsertServiceTagToFirestore(raw);
      setManageTagsNewName('');
      success('Tag added.');
    } catch (e) {
      console.error(e);
      error('Could not add tag');
    }
  };

  const renderTagPickerDropdown = (rowIndex) => {
    const buckets = getTagPickerBuckets(rowIndex);
    const empty = buckets.suggested.length === 0 && buckets.others.length === 0;
    return (
      <>
        <div className="px-3 py-2 border-b border-slate-100 text-xs text-slate-600">
          Pick or type a tag. Vendor and costs can be set here or in Service Ops.
        </div>
        <input
          type="text"
          value={tagSearchText[rowIndex] || ''}
          onChange={(e) => setTagSearchText((prev) => ({ ...prev, [rowIndex]: e.target.value }))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && tagSearchText[rowIndex]?.trim()) {
              void handleAddTag(rowIndex, tagSearchText[rowIndex]);
            } else if (e.key === 'Escape') {
              setShowTagPicker((prev) => ({ ...prev, [rowIndex]: false }));
            }
          }}
          placeholder="Type to search or create a tag…"
          className="w-full min-h-[44px] sm:min-h-0 px-3 py-2.5 sm:py-2.5 border-b border-gray-200 text-base sm:text-sm touch-manipulation placeholder:text-gray-400"
          autoFocus
        />
        {rows[rowIndex]?.item && (
          <label className="flex items-start gap-3 px-3 py-2.5 sm:py-2 text-xs text-gray-600 border-b border-gray-100 cursor-pointer hover:bg-gray-50 touch-manipulation">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300"
              checked={!!pinTagForItem[rowIndex]}
              onChange={(e) => setPinTagForItem((prev) => ({ ...prev, [rowIndex]: e.target.checked }))}
            />
            <span className="leading-snug">Also save to this item&apos;s defaults for quick entry</span>
          </label>
        )}
        <div className="max-h-[min(50dvh,14rem)] sm:max-h-52 overflow-y-auto overscroll-contain [-webkit-overflow-scrolling:touch]">
          {buckets.suggested.length > 0 && (
            <>
              <div className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">Suggested for this item</div>
              {buckets.suggested.map((tag) => (
                <button
                  key={`s-${tag}`}
                  type="button"
                  onClick={() => void handleAddTag(rowIndex, tag)}
                  className="w-full min-h-[44px] sm:min-h-0 text-left px-3 py-3 sm:py-2 hover:bg-emerald-50 active:bg-emerald-100 text-base sm:text-sm touch-manipulation"
                >
                  <span className="break-words">{tag}</span>
                </button>
              ))}
            </>
          )}
          {buckets.others.length > 0 && (
            <>
              {buckets.suggested.length > 0 && <div className="border-t border-gray-100" />}
              <div className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">All tags</div>
              {buckets.others.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => void handleAddTag(rowIndex, tag)}
                  className="w-full min-h-[44px] sm:min-h-0 text-left px-3 py-3 sm:py-2 hover:bg-gray-100 active:bg-gray-200 text-base sm:text-sm touch-manipulation"
                >
                  <span className="break-words">{tag}</span>
                </button>
              ))}
            </>
          )}
          {empty && <div className="px-3 py-3 text-sm text-gray-500">No tags found</div>}
        </div>
        {tagSearchText[rowIndex]?.trim() && (
          <div className="p-2 border-t border-gray-200 flex gap-2">
            <button
              type="button"
              onClick={() => void handleAddTag(rowIndex, tagSearchText[rowIndex], false)}
              className="flex-1 min-h-[44px] sm:min-h-0 text-sm px-3 py-2.5 sm:py-2 rounded bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800 touch-manipulation"
            >
              Add tag
            </button>
            <button
              type="button"
              onClick={() => void handleAddTag(rowIndex, tagSearchText[rowIndex], true)}
              className="shrink-0 min-h-[44px] sm:min-h-0 text-sm px-3 py-2.5 sm:py-2 rounded border border-gray-300 bg-white hover:bg-gray-50 touch-manipulation"
              title="Add and set vendor or cost now"
            >
              + Cost
            </button>
          </div>
        )}
      </>
    );
  };

  // Calculate area, amount, GST, and total for each row
  const handleRowChange = (idx, field, value) => {
    setRows(currentRows => {
      const updatedRows = currentRows.map((row, i) => {
        if (i !== idx) return row;
        let updated = { ...row, [field]: value };

        // Numeric fields must never be negative (input min=0 only blocks the
        // spinner arrows, a typed "-50" would still reach the calculations)
        if (['rate', 'lineDiscountValue', 'sgst', 'cgst', 'igst', 'nos', 'length', 'height'].includes(field)) {
          const n = parseFloat(value);
          if (isFinite(n) && n < 0) updated[field] = 0;
        }

        // If editing an existing bill, never map GST fields from item master, even if other fields change
        if (editingBillId && field !== 'item') {
          // Only update the changed field and recalculate total using current GST values
          let itemObj = findItemInMaster(row.item, items);
          const unit = itemObj ? itemObj.quantityMeasurement : '';
          
          // Ensure unit is set in the row if item has quantityMeasurement
          if (itemObj && itemObj.quantityMeasurement) {
            updated.unit = itemObj.quantityMeasurement;
            updated.quantityMeasurement = itemObj.quantityMeasurement;
          }
          
          // When the qty expression is cleared, reset the synced nos so the old qty doesn't stick
          if (field === 'qtyExpr' && !String(value || '').trim()) {
            updated.nos = 1;
            updated.length = 0;
            updated.height = 0;
          }
          // Compute quantity from qtyExpr if present/valid; else fallback to Nos×Length×Height
          const parsed = parseQtyExpression(updated.qtyExpr);
          let computedQty;
          if (parsed.ok) {
            computedQty = parsed.value;
            updated.qtyDisplay = parsed.display;
          } else {
            computedQty = (parseFloat(updated.nos) || 0) * (parseFloat(updated.length) || 1) * (parseFloat(updated.height) || 1);
            if (!updated.qtyExpr) {
              const a = parseFloat(updated.nos) || 0; const b = parseFloat(updated.length) || 1; const c = parseFloat(updated.height) || 1;
              updated.qtyDisplay = `${a}×${b}×${c}`;
            }
          }
          updated.qty = computedQty;
          // Keep nos in sync for legacy stock logic
          updated.nos = computedQty;
          updated.amount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
          // Compute effective amount after per-line discount
          const rawAmount = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
          let lineDiscAmt = 0;
          if ((updated.lineDiscountType || 'amount') === 'percent') {
            lineDiscAmt = rawAmount * ((parseFloat(updated.lineDiscountValue) || 0) / 100);
          } else {
            lineDiscAmt = parseFloat(updated.lineDiscountValue) || 0;
          }
          lineDiscAmt = Math.min(Math.max(lineDiscAmt, 0), rawAmount);
          updated.amount = roundTo2Decimals(rawAmount - lineDiscAmt);

          // Always use the GST values already present in the row
          updated.sgst = row.sgst;
          updated.cgst = row.cgst;
          updated.igst = row.igst;
          
          // GST calculation based on bill-wise GST calculation type
          let taxableAmount, gstAmount;
          const gstPercent = (parseFloat(row.sgst) || 0) + (parseFloat(row.cgst) || 0) + (parseFloat(row.igst) || 0);
          
          if (gstCalculationType === 'including' && gstPercent > 0) {
            // Rate includes GST - apply line discount on the GST-inclusive amount, then split out GST
            const rawInclusive = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
            let inclusiveDisc = 0;
            if ((updated.lineDiscountType || 'amount') === 'percent') {
              inclusiveDisc = rawInclusive * ((parseFloat(updated.lineDiscountValue) || 0) / 100);
            } else {
              inclusiveDisc = parseFloat(updated.lineDiscountValue) || 0;
            }
            inclusiveDisc = Math.min(Math.max(inclusiveDisc, 0), rawInclusive);
            const totalAmount = rawInclusive - inclusiveDisc;
            taxableAmount = totalAmount / (1 + gstPercent / 100);
            gstAmount = totalAmount - taxableAmount;
            updated.amount = roundTo2Decimals(taxableAmount);
          } else {
            // Rate excludes GST - calculate GST on taxable amount
            taxableAmount = updated.amount;
            gstAmount = (taxableAmount * gstPercent) / 100;
          }
          
          // Calculate individual GST amounts
          const sgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(row.sgst) || 0)) / gstPercent : 0;
          const cgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(row.cgst) || 0)) / gstPercent : 0;
          const igstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(row.igst) || 0)) / gstPercent : 0;
          
          updated.total = roundTo2Decimals(taxableAmount + gstAmount);
          return updated;
        }

        // If user edits SGST, CGST, or IGST directly, update only that field and recalculate total
        if (["sgst", "cgst", "igst"].includes(field)) {
          // GST calculation based on bill-wise GST calculation type
          let taxableAmount, gstAmount;
          const gstPercent = (parseFloat(updated.sgst) || 0) + (parseFloat(updated.cgst) || 0) + (parseFloat(updated.igst) || 0);
          
          if (gstCalculationType === 'including' && gstPercent > 0) {
            // Rate includes GST - apply line discount on the GST-inclusive amount, then split out GST
            const rawInclusive = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
            let inclusiveDisc = 0;
            if ((updated.lineDiscountType || 'amount') === 'percent') {
              inclusiveDisc = rawInclusive * ((parseFloat(updated.lineDiscountValue) || 0) / 100);
            } else {
              inclusiveDisc = parseFloat(updated.lineDiscountValue) || 0;
            }
            inclusiveDisc = Math.min(Math.max(inclusiveDisc, 0), rawInclusive);
            const totalAmount = rawInclusive - inclusiveDisc;
            taxableAmount = totalAmount / (1 + gstPercent / 100);
            gstAmount = totalAmount - taxableAmount;
            updated.amount = roundTo2Decimals(taxableAmount);
          } else {
            // Rate excludes GST - calculate GST on taxable amount
            taxableAmount = updated.amount;
            gstAmount = (taxableAmount * gstPercent) / 100;
          }
          
          // Calculate individual GST amounts
          const sgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.sgst) || 0)) / gstPercent : 0;
          const cgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.cgst) || 0)) / gstPercent : 0;
          const igstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.igst) || 0)) / gstPercent : 0;
          
          updated.total = roundTo2Decimals(taxableAmount + gstAmount);
          return updated;
        }

        // If item is changed (new bill or edit), fetch GST from item master
        if (field === 'item') {
          let itemObj = findItemInMaster(value, items);
          updated.gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
          // Denormalize name immediately so list/print work even if master changes later
          if (itemObj) {
            updated.itemName = getItemDisplayName(itemObj);
          } else if (!value) {
            updated.itemName = '';
          }
          
          // Set unit and quantityMeasurement from item
          if (itemObj) {
            updated.unit = itemObj.quantityMeasurement || '';
            updated.quantityMeasurement = itemObj.quantityMeasurement || '';
            const fromDefaults = Array.isArray(itemObj.defaultServiceTags)
              ? itemObj.defaultServiceTags.map(normalizeTagName).filter(Boolean)
              : [];
            const fromLegacy = Array.isArray(itemObj.tags)
              ? itemObj.tags.map((t) => normalizeTagName(t)).filter(Boolean)
              : [];
            const fromItem = [...new Set([...fromDefaults, ...fromLegacy])];
            const existingTags = updated.tags || [];
            const existingLower = new Set(existingTags.map((t) => String(t).toLowerCase()));
            const newTagNames = [];
            fromItem.forEach((t) => {
              if (t && !existingLower.has(t)) {
                newTagNames.push(t);
                existingLower.add(t);
              }
            });
            updated.tags = [...existingTags, ...newTagNames];
            let services = Array.isArray(updated.services) ? [...updated.services] : [];
            const svcNameSet = new Set(
              services.map((s) => normalizeTagName(s.serviceName || ''))
            );
            newTagNames.forEach((tagLower) => {
              if (!svcNameSet.has(tagLower)) {
                services.push(createDefaultServiceEntry(tagLower));
                svcNameSet.add(tagLower);
              }
            });
            updated.services = services;
          }
          
          // Default qty and rate on item select
          if (!updated.nos) updated.nos = 1;
          // Prefer last party-wise rate, else item sale price
          const partyId = party;
          let defaultRate = 0;
          try {
            // Search latest sales bill for this party and item
            for (let bIndex = bills.length - 1; bIndex >= 0; bIndex -= 1) {
              const bill = bills[bIndex];
              if ((bill.partyId || bill.party) !== partyId) continue;
              const foundRow = (bill.rows || []).find(r => r.item === value);
              if (foundRow && foundRow.rate) { defaultRate = parseFloat(foundRow.rate) || 0; break; }
            }
          } catch (e) { /* no-op */ }
          if (!defaultRate && itemObj) {
            defaultRate = parseFloat(itemObj.salePrice || itemObj.rate || 0) || 0;
          }
          if (!updated.rate || updated.rate === 0) {
            updated.rate = defaultRate;
          }
          
          // Get party GST type and only calculate GST if party is Regular
          const sellerGstin = company.gstin || '';
          const partyObj = parties.find(p => p.id === party);
          const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
          const partyGstType = partyObj ? (partyObj.gstType || 'Unregistered') : 'Unregistered';
          
          if (partyGstType === 'Regular') {
            // Only calculate GST if party is Regular
            if (sellerGstin && buyerGstin && sellerGstin.length >= 2 && buyerGstin.length >= 2) {
              if (sellerGstin.substring(0, 2) === buyerGstin.substring(0, 2)) {
                updated.sgst = updated.gstPercent / 2;
                updated.cgst = updated.gstPercent / 2;
                updated.igst = 0;
              } else {
                updated.sgst = 0;
                updated.cgst = 0;
                updated.igst = updated.gstPercent;
              }
            } else {
              updated.sgst = updated.gstPercent / 2;
              updated.cgst = updated.gstPercent / 2;
              updated.igst = 0;
            }
          } else {
            // For Composition/Unregistered parties, set GST to 0
            updated.sgst = 0;
            updated.cgst = 0;
            updated.igst = 0;
          }
        }

        // For all other fields (new bill), preserve GST values and just recalculate total
        let itemObj = findItemInMaster(field === 'item' ? value : row.item, items);
        const unit = itemObj ? itemObj.quantityMeasurement : '';
        
        // Set unit and quantityMeasurement in the row whenever item is present
        if (itemObj && (field === 'item' || !updated.unit)) {
          updated.unit = unit;
          updated.quantityMeasurement = unit;
        }
        
        // When the qty expression is cleared, reset the synced nos so the old qty doesn't stick
        if (field === 'qtyExpr' && !String(value || '').trim()) {
          updated.nos = 1;
          updated.length = 0;
          updated.height = 0;
        }
        // Compute quantity from qtyExpr if present/valid; else fallback to Nos×Length×Height
        const parsed = parseQtyExpression(updated.qtyExpr);
        let computedQty;
        if (parsed.ok) {
          computedQty = parsed.value;
          updated.qtyDisplay = parsed.display;
        } else {
          computedQty = (parseFloat(updated.nos) || 0) * (parseFloat(updated.length) || 1) * (parseFloat(updated.height) || 1);
          if (!updated.qtyExpr) {
            const a = parseFloat(updated.nos) || 0; const b = parseFloat(updated.length) || 1; const c = parseFloat(updated.height) || 1;
            updated.qtyDisplay = `${a}×${b}×${c}`;
          }
        }
        updated.qty = computedQty;
        // Keep nos in sync for legacy stock logic
        updated.nos = computedQty;
        // Apply per-line discount to amount
        const rawAmount = calculateLineTotal(updated.qty, updated.rate);
        let lineDiscAmt = 0;
        if ((updated.lineDiscountType || 'amount') === 'percent') {
          lineDiscAmt = calculateGSTAmount(rawAmount, updated.lineDiscountValue);
        } else {
          lineDiscAmt = roundTo2Decimals(parseFloat(updated.lineDiscountValue) || 0);
        }
        lineDiscAmt = Math.min(Math.max(lineDiscAmt, 0), rawAmount);
        updated.amount = roundTo2Decimals(rawAmount - lineDiscAmt);
        // GST and total calculation based on bill-wise GST calculation type
        let taxableAmount, gstAmount;
        const gstPercent = (parseFloat(updated.sgst) || 0) + (parseFloat(updated.cgst) || 0) + (parseFloat(updated.igst) || 0);
        
        if (gstCalculationType === 'including' && gstPercent > 0) {
          // Rate includes GST - apply line discount on the GST-inclusive amount, then split out GST
          const rawInclusive = (parseFloat(updated.qty) || 0) * (parseFloat(updated.rate) || 0);
          let inclusiveDisc = 0;
          if ((updated.lineDiscountType || 'amount') === 'percent') {
            inclusiveDisc = rawInclusive * ((parseFloat(updated.lineDiscountValue) || 0) / 100);
          } else {
            inclusiveDisc = parseFloat(updated.lineDiscountValue) || 0;
          }
          inclusiveDisc = Math.min(Math.max(inclusiveDisc, 0), rawInclusive);
          const totalAmount = rawInclusive - inclusiveDisc;
          taxableAmount = totalAmount / (1 + gstPercent / 100);
          gstAmount = totalAmount - taxableAmount;
          updated.amount = roundTo2Decimals(taxableAmount);
        } else {
          // Rate excludes GST - calculate GST on taxable amount
          taxableAmount = updated.amount;
          gstAmount = (taxableAmount * gstPercent) / 100;
        }
        
        // Calculate individual GST amounts
        const sgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.sgst) || 0)) / gstPercent : 0;
        const cgstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.cgst) || 0)) / gstPercent : 0;
        const igstAmt = gstPercent > 0 ? (gstAmount * (parseFloat(updated.igst) || 0)) / gstPercent : 0;
        
        updated.total = roundTo2Decimals(taxableAmount + gstAmount);
        return updated;
      });

      // Auto-add trailing row when user fills the last visible row
      const isRowNonEmpty = (r) => {
        return !!(r.item || (parseFloat(r.rate) || 0) || (r.qtyExpr && String(r.qtyExpr).trim()) || (parseFloat(r.qty) || 0) > 0 || (parseFloat(r.nos) || 0) > 1 || (parseFloat(r.length) || 0) || (parseFloat(r.height) || 0) || (parseFloat(r.lineDiscountValue) || 0));
      };
      const last = updatedRows[updatedRows.length - 1];
      if (isRowNonEmpty(last)) {
        updatedRows.push({ ...initialItemRow });
      }
      // Ensure at least 5 rows visible
      while (updatedRows.length < 5) updatedRows.push({ ...initialItemRow });

      return updatedRows;
    });
  };

  const addRow = () => {
    setRows([...rows, { ...initialItemRow }]);
    // Move to the new item in mobile view
    setCurrentMobileItemIndex(rows.length);
  };
  
  // ADD ITEM Modal functions
  const clearAddItemForm = () => {
    setNewItemName('');
    setNewItemMeasurement('');
    setNewItemRate('');
    setNewItemType('Service');
    setNewItemHsnCode('');
    setNewItemGstPercentage('');
    setNewItemDescription('');
    setNewItemPurchasePrice('0');
    setNewItemSalePrice('0');
    setNewItemCurrentStock('0');
    setNewItemIsActive(true);
    setNewItemCustomUnit('');
    setAddItemMessage('');
  };

  const handleAddItem = async () => {
    if (!db || !userId) {
      setAddItemMessage("Firebase not initialized or user not authenticated.");
      return;
    }
    if (!newItemName) {
      setAddItemMessage("Item Name is required.");
      return;
    }
    if (!newItemMeasurement) {
      setAddItemMessage("Quantity Measurement is required.");
      return;
    }

    try {
      const itemsCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'items');
      const itemData = {
        itemName: newItemName,
        quantityMeasurement: newItemCustomUnit || newItemMeasurement,
        defaultRate: parseFloat(newItemRate) || 0,
        itemType: newItemType,
        hsnCode: newItemHsnCode,
        gstPercentage: parseFloat(newItemGstPercentage) || 0,
        description: newItemDescription,
        purchasePrice: parseFloat(newItemPurchasePrice) || 0,
        salePrice: parseFloat(newItemSalePrice) || 0,
        currentStock: parseFloat(newItemCurrentStock) || 0,
        sgstRate: 0,
        cgstRate: 0,
        isActive: newItemIsActive,
        timestamp: serverTimestamp()
      };

      await addDoc(itemsCollectionRef, itemData);
      setAddItemMessage("Item added successfully!");
      clearAddItemForm();
      setShowAddItemModal(false);
    } catch (error) {
      console.error("Error adding item:", error);
      setAddItemMessage("Error adding item. Please try again.");
    }
  };

  const removeRow = idx => {
    setRows(rows.filter((_, i) => i !== idx));
    // Adjust mobile carousel index
    if (currentMobileItemIndex >= rows.length - 1) {
      setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
    }
  };

  // Mobile carousel navigation functions
  const goToPreviousItem = () => {
    setCurrentMobileItemIndex(Math.max(0, currentMobileItemIndex - 1));
  };

  const goToNextItem = () => {
    setCurrentMobileItemIndex(Math.min(rows.length - 1, currentMobileItemIndex + 1));
  };

  const goToItem = (index) => {
    setCurrentMobileItemIndex(index);
  };

  // Touch event handlers for swipe gestures
  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && currentMobileItemIndex < rows.length - 1) {
      goToNextItem();
    }
    if (isRightSwipe && currentMobileItemIndex > 0) {
      goToPreviousItem();
    }
  };

  useEffect(() => {
    setMobileItemPickerOpen(false);
  }, [currentMobileItemIndex]);

  useEffect(() => {
    if (!mobileItemPickerOpen) return;
    const handlePointerDown = (e) => {
      if (mobileItemPickerRef.current && !mobileItemPickerRef.current.contains(e.target)) {
        setMobileItemPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [mobileItemPickerOpen]);

  // ADD PARTY Modal functions
  const clearAddPartyForm = () => {
    setNewPartyName('');
    setNewPartyGstin('');
    setNewPartyAddress('');
    setNewPartyPhone('');
    setNewPartyEmail('');
    setNewPartyGstType('Unregistered');
    setNewPartyOpeningBalance('');
    setNewPartyCreditLimit('');
    setNewPartyCreditPeriod('');
    setShowAdvancedFields(false);
    setAddPartyMessage('');
  };

  const handleAddParty = async () => {
    if (!db || !userId) {
      setAddPartyMessage("Firebase not initialized or user not authenticated.");
      return;
    }

    // Check email verification before allowing party creation
    const { checkActionRequiresVerification } = await import('../utils/emailVerificationCheck');
    const verificationCheck = await checkActionRequiresVerification('add parties', {
      requireEmail: true,
      allowPhone: true,
      allowGoogle: true
    });
    
    if (verificationCheck.needsVerification) {
      const verificationError = new Error(verificationCheck.message);
      verificationError.code = 'VERIFICATION_REQUIRED';
      handleValidationError(verificationError, {
        title: 'Email Verification Required',
        context: 'Add Party',
        severity: 'warning',
        duration: 10000
      });
      showWarning(verificationCheck.message + ' Please verify your account to continue.', {
        title: 'Verification Required',
        duration: 10000
      });
      setAddPartyMessage("Please verify your email address to add parties.");
      return;
    }

    if (!newPartyName.trim()) {
      setAddPartyMessage("Party name is required.");
      return;
    }

    // Validate GSTIN for Regular parties
    if (newPartyGstType === 'Regular' && !newPartyGstin.trim()) {
      setAddPartyMessage("GSTIN is mandatory for Regular parties.");
      return;
    }

    try {
      const partiesCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'parties');
      const partyData = {
        firmName: newPartyName.trim(),
        personName: '',
        address: newPartyAddress.trim(),
        contact: newPartyPhone.trim(),
        email: newPartyEmail.trim(),
        whatsapp: newPartyPhone.trim(),
        gstin: newPartyGstin.trim(),
        gstType: newPartyGstType,
        pan: '',
        city: '',
        stateName: '',
        pincode: '',
        creditPeriod: parseFloat(newPartyCreditPeriod) || 0,
        creditLimit: parseFloat(newPartyCreditLimit) || 0,
        openingBalance: parseFloat(newPartyOpeningBalance) || 0,
        partyType: 'Buyer',
        useManualBalance: true,
        manualBalance: 0,
        manualBalanceDate: new Date().toISOString().split('T')[0],
        manualBalanceRemarks: '',
        createdAt: serverTimestamp(),
        createdBy: userId,
        companyId: companyId
      };

      const docRef = await addDoc(partiesCollectionRef, partyData);
      setAddPartyMessage("Party added successfully!");
      
      // Auto-select the newly added party
      setParty(docRef.id);
      setSelectedPartyForQuickItems(docRef.id);
      setPartySearchText(newPartyName.trim());
      
      clearAddPartyForm();
      setShowAddPartyModal(false);
    } catch (error) {
      console.error("Error adding party:", error);
      setAddPartyMessage("Error adding party. Please try again.");
    }
  };

  // Zoom detection utility
  const getZoomLevel = () => {
    if (typeof window !== 'undefined') {
      return window.devicePixelRatio || 1;
    }
    return 1;
  };

  // Update scrollbar size based on zoom level
  useEffect(() => {
    const updateScrollbarSize = () => {
      const zoomLevel = getZoomLevel();
      const tableContainer = document.querySelector('.custom-scrollbar');
      if (tableContainer) {
        // Add zoom-specific class for CSS targeting
        tableContainer.classList.remove('zoom-1', 'zoom-1-5', 'zoom-2', 'zoom-3');
        if (zoomLevel >= 3) {
          tableContainer.classList.add('zoom-3');
        } else if (zoomLevel >= 2) {
          tableContainer.classList.add('zoom-2');
        } else if (zoomLevel >= 1.5) {
          tableContainer.classList.add('zoom-1-5');
        } else {
          tableContainer.classList.add('zoom-1');
        }
      }
    };

    updateScrollbarSize();
    window.addEventListener('resize', updateScrollbarSize);
    return () => window.removeEventListener('resize', updateScrollbarSize);
  }, []);

  // Calculate modal position based on button click
  const calculateModalPosition = (buttonElement) => {
    if (!buttonElement) {
      return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    }
    
    const rect = buttonElement.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const modalHeight = Math.min(viewportHeight * 0.85, 600); // Estimated modal height
    const modalWidth = Math.min(viewportWidth - 32, 672); // max-w-2xl = 672px, with 16px margin on each side
    
    let top = rect.bottom + 8; // 8px below the button
    let left = rect.left;
    
    // Adjust if modal would go below viewport
    if (top + modalHeight > viewportHeight - 16) {
      top = Math.max(16, rect.top - modalHeight - 8); // 8px above button, min 16px from top
    }
    
    // Adjust if modal would go beyond right edge
    if (left + modalWidth > viewportWidth - 16) {
      left = viewportWidth - modalWidth - 16;
    }
    
    // Adjust if modal would go beyond left edge
    if (left < 16) {
      left = 16;
    }
    
    return {
      top: `${top}px`,
      left: `${left}px`,
      transform: 'none'
    };
  };

  // Quick items helper functions
  const handleQuickItemClick = (itemData, isPartySpecific = false) => {
    // Use itemId consistently - check both itemId and id fields
    const targetItemId = itemData.itemId || itemData.id;
    if (!targetItemId) {
      return;
    }
    
    // Find the item details from items array
    const itemDetails = items.find(item => item.id === targetItemId);
    if (!itemDetails) {
      return;
    }
    
    // Find an empty row or add a new one
    let targetRowIndex = rows.findIndex(row => !row.item && !row.itemId);
    if (targetRowIndex === -1) {
      // Add new row if all rows are filled
      setRows(prev => [...prev, { ...initialItemRow }]);
      targetRowIndex = rows.length;
    }
    
    // Calculate the amount
    const rate = itemData.lastRate || itemDetails.salePrice || itemDetails.rate || 0;
    const qty = itemData.lastQty || 1;
    const amount = rate * qty;
    
    // Update the row with item details using handleRowChange to ensure proper calculations
    // First set the item to trigger GST calculations
    handleRowChange(targetRowIndex, 'item', targetItemId);
    
    // Then set the rate and quantity in sequence to ensure proper calculations
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'rate', rate.toString());
    }, 50);
    
    setTimeout(() => {
      handleRowChange(targetRowIndex, 'qty', qty.toString());
    }, 100);
    
    // Update item search text for this row
    setItemSearchTextByRow(prev => ({
      ...prev,
      [targetRowIndex]: itemDetails.itemName || itemDetails.name || 'Unknown Item'
    }));
  };

  const toggleEwayBill = () => {
    const newValue = !showEwayBill;
    setShowEwayBill(newValue);
    if (typeof window !== 'undefined') {
      localStorage.setItem('showEwayBill', JSON.stringify(newValue));
    }
  };

  const toggleMobileDrawer = () => {
    setIsMobileDrawerOpen(!isMobileDrawerOpen);
  };

  const isMobileView = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  };

  // Mobile-specific invoice modal handler
  const handleMobileInvoiceView = (bill) => {
    console.log('Company data for seal/sign and GST:', {
      company: company,
      currentCompany: currentCompany,
      companySealUrl: company?.sealUrl,
      companySignUrl: company?.signUrl,
      currentCompanySealUrl: currentCompany?.sealUrl,
      currentCompanySignUrl: currentCompany?.signUrl,
      detailsSealUrl: currentCompany?.details?.sealUrl,
      detailsSignUrl: currentCompany?.details?.signUrl,
      brandingSealUrl: currentCompany?.branding?.sealUrl,
      brandingSignUrl: currentCompany?.branding?.signatureUrl,
      finalSealUrl: (currentCompany || company)?.sealUrl || (currentCompany || company)?.details?.sealUrl || (currentCompany || company)?.branding?.sealUrl,
      finalSignUrl: (currentCompany || company)?.signatureUrl || (currentCompany || company)?.signUrl || (currentCompany || company)?.details?.signUrl || (currentCompany || company)?.branding?.signatureUrl,
      companyGstinType: company?.gstinType,
      currentCompanyGstinType: currentCompany?.gstinType,
      companyGstType: company?.gstType,
      currentCompanyGstType: currentCompany?.gstType,
      companyBasicInfoGstType: company?.basicInfo?.gstType,
      currentCompanyBasicInfoGstType: currentCompany?.basicInfo?.gstType,
      finalGstinType: (currentCompany || company)?.gstType || company?.gstType || (currentCompany || company)?.basicInfo?.gstType || company?.basicInfo?.gstType || (currentCompany || company)?.gstinType || company?.gstinType || 'Unregistered',
      finalSignUrl: (currentCompany || company)?.signUrl || company?.signUrl || (currentCompany || company)?.details?.signUrl || company?.details?.signUrl || (currentCompany || company)?.branding?.signatureUrl || company?.branding?.signatureUrl,
      finalSealUrl: (currentCompany || company)?.sealUrl || company?.sealUrl || (currentCompany || company)?.details?.sealUrl || company?.details?.sealUrl || (currentCompany || company)?.branding?.sealUrl || company?.branding?.sealUrl
    });
    setInvoiceBill(bill);
    setShowInvoiceModal(true);
    setInvoiceZoom(getBillPreviewInitialZoom());
  };

  const getResponsiveColumns = () => {
    if (typeof window !== 'undefined') {
      const screenWidth = window.innerWidth;
      if (screenWidth >= 1920) return 3; // Large desktop
      if (screenWidth >= 1366) return 2; // Medium desktop
      return 1; // Small desktop/mobile
    }
    return 1; // Default fallback
  };

  // Filter functions for sales bill list
  const clearFilters = () => {
    setFilterSearch('');
    setFilterDate('');
    setFilterAmount('');
    setFilterBillNo('');
    setFilterParty('');
    setFilterFromDate('');
    setFilterToDate('');
  };

  // Handle filter changes from CompactFilterBar
  const handleFilterChange = (filters) => {
    setFilterSearch(filters.search || '');
    setFilterDate(filters.date || '');
    setFilterAmount(filters.amount || '');
    setFilterBillNo(filters.billNo || '');
    setFilterParty(filters.party || '');
    setFilterFromDate(filters.fromDate || '');
    setFilterToDate(filters.toDate || '');
  };

  // Get the number label for the current document type
  const numberLabel = docTypeOptions.find(opt => opt.value === docType)?.numberLabel || 'Invoice Number';


  // Update bill list and filters to use bills state
  const getFilteredBills = () => {
    const parseBillDate = (value) => {
      if (!value) return null;
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    };

    return bills.filter(bill => {
      const partyName = getPartyName(bill.party);
      const billNumber = String(bill.number || '').trim();
      let billDate = bill.invoiceDate || bill.date || "";
      if (billDate && billDate.includes("-")) {
        const [yyyy, mm, dd] = billDate.split("-");
        billDate = `${dd}/${mm}/${yyyy}`;
      }
      const billAmount = typeof bill.amount === "number" ? bill.amount.toFixed(2) : bill.amount?.toString() || '';
      const billDateObj = parseBillDate(bill.invoiceDate || bill.date || bill.billDate);
      
      // Combined search for bill number and party name
      const searchMatch = filterSearch === '' || (() => {
        const searchTerms = filterSearch.toLowerCase().trim().split(/\s+/);
        const billNumberLower = billNumber.toLowerCase();
        const partyNameLower = partyName.toLowerCase();
        
        // Check if all search terms match either bill number or party name
        return searchTerms.every(term => 
          billNumberLower.includes(term) || partyNameLower.includes(term)
        );
      })();

      const billNoMatch =
        filterBillNo === '' || billNumber.toLowerCase().includes(filterBillNo.toLowerCase());
      const partyMatch =
        filterParty === '' || partyName.toLowerCase().includes(filterParty.toLowerCase());
      const fromDateMatch =
        filterFromDate === '' ||
        (billDateObj && billDateObj >= new Date(`${filterFromDate}T00:00:00`));
      const toDateMatch =
        filterToDate === '' ||
        (billDateObj && billDateObj <= new Date(`${filterToDate}T23:59:59`));
      
      return (
        searchMatch &&
        (filterDate === '' || billDate.includes(filterDate)) &&
        (filterAmount === '' || billAmount.includes(filterAmount)) &&
        billNoMatch &&
        partyMatch &&
        fromDateMatch &&
        toDateMatch
      );
    });
  };

  // Table pagination hook - placed after function definitions
  const sortedBillsBase = getSortedData(getFilteredBills().map((bill, idx) => {
    const partyName = getPartyName(bill.party);
    const outstanding = getBillOutstanding(bill);
    const totalAmount = parseFloat(bill.amount) || 0;
    const paid = totalAmount - outstanding;
    return {
      ...bill,
      partyName,
      paid: Math.max(0, paid),
      outstanding: Math.max(0, outstanding)
    };
  }));
  const currentFyShort = (() => {
    const fy = getFinancialYear(new Date()); // e.g. 2026-27
    const [start, end] = fy.split('-');
    return `${start.slice(-2)}-${end.slice(-2)}`;
  })();
  const getBillFyShort = (bill) => {
    const billNumber = String(bill?.number || bill?.invoiceNumber || '');
    const numberFy = billNumber.match(/(\d{2})\s*-\s*(\d{2})/);
    if (numberFy) return `${numberFy[1]}-${numberFy[2]}`;

    const billDate = bill?.invoiceDate || bill?.date || bill?.billDate;
    if (!billDate) return '';
    const fyFromDate = getFinancialYear(billDate);
    const [start, end] = String(fyFromDate).split('-');
    if (!start || !end) return '';
    return `${start.slice(-2)}-${end.slice(-2)}`;
  };
  const sortedBills = prioritizeCurrentFY
    ? [
        ...sortedBillsBase.filter((bill) => getBillFyShort(bill) === currentFyShort),
        ...sortedBillsBase.filter((bill) => getBillFyShort(bill) !== currentFyShort)
      ]
    : sortedBillsBase;
  const pagination = useTablePagination(sortedBills, 10);

  // GST calculation logic
  const getRowsForCalculation = () => {
    if (company.gstinType !== 'Regular') {
      // For Composition and Unregistered, set all GST to 0
      return rows.map(row => ({ ...row, sgst: 0, cgst: 0, igst: 0 }));
    }
    return rows;
  };

  // With per-line discounts, subtotalBeforeDiscount is sum of line net amounts, and overall discount is derived
  const subtotalBeforeDiscount = getRowsForCalculation().reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0);
  const derivedDiscount = getRowsForCalculation().reduce((sum, row) => {
    const qty = parseFloat(row.qty) || 0;
    const rate = parseFloat(row.rate) || 0;
    const rawAmount = qty * rate;
    let disc = 0;
    if ((row.lineDiscountType || 'amount') === 'percent') {
      disc = rawAmount * ((parseFloat(row.lineDiscountValue) || 0) / 100);
    } else {
      disc = parseFloat(row.lineDiscountValue) || 0;
    }
    disc = Math.min(Math.max(disc, 0), rawAmount);
    return sum + disc;
  }, 0);
  const discount = derivedDiscount; // for display
  const discountedSubtotal = Math.max(0, subtotalBeforeDiscount);
  // No proportional adjustment needed; amounts are already net of line discounts
  const discountRatio = 0;
  const totalSGST = company.gstinType === 'Regular' ? roundTo2Decimals(getRowsForCalculation().reduce((sum, row) => {
    const amt = parseFloat(row.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, row.sgst);
  }, 0)) : 0;
  const totalCGST = company.gstinType === 'Regular' ? roundTo2Decimals(getRowsForCalculation().reduce((sum, row) => {
    const amt = parseFloat(row.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, row.cgst);
  }, 0)) : 0;
  const totalIGST = company.gstinType === 'Regular' ? roundTo2Decimals(getRowsForCalculation().reduce((sum, row) => {
    const amt = parseFloat(row.amount) || 0;
    const effAmt = amt - (amt * discountRatio);
    return sum + calculateGSTAmount(effAmt, row.igst);
  }, 0)) : 0;
  const grandTotalBeforeRoundOff = roundTo2Decimals(discountedSubtotal + totalSGST + totalCGST + totalIGST);
  const grandTotal = roundToNearestInteger(grandTotalBeforeRoundOff);
  const roundOffAmount = roundTo2Decimals(grandTotal - grandTotalBeforeRoundOff);

  // Seller details from company
  const seller = {
    name: company.firmName || '',
    address: `${company.address || ''}${company.city ? ', ' + company.city : ''}${company.pincode ? ' - ' + company.pincode : ''}`,
    contact: company.contactNumber || '',
    gstin: company.gstin || '',
    state: company.state || '',
    jurisdiction: company.city || '',
    bankName: company.bankName || '',
    bankAccount: company.bankAccount || '',
    bankIfsc: company.bankIfsc || '',
    upiId: company.upiId || '',
  };
  // Amount in words utility
  function numToWords(num) {
    // Simple INR words (for demo, can be replaced with a library)
    const a = [ '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen' ];
    const b = [ '', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety' ];
    function inWords(n) {
      if (n < 20) return a[n];
      if (n < 100) return b[Math.floor(n/10)] + (n%10 ? ' ' + a[n%10] : '');
      if (n < 1000) return a[Math.floor(n/100)] + ' Hundred' + (n%100 ? ' and ' + inWords(n%100) : '');
      if (n < 100000) return inWords(Math.floor(n/1000)) + ' Thousand' + (n%1000 ? ' ' + inWords(n%1000) : '');
      if (n < 10000000) return inWords(Math.floor(n/100000)) + ' Lakh' + (n%100000 ? ' ' + inWords(n%100000) : '');
      return inWords(Math.floor(n/10000000)) + ' Crore' + (n%10000000 ? ' ' + inWords(n%10000000) : '');
    }
    return inWords(Math.floor(num)) + ' Rupees Only';
  }

  const subPartyLineRefs = getSubPartyLineRefsForRows(rows);
  const subPartyLineRefCount = subPartyLineRefs.length;

  const openSubPartyModal = () => {
    const cap = subPartyLineRefCount;
    const base =
      subPartyBreakdown.length > 0
        ? subPartyBreakdown.map((r) => ({
            name: r.name || '',
            amount: r.amount !== undefined && r.amount !== null ? String(r.amount) : '',
            lineIndices: sanitizeSubPartyLineIndices(r.lineIndices, cap),
          }))
        : [
            { name: '', lineIndices: [], amount: '' },
            { name: '', lineIndices: [], amount: '' },
          ];
    const withDefaults = base.map((r) => ({
      ...r,
      amount: String(sumLineTotalsForSubPartyIndices(r.lineIndices || [], subPartyLineRefs, company.gstinType)),
    }));
    setSubPartyDraft(
      applySubPartyDraftInvoiceRoundOff(withDefaults, subPartyLineRefs, company.gstinType, grandTotal, grandTotalBeforeRoundOff)
    );
    setShowSubPartyModal(true);
  };

  const updateSubPartyDraftRow = (idx, field, value) => {
    setSubPartyDraft((prev) =>
      prev.map((r, i) =>
        i === idx
          ? {
              ...r,
              [field]: value,
              ...(field === 'amount' ? { _invoiceRoundOffAdj: undefined } : {}),
            }
          : r
      )
    );
  };

  const toggleSubPartyDraftLine = (spIdx, cleanedIdx) => {
    setSubPartyDraft((prev) => {
      const next = prev.map((r) => ({
        ...r,
        lineIndices: Array.isArray(r.lineIndices) ? [...r.lineIndices] : [],
      }));
      const cur = next[spIdx];
      if (!cur) return prev;
      const has = cur.lineIndices.includes(cleanedIdx);
      if (has) {
        cur.lineIndices = cur.lineIndices.filter((i) => i !== cleanedIdx);
      } else {
        next.forEach((r, i) => {
          if (i !== spIdx && Array.isArray(r.lineIndices)) {
            r.lineIndices = r.lineIndices.filter((j) => j !== cleanedIdx);
          }
        });
        cur.lineIndices = [...cur.lineIndices, cleanedIdx].sort((a, b) => a - b);
      }
      return applySubPartyDraftInvoiceRoundOff(next, subPartyLineRefs, company.gstinType, grandTotal, grandTotalBeforeRoundOff);
    });
  };

  /** Recalculate every sub party amount from its selected lines only */
  const fillSubPartyDraftAmountFromLines = () => {
    setSubPartyDraft((prev) => {
      const next = prev.map((r) => ({ ...r, lineIndices: [...(r.lineIndices || [])] }));
      return applySubPartyDraftInvoiceRoundOff(next, subPartyLineRefs, company.gstinType, grandTotal, grandTotalBeforeRoundOff);
    });
  };

  const addSubPartyDraftRow = () => {
    setSubPartyDraft((prev) => [...prev, { name: '', amount: '', lineIndices: [] }]);
  };
  const removeSubPartyDraftRow = (idx) => {
    setSubPartyDraft((prev) => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx)));
  };
  const applySubPartyModal = () => {
    const n = subPartyLineRefCount;
    setSubPartyBreakdown(normalizeSubPartyBreakdownForSave(subPartyDraft, n));
    setShowSubPartyModal(false);
  };
  const subPartyDraftSum = sumSubPartyBreakdownAmounts(
    normalizeSubPartyBreakdownForSave(subPartyDraft, subPartyLineRefCount)
  );
  const subPartyDraftMatchesGrand = Math.abs(subPartyDraftSum - grandTotal) < 0.02;
  const subPartyAppliedSum = sumSubPartyBreakdownAmounts(
    normalizeSubPartyBreakdownForSave(subPartyBreakdown, subPartyLineRefCount)
  );
  const subPartyAppliedMatches = subPartyBreakdown.length === 0 || Math.abs(subPartyAppliedSum - grandTotal) < 0.02;
  
  // Click outside refs for modals
  const successModalRef = useClickOutside(() => setShowSuccessModal(false), showSuccessModal);

  // Update handleSaveInvoice function
  const handleSaveInvoice = async () => {
    if (!db || !userId || !appId) return;
    if (isSavingInvoiceRef.current) return;
    isSavingInvoiceRef.current = true;
    setIsSavingInvoice(true);
    try {
    // Remove empty placeholder rows before validation/save
    const cleanedRows = rows.filter((r) => {
      const hasItem =
        !!(r.item && String(r.item).trim()) || !!(r.itemId && String(r.itemId).trim());
      const hasNumbers = (parseFloat(r.qty) || 0) > 0 || (parseFloat(r.rate) || 0) > 0 || (parseFloat(r.amount) || 0) > 0 || (parseFloat(r.total) || 0) > 0 || (parseFloat(r.length) || 0) > 0 || (parseFloat(r.height) || 0) > 0 || ((parseFloat(r.nos) || 0) > 1);
      return hasItem || hasNumbers;
    });
    if (!party || cleanedRows.length === 0) {
      warning("Please select a party and add at least one item.");
      return;
    }

    // Resolve typed names → item ids; block save if any line still has no item
    const resolvedRows = [];
    const unresolvedLabels = [];
    cleanedRows.forEach((row, idx) => {
      const originalIdx = rows.indexOf(row);
      const searchText =
        (originalIdx >= 0 ? itemSearchTextByRow[originalIdx] : undefined) ??
        itemSearchTextByRow[idx] ??
        '';
      const resolved = resolveRowItemBeforeSave(row, items, searchText);
      if (resolved.ok && getBillLineItemId(resolved.row)) {
        resolvedRows.push(resolved.row);
      } else {
        unresolvedLabels.push(
          searchText || row.itemName || row.name || `Line ${idx + 1} (qty ${row.qty || row.nos || 0})`
        );
      }
    });
    if (unresolvedLabels.length > 0) {
      warning(
        `Please select a valid item for: ${unresolvedLabels.slice(0, 3).join(', ')}${unresolvedLabels.length > 3 ? '…' : ''}. Typed names must match an item from the list.`,
        { title: 'Item not linked' }
      );
      return;
    }
    if (itemsFetchError && items.length === 0) {
      warning('Items failed to load. Refresh the page before saving this bill.', { title: 'Items unavailable' });
      return;
    }
    // Validation logic for duplicate number and date/number order
    const allBillCollections = ['salesBills', 'challans', 'quotations', 'purchaseBills', 'purchaseOrders'];
    let allBills = [];
    for (const coll of allBillCollections) {
      const billsSnap = await getDocs(getCollectionForCompany(db, { companyId, appId, userId }, coll));
      billsSnap.forEach(doc => allBills.push({ ...doc.data(), id: doc.id, collection: coll }));
    }
    // 1. Check for duplicate number
    if (allBills.some(b => b.number === invoiceNumber && (editingBillId ? b.id !== editingBillId : true))) {
      setErrorMessage('Bill number already exists in another document. Please use a unique number.');
      return;
    }
    // 2. Gap/date check: allow if any bill in FY has the same date (match current doc type and FY)
    const fy = getFinancialYear(invoiceDate);
    const fyShort = String(fy)
      .split('-')
      .map(y => String(y).slice(-2))
      .join('-');
    const prefixMap = { 
      invoice: 'INV', 
      challan: 'CHA', 
      quotation: 'QUO',
      salesReturn: 'SR',
      creditNote: 'CN',
      debitNote: 'DN'
    };
    const typePrefix = prefixMap[docType] || 'INV';
    const fyBills = allBills.filter(b => (b.number || '').startsWith(typePrefix + fyShort));
    const thisSerial = parseInt((invoiceNumber || '').split('/')[1], 10);
    const thisDate = new Date(invoiceDate);
    const sameDateExists = fyBills.some(b => {
      if (!b.number || (!b.invoiceDate && !b.billDate)) return false;
      if (b.id === editingBillId) return false;
      const date = new Date(b.invoiceDate || b.billDate);
      return date.getTime() === thisDate.getTime();
    });
    if (!sameDateExists) {
      // Find previous and next serials
      const sortedBills = fyBills
        .filter(b => b.id !== editingBillId && b.number)
        .map(b => ({
          serial: parseInt((b.number || '').split('/')[1], 10),
          date: new Date(b.invoiceDate || b.billDate),
        }))
        .sort((a, b) => a.serial - b.serial);
      let prev = null, next = null;
      for (let i = 0; i < sortedBills.length; i++) {
        if (sortedBills[i].serial < thisSerial) prev = sortedBills[i];
        if (sortedBills[i].serial > thisSerial) { next = sortedBills[i]; break; }
      }
      if (prev && thisDate.getTime() < prev.date.getTime()) {
        setErrorMessage(`Date must be on or after previous bill (${prev.serial}) date: ${prev.date.toLocaleDateString()}`);
        return;
      }
      if (next && thisDate.getTime() > next.date.getTime()) {
        setErrorMessage(`Date must be on or before next bill (${next.serial}) date: ${next.date.toLocaleDateString()}`);
        return;
      }
    }
    // 2. Block if any lower bill number exists with a later date
    for (const b of fyBills) {
      if (!b.number || (!b.invoiceDate && !b.billDate)) continue;
      if (b.id === editingBillId) continue;
      const serial = parseInt((b.number || '').split('/')[1], 10);
      const date = new Date(b.invoiceDate || b.billDate);
      if (serial < thisSerial && date.getTime() > thisDate.getTime()) {
        setErrorMessage('A lower bill number exists with a later date. Please correct the date or number.');
        return;
      }
    }
    // 3. Block if any higher bill number exists with an earlier date
    for (const b of fyBills) {
      if (!b.number || (!b.invoiceDate && !b.billDate)) continue;
      if (b.id === editingBillId) continue;
      const serial = parseInt((b.number || '').split('/')[1], 10);
      const date = new Date(b.invoiceDate || b.billDate);
      if (serial > thisSerial && date.getTime() < thisDate.getTime()) {
        setErrorMessage('A higher bill number exists with an earlier date. Please correct the date or number.');
        return;
      }
    }
    const selected = docTypeOptions.find(opt => opt.value === docType);
    if (!selected) return;
    const collectionRef = getCollectionForCompany(db, { companyId, appId, userId }, selected.collection);
    const stockCollectionRef = getCollectionForCompany(db, { companyId, appId, userId }, 'stock');
    const rowsForSave = resolvedRows.map((row) => enrichBillRowForSave(row, items));
    const normalizedSubParties = normalizeSubPartyBreakdownForSave(
      subPartyBreakdown,
      getSubPartyLineRefsForRows(rows).length
    );
    if (normalizedSubParties.length > 0) {
      const spSum = sumSubPartyBreakdownAmounts(normalizedSubParties);
      if (Math.abs(spSum - grandTotal) > 0.02) {
        alert(
          `Sub party amounts must add up to the invoice Grand Total (${formatCurrency(grandTotal, companyCurrency)}). Current sum: ${formatCurrency(spSum, companyCurrency)}.`
        );
        return;
      }
    }
    const billData = {
      number: invoiceNumber,
      invoiceDate,
      party,
      notes,
      rows: rowsForSave,
      amount: grandTotal,
      totalAmount: grandTotal,
      gstCalculationType,
      roundOffAmount,
      grandTotalBeforeRoundOff,
      createdAt: serverTimestamp(),
      customFields,
      discountType,
      discountValue,
      docType, // Store document type
      subPartyBreakdown: normalizedSubParties,
    };
    if (docType === 'quotation') billData.quotationTermsOverride = quotationTermsOverride;
    if (['invoice', 'challan', 'quotation'].includes(docType)) {
      appendWorkOrderFields(billData, workOrderLink);
    }
    // Add fields for returns and notes
    if (docType === 'salesReturn' || docType === 'creditNote' || docType === 'debitNote') {
      billData.originalBillId = originalBillId;
      billData.originalBillNumber = originalBillNumber;
      if (docType === 'salesReturn') {
        billData.returnReason = returnReason;
        billData.isReturn = true;
      }
    }
      let savedBill;
      if (editingBillId) {
        const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), editingBillId);
        await setDoc(billRef, billData, { merge: true });
        savedBill = { id: editingBillId, ...billData };
      } else {
        // 🚦 This will automatically check subscription limits
        const docRef = await addDoc(collectionRef, billData);
        savedBill = { id: docRef.id, ...billData };
      }

      if (['invoice', 'challan', 'quotation'].includes(docType) && savedBill?.workOrderId) {
        await appendLinkedDocument(db, companyCtx, savedBill.workOrderId, {
          collection: selected.collection,
          docId: savedBill.id,
          docType,
          number: savedBill.number || savedBill.invoiceNumber || '',
        });
      }

      // --- ADVANCE ALLOCATION LOGIC ---
      // Only allocate advance for invoices (not quotations, returns, notes, etc.)
      if (docType === 'invoice') {
        const availableAdvance = getPartyAdvance(party, payments);
        if (availableAdvance > 0) {
          const { allocatedAdvance, advanceAllocations, remainingBillAmount } = allocateAdvanceToBill(party, grandTotal, payments);
          if (allocatedAdvance > 0) {
            // Mark advances as used
            await markAdvanceUsed(advanceAllocations, db, appId, userId, payments, companyId);
            // Optionally, create a payment record for advance allocation
            const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
            await addDoc(paymentsRef, {
              receiptNumber: `ADV-${invoiceNumber}`,
              paymentDate: new Date().toISOString().split('T')[0],
              partyId: party,
              partyName: parties.find(p => p.id === party)?.firmName || '',
              totalAmount: allocatedAdvance,
              paymentMode: 'Advance Allocation',
              reference: 'Auto-advance',
              notes: 'Advance automatically allocated to new invoice',
              type: 'invoice',
              paymentType: 'advance-allocation',
              billId: savedBill.id,
              billNumber: invoiceNumber,
              allocations: [{
                billType: 'invoice',
                billId: savedBill.id,
                billNumber: invoiceNumber,
                allocatedAmount: allocatedAdvance,
                billOutstanding: grandTotal,
                isFullPayment: allocatedAdvance >= grandTotal
              }],
              advanceAllocations,
              createdAt: serverTimestamp(),
              updatedAt: serverTimestamp()
            });
          }
        }
      }

      // Update stock for each sold item
      if (docType === 'invoice') {
        for (const row of resolvedRows) {
          if (!row.item) continue;
          const stockDocRef = doc(stockCollectionRef, row.item);
          const stockDocSnap = await getDoc(stockDocRef);
          let currentStock = 0;
          if (stockDocSnap.exists()) {
            currentStock = stockDocSnap.data().itemQuantity || 0;
          }
          const updatedStock = currentStock - (parseFloat(row.nos) || 0);
          await setDoc(stockDocRef, {
            itemQuantity: updatedStock,
            lastSaleDate: invoiceDate,
          }, { merge: true });
        }
      }
      // For sales returns, add stock back
      if (docType === 'salesReturn') {
        for (const row of resolvedRows) {
          if (!row.item) continue;
          const stockDocRef = doc(stockCollectionRef, row.item);
          const stockDocSnap = await getDoc(stockDocRef);
          let currentStock = 0;
          if (stockDocSnap.exists()) {
            currentStock = stockDocSnap.data().itemQuantity || 0;
          }
          const updatedStock = currentStock + (parseFloat(row.nos) || 0);
          await setDoc(stockDocRef, {
            itemQuantity: updatedStock,
            lastReturnDate: invoiceDate,
          }, { merge: true });
        }
      }
      // Show success notification
      const docTypeLabel = docTypeOptions.find(opt => opt.value === docType)?.label || 'Document';
      const partyName = parties.find(p => p.id === party)?.firmName || 'Unknown Party';
      const action = editingBillId ? 'updated' : 'created';
      
      success(
        `${docTypeLabel} ${invoiceNumber} ${action} successfully!`,
        {
          title: `${docTypeLabel} ${action.charAt(0).toUpperCase() + action.slice(1)}`,
          details: `${invoiceNumber} • ${partyName} • ${formatCurrency(grandTotal, companyCurrency)}`,
          duration: 6000
        }
      );
      
      // Show success modal
                            setSavedBillId(savedBill.id);
                      setShowSuccessModal(true);
                      // Form reset will be handled by useEffect when modal is shown
    } catch (err) {
      // 🚦 Handle subscription limit errors
      if (err.code === 'PLAN_LIMIT_REACHED') {
        console.log('🚫 Plan limit reached:', err.limitCheck);
        setLimitCheckData(err.limitCheck);
        setLimitReached(true);
        return;
      }
      // 🚫 Handle subscription expired errors
      if (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'FEATURE_LIMIT_EXCEEDED') {
        console.log('🚫 Subscription expired or limit exceeded:', err);
        // Error notification is already shown by ErrorNotificationService
        // Just return to prevent further execution
        return;
      }
      error("Error saving invoice: " + err.message, { title: 'Save Failed' });
    } finally {
      isSavingInvoiceRef.current = false;
      setIsSavingInvoice(false);
    }
  };

  // Reset form to initial state
  const resetFormToInitialState = () => {
    setIsResettingForm(true);
    
    // Force a re-render by updating a timestamp
    const timestamp = Date.now();
    setForceUpdate(timestamp);
    
    setInvoiceNumber('');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setParty('');
    setPaymentStatus('Pending');
    setNotes('');
    setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
    setItemSearchTextByRow({});
    setCurrentMobileItemIndex(0);
    setGstCalculationType('excluding'); // Reset to default
    setCustomFields({
      ewayBillNo: '',
      ewayQr: '',
      ewayDate: ''
    });
    setDiscountType('amount');
    setDiscountValue('');
    setQuotationTermsOverride('');
    setSubPartyBreakdown([]);
    setSubPartyDraft([]);
    setEditingBillId(null);
    setErrorMessage('');
    setGstTypes(Array.from({ length: 5 }, () => ''));
    // Reset quick items selection
    setSelectedPartyForQuickItems('');
    // Reset return/note fields
    setOriginalBillId('');
    setOriginalBillNumber('');
    setReturnReason('');
    
    // Calculate next invoice number after reset
    setTimeout(() => {
      const prefixMap = { 
        invoice: 'INV', 
        challan: 'CHA', 
        quotation: 'QUO',
        salesReturn: 'SR',
        creditNote: 'CN',
        debitNote: 'DN'
      };
      const fy = getFinancialYear(new Date().toISOString().split('T')[0]);
      const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
      const prefix = prefixMap[docType] || 'INV';
      const serials = bills
        .filter(bill => (bill.number || '').startsWith(`${prefix}${fyShort}/`))
        .map(bill => parseInt((bill.number || '').split('/')[1], 10))
        .filter(n => !isNaN(n));
              const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
        const nextInvoiceNumber = `${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`;
        setInvoiceNumber(nextInvoiceNumber);
    }, 100);
    
    // Clear loading state after a short delay
    setTimeout(() => {
      setIsResettingForm(false);
    }, 200);
  };

  useCompanySwitchGuard({
    isDirty: () =>
      !!editingBillId ||
      !!party ||
      rows.some(
        (r) =>
          !!(r.item && String(r.item).trim()) ||
          !!(r.itemId && String(r.itemId).trim()) ||
          (parseFloat(r.qty) || 0) > 0
      ),
    onSwitch: () => {
      resetFormToInitialState();
    },
    notify: (message) => warning(message, { title: 'Company switched' }),
  });

  // Add a force update state to ensure re-renders
  const [forceUpdate, setForceUpdate] = useState(0);
  const [isResettingForm, setIsResettingForm] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);



  // useEffect to trigger form reset when success modal is shown
  useEffect(() => {
    if (showSuccessModal) {
      // Small delay to ensure modal is fully rendered before resetting
      const timer = setTimeout(() => {
        resetFormToInitialState();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  // Handle window resize for mobile responsiveness
  useEffect(() => {
    const handleResize = () => {
      // Force re-render when screen size changes
      setForceUpdate(Date.now());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Save showFilters state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sales_showFilters', JSON.stringify(showFilters));
    }
  }, [showFilters]);

  // Save current-FY-priority state to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('sales_prioritizeCurrentFY', JSON.stringify(prioritizeCurrentFY));
    }
  }, [prioritizeCurrentFY]);

  // Modal and action state for View/Edit/Delete
  const [viewBill, setViewBill] = useState(null); // Bill to view in modal
  const [showViewModal, setShowViewModal] = useState(false);
  const [deletingBillId, setDeletingBillId] = useState(null); // Bill id being deleted
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Add import for BillTemplates and modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceBill, setInvoiceBill] = useState(null);

  // Warm bill image network + data-URL cache when preview opens so Copy image / PDF capture is faster.
  useEffect(() => {
    if (!showInvoiceModal || !invoiceBill) return;
    const companyDetails = {
      logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
      upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
      sealUrl: resolveCompanySealUrl(currentCompany, company),
      signUrl: resolveCompanySignUrl(currentCompany, company)
    };
    const bankDetails = {
      sealUrl: resolveCompanySealUrl(currentCompany, company),
      signUrl: resolveCompanySignUrl(currentCompany, company)
    };
    void preloadBillImages(companyDetails, bankDetails);
    const urls = [...new Set([
      companyDetails.logoUrl, companyDetails.upiQrUrl, companyDetails.sealUrl, companyDetails.signUrl,
      bankDetails.sealUrl, bankDetails.signUrl
    ].filter(Boolean))];
    void Promise.all(urls.map((u) => fetchImageAsDataUrlForPrint(u)));
  }, [showInvoiceModal, invoiceBill?.id, currentCompany?.companyId]);

  // ESC Key Functionality: Press ESC key to close modals in LIFO order (Last In, First Out)
  // Order: Service Editor -> Add Party Modal -> Sub Party Modal -> Receipt Modal -> Payment Details Modal -> Payment Modal -> Invoice Modal -> View Modal -> Success Modal -> Add Item Modal
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        if (showServiceEditorModal) {
          closeServiceEditor();
        } else if (showAddPartyModal) {
          setShowAddPartyModal(false);
          clearAddPartyForm();
        } else if (showSubPartyModal) {
          setShowSubPartyModal(false);
        } else if (showReceiptModal) {
          setShowReceiptModal(false);
        } else if (showPaymentDetailsModal) {
          setShowPaymentDetailsModal(false);
        } else if (showPaymentModal) {
          setShowPaymentModal(false);
          setSelectedBillForPayment(null);
        } else if (showFIFOPaymentModal) {
          setShowFIFOPaymentModal(false);
          setSelectedPartyForFIFO('');
          setFifoPaymentAmount('');
          setShowPaymentModal(false);
        } else if (showInvoiceModal) {
          setShowInvoiceModal(false);
        } else if (showViewModal) {
          setShowViewModal(false);
        } else if (showSuccessModal) {
          setShowSuccessModal(false);
        } else if (showAddItemModal) {
          setShowAddItemModal(false);
          clearAddItemForm();
        }
      }
    };
    
    // Add event listener if any modal is open
    if (showServiceEditorModal || showAddPartyModal || showSubPartyModal || showReceiptModal || showPaymentDetailsModal || showPaymentModal || showFIFOPaymentModal || showInvoiceModal || showViewModal || showSuccessModal || showAddItemModal) {
      document.addEventListener('keydown', handleEscKey);
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showServiceEditorModal, showAddPartyModal, showSubPartyModal, showReceiptModal, showPaymentDetailsModal, showPaymentModal, showFIFOPaymentModal, showInvoiceModal, showViewModal, showSuccessModal, showAddItemModal]);

  useEffect(() => {
    if (!showServiceEditorModal) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [showServiceEditorModal]);

  // Handler to view bill details
  const handleViewBill = (bill) => {
    setViewBill(bill);
    setShowViewModal(true);
  };

  // Toggle function for dropdown form rows
  const toggleRowExpansion = (index) => {
    setExpandedRows(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  // Handler to edit bill (load into form)
  const handleEditBill = (bill) => {
    setEditingBillId(bill.id);
    setInvoiceNumber(bill.number || '');
    setInvoiceDate(bill.invoiceDate || '');
    setParty(bill.party || '');
    setNotes(bill.notes || '');
    setWorkOrderLink({
      workOrderId: bill.workOrderId || '',
      phaseId: bill.phaseId || '',
      costCategory: bill.costCategory || '',
    });
    // Always use GST values from bill.rows when editing, but merge with initialItemRow to ensure all properties exist
    const loadedBillRows = (bill.rows && bill.rows.length > 0)
      ? bill.rows.map(row => ({ ...initialItemRow, ...row, qtyExpr: row.qtyExpr || '', qty: row.qty || 0, rate: row.rate || 0, amount: row.amount || 0, sgst: row.sgst || 0, cgst: row.cgst || 0, igst: row.igst || 0, total: row.total || 0, lineDiscountValue: row.lineDiscountValue || 0, lineDiscountType: row.lineDiscountType || 'amount', tags: row.tags || [], services: (Array.isArray(row.services) ? row.services : []).map(normalizeServiceEntry) }))
      : [{ ...initialItemRow }];
    // Pad with blank rows so the grid matches new-bill layout and has room for additions
    loadedBillRows.push({ ...initialItemRow });
    while (loadedBillRows.length < 5) loadedBillRows.push({ ...initialItemRow });
    setRows(loadedBillRows);
    setItemSearchTextByRow({});
    // Load GST calculation type from bill data
    setGstCalculationType(bill.gstCalculationType || 'excluding');
    if (Array.isArray(bill.subPartyBreakdown) && bill.subPartyBreakdown.length > 0) {
      setSubPartyBreakdown(
        bill.subPartyBreakdown.map((r) => ({
          name: r.name || '',
          amount: r.amount !== undefined && r.amount !== null ? String(r.amount) : '',
          lineIndices: Array.isArray(r.lineIndices) ? [...r.lineIndices] : [],
        }))
      );
    } else {
      setSubPartyBreakdown([]);
    }
    // Update quick items selection to match the party being edited
    setSelectedPartyForQuickItems(bill.party || '');
    
    // Initialize itemSearchTextByRow for mobile view
    const newItemSearchText = {};
    if (bill.rows && bill.rows.length > 0) {
      bill.rows.forEach((row, index) => {
        if (row.item) {
          const item = findItemInMaster(row.item, items);
          if (item) {
            newItemSearchText[index] = getItemDisplayName(item);
          }
        }
      });
    }
    setItemSearchTextByRow(newItemSearchText);
    
    // Reset mobile carousel to first item
    setCurrentMobileItemIndex(0);
    
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (bill.quotationTermsOverride !== undefined) setQuotationTermsOverride(bill.quotationTermsOverride);
    else setQuotationTermsOverride('');
    // Load return/note fields if applicable
    if (bill.docType === 'salesReturn' || bill.docType === 'creditNote' || bill.docType === 'debitNote') {
      setOriginalBillId(bill.originalBillId || '');
      setOriginalBillNumber(bill.originalBillNumber || '');
      if (bill.docType === 'salesReturn') {
        setReturnReason(bill.returnReason || '');
      }
    }
  };
  
  // Handler to convert quotation to invoice
  const handleConvertToInvoice = (quotation) => {
    try {
      // Step 1: Switch docType to invoice
      setDocType('invoice');
      
      // Step 2: Generate new invoice number
      const fy = getFinancialYear(new Date().toISOString().split('T')[0]);
      const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
      const prefix = 'INV';
      const invoiceSerials = bills
        .filter(bill => (bill.number || '').startsWith(prefix + fyShort))
        .map(bill => parseInt((bill.number || '').split('/')[1], 10))
        .filter(n => !isNaN(n));
      const nextSerial = (invoiceSerials.length ? Math.max(...invoiceSerials) : 0) + 1;
      const newInvoiceNumber = `${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`;
      setInvoiceNumber(newInvoiceNumber);
      
      // Step 3: Set invoice date to today
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      
      // Step 4: Populate form with quotation data
      setParty(quotation.party || '');
      setNotes(quotation.notes || '');
      const loadedQuotationRows = (quotation.rows && quotation.rows.length > 0)
        ? quotation.rows.map(row => ({ ...initialItemRow, ...row, qtyExpr: row.qtyExpr || '', qty: row.qty || 0, rate: row.rate || 0, amount: row.amount || 0, sgst: row.sgst || 0, cgst: row.cgst || 0, igst: row.igst || 0, total: row.total || 0, lineDiscountValue: row.lineDiscountValue || 0, lineDiscountType: row.lineDiscountType || 'amount', tags: row.tags || [], services: (Array.isArray(row.services) ? row.services : []).map(normalizeServiceEntry) }))
        : [{ ...initialItemRow }];
      // Pad with blank rows so the grid matches new-bill layout and has room for additions
      loadedQuotationRows.push({ ...initialItemRow });
      while (loadedQuotationRows.length < 5) loadedQuotationRows.push({ ...initialItemRow });
      setRows(loadedQuotationRows);
      if (Array.isArray(quotation.subPartyBreakdown) && quotation.subPartyBreakdown.length > 0) {
        setSubPartyBreakdown(
          quotation.subPartyBreakdown.map((r) => ({
            name: r.name || '',
            amount: r.amount !== undefined && r.amount !== null ? String(r.amount) : '',
            lineIndices: Array.isArray(r.lineIndices) ? [...r.lineIndices] : [],
          }))
        );
      } else {
        setSubPartyBreakdown([]);
      }
      setGstCalculationType(quotation.gstCalculationType || 'excluding');
      setDiscountType(quotation.discountType || 'amount');
      setDiscountValue(quotation.discountValue || '');
      setCustomFields(quotation.customFields || { ewayBillNo: '', ewayDate: '', ewayQr: '' });
      
      // Initialize itemSearchTextByRow for mobile view
      const newItemSearchText = {};
      if (quotation.rows && quotation.rows.length > 0) {
        quotation.rows.forEach((row, index) => {
          if (row.item) {
            const item = findItemInMaster(row.item, items);
            if (item) {
              newItemSearchText[index] = getItemDisplayName(item);
            }
          }
        });
      }
      setItemSearchTextByRow(newItemSearchText);
      
      // Update quick items selection
      setSelectedPartyForQuickItems(quotation.party || '');
      
      // Reset mobile carousel to first item
      setCurrentMobileItemIndex(0);
      
      // Clear editing state (this is a new invoice, not editing)
      setEditingBillId(null);
      
      // Clear quotation terms (invoices don't have quotation terms)
      setQuotationTermsOverride('');
      
      // Step 5: Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Step 6: Show success notification
      success(`Quotation ${quotation.number || ''} converted to invoice. Review and save.`, {
        title: 'Conversion Successful',
        duration: 4000
      });
    } catch (err) {
      console.error('Error converting quotation to invoice:', err);
      error('Error converting quotation to invoice: ' + err.message, { title: 'Conversion Failed' });
    }
  };
  
  // Handler to delete bill
  const handleDeleteBill = (billId) => {
    setDeletingBillId(billId);
    setShowDeleteConfirm(true);
  };
  // Confirm delete
  const confirmDeleteBill = async () => {
    if (!db || !userId || !appId || !deletingBillId) return;
    try {
      const selected = docTypeOptions.find(opt => opt.value === docType);
      if (!selected) return;
      const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), deletingBillId);
      await deleteDoc(billRef);
      setShowDeleteConfirm(false);
      setDeletingBillId(null);
    } catch (err) {
      error('Error deleting bill: ' + err.message, { title: 'Delete Failed' });
    }
  };
  // Cancel delete
  const cancelDeleteBill = () => {
    setShowDeleteConfirm(false);
    setDeletingBillId(null);
  };

  // Function to fetch original bill by number and auto-populate form
  const fetchOriginalBill = async (billNumber) => {
    if (!billNumber || !billNumber.trim() || !db || !userId || !appId || !companyId) {
      return;
    }

    try {
      // Search in salesBills collection (for invoices, challans, quotations)
      const salesBillsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'salesBills');
      if (!salesBillsRef) return;

      const salesBillsQuery = query(salesBillsRef, where('number', '==', billNumber.trim()));
      const salesBillsSnapshot = await getDocs(salesBillsQuery);
      
      let originalBill = null;
      
      if (!salesBillsSnapshot.empty) {
        const doc = salesBillsSnapshot.docs[0];
        originalBill = { id: doc.id, ...doc.data() };
      } else {
        // Also check challans and quotations
        const challansRef = getCollectionForCompany(db, { companyId, appId, userId }, 'challans');
        if (challansRef) {
          const challansQuery = query(challansRef, where('number', '==', billNumber.trim()));
          const challansSnapshot = await getDocs(challansQuery);
          if (!challansSnapshot.empty) {
            const doc = challansSnapshot.docs[0];
            originalBill = { id: doc.id, ...doc.data() };
          }
        }

        if (!originalBill) {
          const quotationsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'quotations');
          if (quotationsRef) {
            const quotationsQuery = query(quotationsRef, where('number', '==', billNumber.trim()));
            const quotationsSnapshot = await getDocs(quotationsQuery);
            if (!quotationsSnapshot.empty) {
              const doc = quotationsSnapshot.docs[0];
              originalBill = { id: doc.id, ...doc.data() };
            }
          }
        }
      }

      if (originalBill) {
        // Set original bill ID and number
        setOriginalBillId(originalBill.id);
        setOriginalBillNumber(billNumber.trim());
        
        // Auto-populate party
        if (originalBill.party || originalBill.partyId) {
          setParty(originalBill.party || originalBill.partyId);
        }

        // Auto-populate items/rows
        const billRows = originalBill.rows || originalBill.items || [];
        if (billRows.length > 0) {
          const newRows = billRows.map(row => {
            // Get item details to get default unit/measurement
            const itemObj = findItemInMaster(row.item, items);
            const defaultUnit = itemObj ? (itemObj.quantityMeasurement || itemObj.unit || '') : '';
            
            return {
              item: row.item || '',
              // Preserve all measurement fields
              nos: row.nos || 1,
              length: row.length || 0,
              height: row.height || 0,
              qty: row.qty || row.quantity || 0,
              qtyExpr: row.qtyExpr || '',
              qtyDisplay: row.qtyDisplay || '',
              unit: row.unit || row.measurement || defaultUnit || '',
              quantityMeasurement: row.quantityMeasurement || row.unit || row.measurement || defaultUnit || '',
              rate: row.rate || row.price || 0,
              discount: row.discount || '',
              discountType: row.discountType || 'percentage',
              lineDiscountType: row.lineDiscountType || 'amount',
              lineDiscountValue: row.lineDiscountValue || 0,
              amount: row.amount || 0,
              gstRate: row.gstRate || row.gst || 0,
              sgst: row.sgst || 0,
              cgst: row.cgst || 0,
              igst: row.igst || 0,
              total: row.total || 0,
              hsn: row.hsn || row.hsnCode || '',
              description: row.description || '',
              // Store original quantity for partial return reference
              originalQty: row.qty || row.quantity || 0,
              originalQtyExpr: row.qtyExpr || '',
              originalQtyDisplay: row.qtyDisplay || ''
            };
          });

          // Fill remaining rows with empty data
          const totalRows = Math.max(newRows.length, 5);
          const filledRows = Array.from({ length: totalRows }, (_, i) => 
            newRows[i] || { ...initialItemRow }
          );
          setRows(filledRows);

          // Update item search text for each row
          const newItemSearchText = {};
          filledRows.forEach((row, index) => {
            if (row.item) {
              const item = findItemInMaster(row.item, items);
              if (item) {
                newItemSearchText[index] = getItemDisplayName(item);
              }
            }
          });
          setItemSearchTextByRow(newItemSearchText);
        }

        // Auto-populate date
        if (originalBill.invoiceDate || originalBill.date) {
          setInvoiceDate(originalBill.invoiceDate || originalBill.date);
        }

        // Auto-populate notes if any
        if (originalBill.notes) {
          setNotes(originalBill.notes);
        }

        success(`Original bill "${billNumber}" loaded successfully!`);
      } else {
        warning(`Bill number "${billNumber}" not found. Please check the number and try again.`);
      }
    } catch (err) {
      console.error('Error fetching original bill:', err);
      error('Error fetching original bill: ' + err.message);
    }
  };

  // Handle original bill number change with debounce
  const originalBillSearchTimeoutRef = useRef(null);
  const handleOriginalBillNumberChange = (value) => {
    setOriginalBillNumber(value);
    
    // Clear previous timeout
    if (originalBillSearchTimeoutRef.current) {
      clearTimeout(originalBillSearchTimeoutRef.current);
    }

    // Only search if value is not empty and has at least 3 characters
    if (value && value.trim().length >= 3) {
      originalBillSearchTimeoutRef.current = setTimeout(() => {
        fetchOriginalBill(value.trim());
      }, 1000); // Wait 1 second after user stops typing
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (originalBillSearchTimeoutRef.current) {
        clearTimeout(originalBillSearchTimeoutRef.current);
      }
    };
  }, []);

  // Handle individual selection for bulk deletion
  const handleDeleteSelection = (billId) => {
    const newSelected = new Set(selectedForDeletion);
    if (newSelected.has(billId)) {
      newSelected.delete(billId);
    } else {
      newSelected.add(billId);
    }
    setSelectedForDeletion(newSelected);
  };

  // Handle select all for bulk deletion
  const handleSelectAllForDeletion = () => {
    if (selectedForDeletion.size === bills.length) {
      setSelectedForDeletion(new Set());
    } else {
      setSelectedForDeletion(new Set(bills.map(bill => bill.id)));
    }
  };

  // Bulk delete function
  const handleBulkDelete = async () => {
    if (selectedForDeletion.size === 0) {
      warning('Please select invoices to delete');
      return;
    }

    setIsBulkDeleting(true);
    try {
      const selected = docTypeOptions.find(opt => opt.value === docType);
      if (!selected) return;

      const deletePromises = Array.from(selectedForDeletion).map(billId => {
        const billRef = doc(getCollectionForCompany(db, { companyId, appId, userId }, selected.collection), billId);
        return deleteDoc(billRef);
      });

      await Promise.all(deletePromises);
      
      setSelectedForDeletion(new Set());
      setShowBulkDeleteConfirm(false);
      
      success(`Successfully deleted ${selectedForDeletion.size} invoice(s)`, { title: 'Deleted Successfully' });
    } catch (error) {
      console.error('Bulk delete error:', error);
      error('Failed to delete invoices: ' + error.message, { title: 'Delete Failed' });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  // Payment handling functions
  const handleAddPayment = async (bill) => {
    setPaymentAmount('');
    setPaymentDate(new Date().toISOString().split('T')[0]);
    setPaymentMode('Cash');
    setPaymentReference('');
    setPaymentNotes('');
    setReceiptNumber('');
    setRecordInEmployeeCashFlow(false);
    setSelectedEmployeeForCashFlow('');
    
    // Generate receipt number
    const receiptNum = await generateReceiptNumber();
    setReceiptNumber(receiptNum);
    
    // Pre-fill with bill details
    setPaymentAmount(getBillOutstanding(bill).toString());
    
    // Store the bill information for payment
    setSelectedBillForPayment(bill);
    setShowPaymentModal(true);
  };

  // Quick employee cash flow recording
  const handleQuickEmployeeCashFlow = async (bill, employee, paymentMode = 'cash', customAmount = null) => {
    if (!bill || !employee || !currentCompany) {
      warning('Missing information for cash flow recording');
      return;
    }

    const outstandingAmount = getBillOutstanding(bill);
    // Parse outstanding amount - handle both string (formatted) and number
    const outstandingAmountNum = typeof outstandingAmount === 'string' 
      ? parseFloat(outstandingAmount.replace(/[₹,]/g, '')) 
      : parseFloat(outstandingAmount);
    
    // Use custom amount if provided, otherwise use outstanding amount
    let paymentAmountNum = customAmount !== null && customAmount !== undefined && customAmount !== ''
      ? parseFloat(String(customAmount).replace(/[₹,]/g, ''))
      : outstandingAmountNum;
    
    if (isNaN(paymentAmountNum) || paymentAmountNum <= 0) {
      warning('Invalid payment amount. Please enter a valid amount.');
      return;
    }
    
    // Validate that custom amount doesn't exceed outstanding
    if (paymentAmountNum > outstandingAmountNum) {
      error(`Payment amount (${formatCurrency(paymentAmountNum, companyCurrency)}) cannot exceed the bill's outstanding amount (${formatCurrency(outstandingAmountNum, companyCurrency)}).`);
      return;
    }

    try {
      const companyData = {
        companyId: currentCompany.companyId,
        userId: currentCompany.userId,
        appId: currentCompany.appId
      };

      // Use the selected payment mode from dropdown (cash or upi)
      const paymentModeValue = paymentMode === 'upi' ? 'UPI' : 'Cash';
      
      // Generate receipt number
      const receiptNum = await generateReceiptNumber();
      const paymentDateValue = new Date().toISOString().split('T')[0];
      
      // First, try to allocate available advance
      const availableAdvance = getPartyAdvance(bill.party, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      let remainingPaymentAmount = paymentAmountNum;
      
      if (availableAdvance > 0) {
        // Allocate advance to this bill (use paymentAmountNum for allocation calculation)
        const advanceResult = allocateAdvanceToBill(bill.party, paymentAmountNum, payments);
        if (advanceResult.allocatedAdvance > 0) {
          advanceAllocations = advanceResult.advanceAllocations;
          advanceUsed = advanceResult.allocatedAdvance;
          remainingPaymentAmount = paymentAmountNum - advanceUsed;
          
          // Mark advances as used
          if (Array.isArray(advanceResult.advanceAllocations) && advanceResult.advanceAllocations.length > 0) {
            await markAdvanceUsed(advanceResult.advanceAllocations, db, appId, userId, payments, companyId);
          }
        }
      }
      
      // Create payment record
      const paymentData = {
        receiptNumber: receiptNum,
        paymentDate: paymentDateValue,
        partyId: bill.party,
        partyName: getPartyName(bill.party),
        totalAmount: paymentAmountNum,
        paymentMode: paymentModeValue,
        reference: `Employee: ${employee.name}`,
        notes: '',
        type: docType,
        paymentType: 'bill',
        billId: bill.id,
        billNumber: bill.number || bill.invoiceNumber,
        allocations: [{
          billType: docType,
          billId: bill.id,
          billNumber: bill.number || bill.invoiceNumber,
          allocatedAmount: paymentAmountNum,
          billOutstanding: outstandingAmountNum,
          isFullPayment: paymentAmountNum >= outstandingAmountNum
        }],
        remainingAmount: 0,
        fifoAllocationUsed: 0,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add to payments collection
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      let paymentDocRef = null;
      
      try {
        paymentDocRef = await addDoc(paymentsRef, paymentData);
      } catch (paymentError) {
        console.error('Error creating payment record:', paymentError);
        error('Failed to create payment record. Please try again.');
        return;
      }
      
      // For sales bills, this is an inflow (money received)
      // Note: No balance validation needed for inflows - employees can always receive money
      const transactionData = {
        employeeId: employee.id,
        employeeName: employee.name || '',
        transactionType: 'inflow',
        amount: paymentAmountNum, // Use the actual payment amount
        paymentMode: paymentMode,
        description: `Payment received for ${bill.number || bill.invoiceNumber || 'Bill'} - ${bill.partyName || 'Party'}`,
        referenceNumber: bill.number || bill.invoiceNumber || '',
        date: paymentDateValue,
        category: 'Collection',
        createdBy: {
          userId: userId || currentCompany?.userId || '',
          name: currentCompany?.userName || 'User'
        },
        linkedPaymentId: paymentDocRef.id,
        linkedReceiptNumber: receiptNum,
        ...(paymentMode === 'cash' && {
          cashReceivedFrom: bill.partyName || 'Customer',
          cashVerified: true
        }),
        ...(paymentMode === 'upi' && {
          upiId: employee.paymentPreferences?.upiId || '',
          transactionId: `QUICK-${Date.now()}`,
          upiApp: employee.paymentPreferences?.upiApp || 'Other'
        })
      };

      const result = await EmployeeCashManager.createCashTransaction(companyData, transactionData);

      if (result.success) {
        // Verify balance was actually updated
        if (result.balanceUpdateSuccess === false) {
          warning('Payment recorded but balance update had issues. Please verify employee balance.', {
            title: 'Balance Update Warning',
            duration: 6000
          });
        }
        
        success(`₹${paymentAmountNum.toLocaleString('en-IN')} payment recorded and added to ${employee.name}'s cash flow`, {
          title: 'Payment & Cash Flow Recorded',
          details: `Receipt #${receiptNum} • ${employee.name} • Balance: ₹${result.newBalance?.total?.toLocaleString('en-IN') || 'N/A'}`,
          duration: 5000
        });
      } else {
        // Rollback: Delete payment record if employee transaction failed
        if (paymentDocRef) {
          try {
            await deleteDoc(paymentDocRef);
            console.log('Payment record rolled back due to employee transaction failure');
          } catch (rollbackError) {
            console.error('Error rolling back payment record:', rollbackError);
          }
        }
        error(result.error || 'Failed to record cash flow. Payment was not processed.', {
          title: 'Transaction Failed',
          duration: 6000
        });
      }
    } catch (err) {
      console.error('Error recording quick cash flow:', err);
      error('Failed to record cash flow: ' + err.message);
    }
  };

  // Get bill payments for receipt preview
  const getBillPayments = (bill) => {
    return payments.filter(payment => {
      // Check if payment is for the same party
      const paymentPartyId = payment.partyId || payment.party;
      if (paymentPartyId !== bill.party) {
        return false;
      }
      
      // Special handling for advance receipts
      if (payment.receiptNumber && payment.receiptNumber.startsWith('ADV-')) {
        // Extract invoice number from advance receipt (e.g., ADV-INV25-26/12 -> INV25-26/12)
        const targetInvoiceNo = payment.receiptNumber.replace('ADV-', '');
        const currentInvoiceNo = bill.number || bill.invoiceNumber || bill.id;
        return targetInvoiceNo === currentInvoiceNo;
      }
      
      // Check if payment has allocations for this invoice
      if (payment.allocations && Array.isArray(payment.allocations)) {
        return payment.allocations.some(allocation => 
          allocation.billId === bill.id && allocation.billType === docType
        );
      }
      
      // Check if payment has advance allocations for this invoice
      if (payment.advanceAllocations && Array.isArray(payment.advanceAllocations)) {
        return payment.advanceAllocations.some(allocation => 
          allocation.billId === bill.id
        );
      }
      
      // Check direct billId match
      if (payment.billId === bill.id) {
        return true;
      }
      
      return false;
    });
  };

  // Handle payment receipt preview
  const handlePreviewReceipt = (payment) => {
    setSelectedPaymentForReceipt(payment);
    setShowReceiptModal(true);
  };

  // Handle payment receipt preview from bill
  const handleViewPaymentReceipts = (bill) => {
    const billPayments = getBillPayments(bill);
    if (billPayments.length > 0) {
      setSelectedBillForReceipts(bill);
      setShowPaymentDetailsModal(true);
    }
  };

  // Image management functions
  const handleImageSelect = (image) => {
    setSelectedImages(prev => ({
      ...prev,
      [image.category]: image.data
    }));
  };

  const loadSavedImages = async () => {
    try {
      const logoImages = await imageManager.getImagesByCategory('logo', userId);
      const sealImages = await imageManager.getImagesByCategory('seal', userId);
      const signatureImages = await imageManager.getImagesByCategory('signature', userId);
      const qrImages = await imageManager.getImagesByCategory('qr', userId);

      setSelectedImages({
        logo: logoImages.length > 0 ? logoImages[0].data : null,
        seal: sealImages.length > 0 ? sealImages[0].data : null,
        signature: signatureImages.length > 0 ? signatureImages[0].data : null,
        qr: qrImages.length > 0 ? qrImages[0].data : null
      });
    } catch (error) {
      console.error('Error loading saved images:', error);
    }
  };

  // Load saved images on component mount
  useEffect(() => {
    if (userId) {
      loadSavedImages();
    }
  }, [userId]);

  // Print receipt
  const printReceipt = () => {
    if (!selectedPaymentForReceipt) {
      warning('No receipt selected for printing');
      return;
    }

    if (!receiptRef.current) {
      error('Receipt content not available');
      return;
    }

    const printContents = receiptRef.current.innerHTML;
    const printWindow = window.open('', '_blank', 'height=800,width=1000');
    
    if (!printWindow) {
      info('Please allow popups to print the receipt');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Payment Receipt</title>
          <meta charset="utf-8">
          <script src="https://cdn.tailwindcss.com"></script>
          <style>
            @media print {
              body { margin: 0; padding: 0; }
              .print-content { 
                width: 210mm; 
                height: 297mm; 
                margin: 0 auto; 
                background: white; 
                padding: 20mm;
                box-sizing: border-box;
              }
              .no-print { display: none !important; }
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              background: #f5f5f5; 
            }
            .print-content { 
              background: white; 
              padding: 40px; 
              border-radius: 8px; 
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              max-width: 800px;
              margin: 0 auto;
            }
            .receipt-header { text-align: center; margin-bottom: 30px; }
            .receipt-title { font-size: 28px; font-weight: bold; color: #1f2937; margin-bottom: 10px; }
            .receipt-number { font-size: 18px; color: #2563eb; font-family: monospace; }
            .company-info { text-align: center; margin-bottom: 30px; padding-bottom: 20px; border-bottom: 2px solid #d1d5db; }
            .company-name { font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 5px; }
            .company-details { font-size: 14px; color: #6b7280; }
            .receipt-details { margin-bottom: 30px; }
            .detail-row { display: flex; justify-content: space-between; margin-bottom: 15px; }
            .detail-label { font-weight: 600; color: #374151; }
            .detail-value { font-size: 16px; }
            .amount { font-size: 24px; font-weight: bold; color: #16a34a; }
            .allocation-section { margin-top: 30px; }
            .allocation-title { font-weight: 600; color: #2563eb; margin-bottom: 15px; }
            .allocation-item { border: 1px solid #e5e7eb; border-radius: 4px; padding: 15px; margin-bottom: 10px; background: #f9fafb; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #d1d5db; display: flex; justify-content: space-between; align-items: flex-end; }
            .footer-left { font-size: 12px; color: #6b7280; }
            .signature { text-align: center; }
            .signature-line { border-top: 2px solid #9ca3af; width: 120px; margin: 0 auto 10px; }
            .signature-text { font-weight: 600; color: #374151; }
          </style>
        </head>
        <body>
          <div class="print-content">
            ${printContents}
          </div>
          <div class="no-print" style="text-align: center; margin-top: 20px;">
            <button onclick="window.print()" style="background: #2563eb; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer; margin-right: 10px;">Print</button>
            <button onclick="window.close()" style="background: #6b7280; color: white; border: none; padding: 10px 20px; border-radius: 4px; cursor: pointer;">Close</button>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    
    // Wait for content to load before printing
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
      }, 500);
    };
  };

  // Generate PDF receipt
  const generatePDFReceipt = async () => {
    if (!selectedPaymentForReceipt) {
      warning('No receipt selected for PDF generation');
      return;
    }
    
    // Ensure jsPDF is available (try global first, then dynamic import)
    let jsPDF;
    if (window.jspdf && window.jspdf.jsPDF) {
      jsPDF = window.jspdf.jsPDF;
    } else if (window.jsPDF) {
      jsPDF = window.jsPDF;
    } else {
      try {
        const mod = await import('jspdf');
        jsPDF = (mod && (mod.jsPDF || mod.default)) || undefined;
      } catch (e) {
        console.error('Failed to load jsPDF:', e);
      }
    }
    if (!jsPDF) {
      error('PDF generation library not available. Please refresh the page and try again.');
      return;
    }
    
    const { jsPDF: _unused } = { jsPDF };
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      let yPosition = margin;
    
    // Helper function to get company name safely
    const getCompanyName = () => {
      if (company?.firmName) return company.firmName;
      if (company?.name) return company.name;
      if (company?.companyName) return company.companyName;
      return 'Company Name';
    };
    
    // Helper function to get party name safely
    const getPartyName = () => {
      if (selectedPaymentForReceipt?.partyName) return selectedPaymentForReceipt.partyName;
      if (selectedPaymentForReceipt?.party?.firmName) return selectedPaymentForReceipt.party.firmName;
      if (selectedPaymentForReceipt?.party?.name) return selectedPaymentForReceipt.party.name;
      return 'Party Name';
    };
    
    // Helper function to format date according to company settings
    const formatDate = (dateField) => {
      if (!dateField) return 'Unknown date';
      return formatDateToCompanyFormat(dateField, currentCompany);
    };
    
    // Company details
    doc.setFontSize(16);
    doc.setFont(undefined, 'bold');
    doc.text(getCompanyName(), pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;
    
    // Add company logo if available
    if (selectedImages.logo) {
      try {
        const logoImg = new Image();
        logoImg.src = selectedImages.logo;
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
        });
        
        const logoWidth = 40;
        const logoHeight = 30;
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = yPosition - 35;
        
        doc.addImage(logoImg, 'JPEG', logoX, logoY, logoWidth, logoHeight);
        yPosition += 5;
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }
    
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    if (company?.address) {
      doc.text(company.address, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
    }
    if (company?.gstin) {
      doc.text(`GSTIN: ${company.gstin}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 5;
    }
    
    yPosition += 10;
    
    // Receipt title
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    doc.text('PAYMENT RECEIPT', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;
    
    // Receipt details
    doc.setFontSize(12);
    doc.setFont(undefined, 'normal');
    
    const receiptNumber = selectedPaymentForReceipt?.receiptNumber || 'N/A';
    const paymentDate = formatDate(selectedPaymentForReceipt?.date || selectedPaymentForReceipt?.paymentDate);
    const partyName = getPartyName();
    const paymentMode = selectedPaymentForReceipt?.mode || selectedPaymentForReceipt?.paymentMode || 'Cash';
    const amount = selectedPaymentForReceipt?.totalAmount || selectedPaymentForReceipt?.amount || 0;
    const reference = selectedPaymentForReceipt?.reference || '-';
    
    const receiptData = [
      ['Receipt Number:', receiptNumber],
      ['Date:', paymentDate],
      ['Party Name:', partyName],
      ['Payment Mode:', paymentMode],
      ['Amount:', formatCurrency(parseFloat(amount), companyCurrency)],
      ['Reference:', reference],
    ];
    
    receiptData.forEach(([label, value]) => {
      doc.setFont(undefined, 'bold');
      doc.text(label, margin, yPosition);
      doc.setFont(undefined, 'normal');
      doc.text(value, margin + 60, yPosition);
      yPosition += 8;
    });
    
    // Payment Allocations
    const allocations = selectedPaymentForReceipt?.allocations || [];
    if (allocations.length > 0) {
      yPosition += 10;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Payment Allocations:', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      allocations.forEach((allocation, index) => {
        const billType = allocation?.billType?.toUpperCase() || 'INVOICE';
        const billNumber = allocation?.billNumber || allocation?.billId || 'N/A';
        const allocatedAmount = allocation?.allocatedAmount || 0;
        doc.text(`${index + 1}. ${billType} ${billNumber}: ${formatCurrency(parseFloat(allocatedAmount), companyCurrency)}`, margin + 5, yPosition);
        yPosition += 6;
      });
    }
    
    // Advance allocations
    const advanceAllocations = selectedPaymentForReceipt?.advanceAllocations || [];
    if (advanceAllocations.length > 0) {
      yPosition += 10;
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Advance Allocations:', margin, yPosition);
      yPosition += 10;
      
      doc.setFontSize(10);
      doc.setFont(undefined, 'normal');
      advanceAllocations.forEach((advance, index) => {
        const amountUsed = advance?.amountUsed || 0;
        doc.text(`${index + 1}. Advance Payment: ${formatCurrency(parseFloat(amountUsed), companyCurrency)}`, margin + 5, yPosition);
        yPosition += 6;
      });
    }
    
    // Footer
    yPosition = pageHeight - 40;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 5;
    doc.text(`Time: ${new Date().toLocaleTimeString()}`, margin, yPosition);
    
    // Signature area
    yPosition = pageHeight - 30;
    doc.text('Authorized Signatory', pageWidth - margin - 50, yPosition, { align: 'right' });
    
      // Save PDF with initials and compact date
      const fmt = (d) => {
        try { const x = new Date(d); const day = String(x.getDate()); const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()]; const yr = x.getFullYear(); return `${day}${mon}${yr}`; } catch { return d; }
      };
      const fileName = `REC_${receiptNumber.replace(/[^a-zA-Z0-9/]/g, '_')}_${fmt(paymentDate)}.pdf`;
      doc.save(fileName);
    } catch (error) {
      console.error('Error generating PDF:', error);
      error('Error generating PDF. Please try again.');
    }
  };

  const handleSavePayment = async () => {
    if (!selectedBillForPayment || !paymentAmount) {
      warning('Please fill in amount field.');
      return;
    }

    try {
      // Ensure every payment has a receipt number - auto-generate if empty
      let effectiveReceiptNumber = (receiptNumber || '').trim();
      if (!effectiveReceiptNumber) {
        effectiveReceiptNumber = await generateReceiptNumber();
        setReceiptNumber(effectiveReceiptNumber);
      }

      const paymentAmountNum = parseFloat(paymentAmount);
      const billOutstanding = getBillOutstanding(selectedBillForPayment);
      
      // Check if payment amount exceeds bill outstanding
      if (paymentAmountNum > billOutstanding) {
        error(`Payment amount (${formatCurrency(paymentAmountNum, companyCurrency)}) cannot exceed the bill's outstanding amount (${formatCurrency(billOutstanding, companyCurrency)}). Please use the Payments page for amounts greater than the bill outstanding.`);
        return;
      }
      
      // IMPORTANT: Validate employee balance BEFORE creating payment (for outflow transactions)
      // Note: For sales, payments are typically inflow (receiving money), but we validate anyway
      if (recordInEmployeeCashFlow && selectedEmployeeForCashFlow) {
        const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
        if (selectedEmployee) {
          if ((selectedEmployee.status || 'active') !== 'active') {
            error('Selected employee is inactive. Please choose an active employee.', {
              title: 'Inactive Employee',
              duration: 5000
            });
            return;
          }
          // For sales, receiving payment is inflow (employee receives money)
          // But we still validate if somehow it's configured as outflow
          const transactionType = 'inflow'; // Sales payments are always inflow
          
          // Map paymentMode to cash/upi
          let mappedPaymentMode = 'cash';
          const paymentModeStr = String(paymentMode || '').toLowerCase().trim();
          if (paymentModeStr === 'upi' || paymentModeStr.includes('upi') || paymentModeStr === 'unified payments interface') {
            mappedPaymentMode = 'upi';
          } else if (paymentModeStr === 'cash' || paymentModeStr.includes('cash')) {
            mappedPaymentMode = 'cash';
          }
          
          const companyData = {
            companyId: currentCompany?.companyId,
            userId: currentCompany?.userId,
            appId: currentCompany?.appId
          };
          
          // For sales, we're receiving payment, so it's inflow - no balance validation needed
          // But we keep the structure for consistency
        }
      }
      
      // First, try to allocate available advance
      const availableAdvance = getPartyAdvance(selectedBillForPayment.party, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      let remainingPaymentAmount = paymentAmountNum;
      
      if (availableAdvance > 0) {
        // Allocate advance to this bill
        const advanceResult = allocateAdvanceToBill(selectedBillForPayment.party, billOutstanding, payments);
        if (advanceResult.allocatedAdvance > 0) {
          advanceAllocations = advanceResult.advanceAllocations;
          advanceUsed = advanceResult.allocatedAdvance;
          remainingPaymentAmount = paymentAmountNum - advanceUsed;
          
          // Mark advances as used
          if (Array.isArray(advanceResult.advanceAllocations) && advanceResult.advanceAllocations.length > 0) {
            await markAdvanceUsed(advanceResult.advanceAllocations, db, appId, userId, payments, companyId);
          }
        }
      }
      
      // Simple bill payment - no excess amount handling since it's restricted above
      const paymentData = {
        receiptNumber: effectiveReceiptNumber,
        paymentDate: paymentDate,
        partyId: selectedBillForPayment.party,
        partyName: getPartyName(selectedBillForPayment.party),
        totalAmount: paymentAmountNum,
        paymentMode: paymentMode,
        reference: paymentReference,
        notes: paymentNotes,
        type: docType,
        paymentType: 'bill',
        billId: selectedBillForPayment.id,
        billNumber: selectedBillForPayment.number,
        allocations: [{
          billType: docType,
          billId: selectedBillForPayment.id,
          billNumber: selectedBillForPayment.number,
          allocatedAmount: paymentAmountNum,
          billOutstanding: billOutstanding,
          isFullPayment: paymentAmountNum >= billOutstanding
        }],
        remainingAmount: 0, // No remaining amount since payment cannot exceed bill outstanding
        fifoAllocationUsed: 0,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Add to payments collection
      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      const docRef = await addDoc(paymentsRef, paymentData);
      
      // Record in employee cash flow if enabled
      if (recordInEmployeeCashFlow && selectedEmployeeForCashFlow) {
        try {
          const selectedEmployee = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
          if (selectedEmployee) {
            if ((selectedEmployee.status || 'active') !== 'active') {
              error('Selected employee is inactive. Please choose an active employee.', {
                title: 'Inactive Employee',
                duration: 5000
              });
              return;
            }
            // For sales, receiving payment is inflow (employee receives money)
            const transactionType = 'inflow';
            
            // Map paymentMode to cash/upi
            let mappedPaymentMode = 'cash';
            const paymentModeStr = String(paymentMode || '').toLowerCase().trim();
            if (paymentModeStr === 'upi' || paymentModeStr.includes('upi') || paymentModeStr === 'unified payments interface') {
              mappedPaymentMode = 'upi';
            } else if (paymentModeStr === 'cash' || paymentModeStr.includes('cash')) {
              mappedPaymentMode = 'cash';
            }
            
            const companyData = {
              companyId: currentCompany?.companyId,
              userId: currentCompany?.userId,
              appId: currentCompany?.appId
            };
            
            const employeeTransactionData = {
              employeeId: selectedEmployeeForCashFlow,
              employeeName: selectedEmployee.name || '',
              transactionType,
              amount: paymentAmountNum, // Use the actual payment amount
              paymentMode: mappedPaymentMode,
              description: `Payment ${paymentData.receiptNumber} - ${paymentData.partyName}`,
              referenceNumber: paymentData.receiptNumber,
              date: paymentData.paymentDate,
              category: 'Collection',
              createdBy: {
                userId: userId || currentCompany?.userId || '',
                name: currentCompany?.userName || 'User'
              },
              linkedPaymentId: docRef.id, // Link to payment record
              linkedReceiptNumber: paymentData.receiptNumber, // Link to receipt number
              ...(mappedPaymentMode === 'cash' && {
                cashReceivedFrom: paymentData.partyName
              }),
              ...(mappedPaymentMode === 'upi' && {
                upiId: selectedEmployee.paymentPreferences?.upiId || '',
                transactionId: paymentData.reference || '',
                upiApp: selectedEmployee.paymentPreferences?.upiApp || 'Other'
              })
            };
            
            const employeeResult = await EmployeeCashManager.createCashTransaction(
              companyData, 
              employeeTransactionData
            );
            
            if (employeeResult.success) {
              success('Payment recorded in employee cash flow', {
                title: 'Employee Transaction Created',
                duration: 4000
              });
            } else {
              // Rollback payment if employee transaction failed
              try {
                await deleteDoc(docRef);
                console.log('Payment rolled back due to employee transaction failure');
              } catch (rollbackError) {
                console.error('Error rolling back payment:', rollbackError);
              }
              error(employeeResult.error || 'Failed to record in employee cash flow. Payment was not processed.', {
                title: 'Transaction Failed',
                duration: 6000
              });
              return; // Stop here - payment rolled back
            }
          }
        } catch (employeeError) {
          console.error('Error recording in employee cash flow:', employeeError);
          // Rollback payment if employee transaction fails
          try {
            await deleteDoc(docRef);
            console.log('Payment rolled back due to employee transaction error');
          } catch (rollbackError) {
            console.error('Error rolling back payment:', rollbackError);
          }
          error('Payment was not processed. Failed to record in employee cash flow: ' + employeeError.message, {
            title: 'Transaction Failed',
            duration: 6000
          });
          return; // Stop here - payment rolled back
        }
      }
      
      // Reset payment form
      setPaymentAmount('');
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setPaymentMode('Cash');
      setPaymentReference('');
      setPaymentNotes('');
      setReceiptNumber('');
      setSelectedBillForPayment(null);
      setShowPaymentModal(false);
      setRecordInEmployeeCashFlow(false);
      setSelectedEmployeeForCashFlow('');
      
      // Show success notification
      paymentSuccess(
        `Payment of ${formatCurrency(paymentAmountNum, companyCurrency)} added successfully!`,
        {
          title: 'Payment Added Successfully',
          details: `Receipt #${effectiveReceiptNumber} • ${getPartyName(selectedBillForPayment.party)} • ${paymentMode}`,
          duration: 6000
        }
      );
    } catch (err) {
      console.error('Error saving payment:', err);
      error('Error saving payment. Please try again.', { title: 'Payment Failed' });
    }
  };

  // FIFO Payment Allocation Logic
  const allocatePaymentFIFO = (partyId, paymentAmount) => {
    try {
      if (!partyId || !paymentAmount) {
        return { allocations: [], remainingAmount: paymentAmount };
      }
      
      // Filter bills for this party with outstanding > 0
      const partyBills = bills.filter(bill => 
        (bill.partyId === partyId || bill.party === partyId) && 
        (bill.outstanding || 0) > 0
      );
      
      // Sort by date (FIFO - oldest first)
      partyBills.sort((a, b) => {
        const dateA = new Date(a.invoiceDate || a.date || 0);
        const dateB = new Date(b.invoiceDate || b.date || 0);
        return dateA - dateB;
      });
      
      // Check if there's available advance
      const availableAdvance = getPartyAdvance(partyId, payments);
      let remainingAmount = paymentAmount;
      const allocations = [];
      
      // Apply advance first if available
      if (availableAdvance > 0 && partyBills.length > 0) {
        const advanceResult = applyAdvanceToBillsUtil(partyId, partyBills, payments);
        
        if (advanceResult.totalApplied > 0) {
          advanceResult.allocations.forEach(advanceAlloc => {
            const bill = partyBills.find(b => b.id === advanceAlloc.billId);
            if (bill) {
              allocations.push({
                billType: docType,
                billId: bill.id,
                billNumber: bill.number || bill.invoiceNumber,
                allocatedAmount: advanceAlloc.amountApplied,
                billOutstanding: advanceAlloc.billOutstanding,
                isFullPayment: advanceAlloc.amountApplied >= advanceAlloc.billOutstanding,
                isAdvanceAllocation: true,
                advancePaymentId: advanceAlloc.advancePaymentId
              });
              bill.outstanding = Math.max(0, bill.outstanding - advanceAlloc.amountApplied);
            }
          });
        }
      }
      
      // Now allocate the current payment amount
      for (const bill of partyBills) {
        if (remainingAmount <= 0) break;
        
        const originalBillAmount = bill.totalAmount || bill.amount || 0;
        const existingAdvanceAllocation = allocations.find(alloc => alloc.billId === bill.id && alloc.isAdvanceAllocation);
        const remainingBillOutstanding = bill.outstanding || 0;
        
        if (remainingBillOutstanding <= 0) continue;
        
        const allocatedAmount = Math.min(remainingAmount, remainingBillOutstanding);
        
        if (allocatedAmount > 0) {
          if (existingAdvanceAllocation) {
            existingAdvanceAllocation.allocatedAmount += allocatedAmount;
            existingAdvanceAllocation.isFullPayment = existingAdvanceAllocation.allocatedAmount >= originalBillAmount;
            existingAdvanceAllocation.isAdvanceAllocation = false;
          } else {
            allocations.push({
              billType: docType,
              billId: bill.id,
              billNumber: bill.number || bill.invoiceNumber,
              allocatedAmount: allocatedAmount,
              billOutstanding: originalBillAmount,
              isFullPayment: allocatedAmount >= originalBillAmount,
              isAdvanceAllocation: false
            });
          }
          
          bill.outstanding = Math.max(0, remainingBillOutstanding - allocatedAmount);
          remainingAmount -= allocatedAmount;
        }
      }
      
      return { allocations, remainingAmount };
    } catch (error) {
      console.error('Error in allocatePaymentFIFO:', error);
      return { allocations: [], remainingAmount: paymentAmount };
    }
  };

  // Handle FIFO Payment
  const handleFIFOPayment = async () => {
    if (!selectedPartyForFIFO || !fifoPaymentAmount) {
      warning('Please select a party and enter payment amount.');
      return;
    }

    try {
      const paymentAmountNum = parseFloat(fifoPaymentAmount);
      if (isNaN(paymentAmountNum) || paymentAmountNum <= 0) {
        warning('Please enter a valid payment amount.');
        return;
      }

      if (!fifoReceiptNumber.trim()) {
        warning('Please enter a receipt number.');
        return;
      }

      // Get available advance
      const availableAdvance = getPartyAdvance(selectedPartyForFIFO, payments);
      let advanceAllocations = [];
      let advanceUsed = 0;
      let totalAmountToAllocate = paymentAmountNum;

      // Apply advance if available
      if (availableAdvance > 0) {
        const outstandingBills = bills.filter(bill => 
          (bill.partyId === selectedPartyForFIFO || bill.party === selectedPartyForFIFO) && 
          (bill.outstanding || 0) > 0
        );
        
        if (outstandingBills.length > 0) {
          const advanceResult = applyAdvanceToBillsUtil(selectedPartyForFIFO, outstandingBills, payments);
          if (advanceResult.totalApplied > 0) {
            advanceAllocations = advanceResult.advanceAllocations || [];
            advanceUsed = advanceResult.totalApplied;
            await markAdvanceUsed(advanceAllocations, db, appId, userId, payments, companyId);
          }
        }
      }

      // Allocate payment using FIFO
      const fifoResult = allocatePaymentFIFO(selectedPartyForFIFO, totalAmountToAllocate);
      
      if (!fifoResult || !Array.isArray(fifoResult.allocations)) {
        throw new Error('Failed to allocate payment. Please try again.');
      }

      const { allocations, remainingAmount } = fifoResult;

      const paymentData = {
        receiptNumber: fifoReceiptNumber,
        paymentDate: fifoPaymentDate,
        partyId: selectedPartyForFIFO,
        partyName: getPartyName(selectedPartyForFIFO),
        totalAmount: totalAmountToAllocate,
        paymentMode: fifoPaymentMode,
        reference: fifoPaymentReference,
        notes: fifoPaymentNotes,
        type: docType,
        paymentType: 'khata',
        allocations: allocations,
        remainingAmount: remainingAmount,
        advanceAllocations: advanceAllocations,
        advanceUsed: advanceUsed,
        originalPaymentAmount: paymentAmountNum,
        advanceAmount: availableAdvance,
        fifoAllocationUsed: remainingAmount > 0 ? 0 : totalAmountToAllocate,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const paymentsRef = getCollectionForCompany(db, { companyId, appId, userId }, 'payments');
      await addDoc(paymentsRef, paymentData);

      // Reset form
      setFifoPaymentAmount('');
      setFifoPaymentDate(new Date().toISOString().split('T')[0]);
      setFifoPaymentMode('Cash');
      setFifoPaymentReference('');
      setFifoPaymentNotes('');
      setFifoReceiptNumber('');
      setSelectedPartyForFIFO('');
      setShowFIFOPaymentModal(false);

      success(
        `FIFO Payment of ${formatCurrency(paymentAmountNum, companyCurrency)} added successfully!`,
        {
          title: 'FIFO Payment Added',
          details: `Receipt #${fifoReceiptNumber} • ${getPartyName(selectedPartyForFIFO)} • ${fifoPaymentMode}`,
          duration: 6000
        }
      );
    } catch (err) {
      console.error('Error saving FIFO payment:', err);
      error('Error saving FIFO payment. Please try again.', { title: 'Payment Failed' });
    }
  };

  // Open FIFO Payment Modal
  const handleOpenFIFOPayment = async () => {
    const receiptNum = await generateReceiptNumber();
    setFifoReceiptNumber(receiptNum);
    setFifoPaymentDate(new Date().toISOString().split('T')[0]);
    setShowFIFOPaymentModal(true);
  };

  // For SunriseTemplate, ensure A4 size and fixed layout
  const [invoiceZoom, setInvoiceZoom] = useState(1);
  const [copyInvoiceImageBusy, setCopyInvoiceImageBusy] = useState(false);
  const invoiceRef = useRef();

  const handleInvoicePrint = async () => {
    if (!invoiceRef.current) return;
    try {
      const bodyHtml = await prepareInvoicePrintBodyHtml(invoiceRef.current, { forPdfExport: false });
      const printWindow = window.open('', '', 'height=800,width=1000');
      if (!printWindow) {
        warning('Please allow popups to print the invoice.');
        return;
      }
      printWindow.document.write(
        '<!DOCTYPE html><html><head><title>Invoice</title>' +
          '<link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">' +
          '<style>' +
          INVOICE_PRINT_WINDOW_CHROME_CSS +
          '</style></head><body style="background:white;">' +
          bodyHtml +
          '</body></html>'
      );
      printWindow.document.close();
      printWindow.focus();
      printWindow.onload = async () => {
        await waitDocumentImages(printWindow.document);
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 300);
      };
    } catch (err) {
      console.error('Print failed:', err);
      error('Could not open print preview. Please try again.');
    }
  };

  const hidePrintOnlyElements = (container) => {
    const hiddenElements = container.querySelectorAll('.print\\:hidden, [class*="print:hidden"]');
    hiddenElements.forEach(el => el.style.display = 'none');
  };

  const processImagesForPdf = async (container) => {
    const imgs = Array.from(container.querySelectorAll('img'));
    const imagePromises = imgs.map(async (img) => {
      const src = img.getAttribute('src');
      if (!src) return;

      try {
        let imageData = await fetchImageAsDataUrlForPrint(src);
        if (!imageData) {
          imageData = await convertImageToDataUrl(src);
        }
        if (imageData) {
          img.setAttribute('src', imageData);
          img.removeAttribute('crossorigin');
          try {
            if (img.decode) await img.decode();
          } catch (_) {
            /* decode optional */
          }
          return;
        }

        if (window.imageManager) {
          const imageRecord = await window.imageManager.getImage(src);
          if (imageRecord?.data) {
            img.setAttribute('src', imageRecord.data);
            img.removeAttribute('crossorigin');
          }
        }
      } catch (error) {
        console.warn('Image preprocessing failed for PDF export:', error);
        img.dataset.pdfImageError = 'true';
        if (!img.getAttribute('alt')) {
          img.setAttribute('alt', 'Image unavailable');
        }
        if (!img.getAttribute('src')) {
          img.setAttribute('src', src);
        }
      }
    });

    await Promise.all(imagePromises);
  };

  const prepareInvoicePrintExportDom = async ({ forImageExport = false } = {}) => {
    if (!invoiceRef.current) {
      throw new Error('Invoice preview is not available for export');
    }
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const bodyHtml = await prepareInvoicePrintBodyHtml(invoiceRef.current, {
      forPdfExport: !forImageExport,
      forImageExport,
    });
    const iframe = await mountInvoicePrintIframe(bodyHtml);
    const doc = iframe.contentDocument;
    const exportRoot = getInvoiceTemplateRoot(doc.body);
    if (!exportRoot) {
      iframe.remove();
      throw new Error('Invoice template not found in export frame');
    }
    hidePrintOnlyElements(doc.body);
    await processImagesForPdf(doc.body);
    if (document.fonts?.ready) {
      try {
        await document.fonts.ready;
      } catch (err) {
        console.warn('Font readiness check failed for export:', err);
      }
    }
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    await new Promise((r) => setTimeout(r, 200));
    // Re-measure after images/fonts — short iframe crops PDF bottoms.
    sizeInvoiceExportIframe(iframe, exportRoot);
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    return {
      exportRoot,
      iframe,
      cleanup: () => {
        try {
          iframe.remove();
        } catch (_) {
          /* ignore */
        }
      },
    };
  };

  const ensureHtml2Canvas = async () => {
    if (typeof window !== 'undefined' && window.__acctooHtml2Canvas) {
      return window.__acctooHtml2Canvas;
    }
    const mod = await import('html2canvas');
    const lib = mod?.default || mod;
    if (typeof lib !== 'function') {
      throw new Error('html2canvas did not export a function');
    }
    if (typeof window !== 'undefined') {
      window.__acctooHtml2Canvas = lib;
    }
    return lib;
  };

  const ensureHtml2Pdf = async () => {
    if (typeof window !== 'undefined' && window.__acctooHtml2Pdf) {
      return window.__acctooHtml2Pdf;
    }
    const mod = await import('html2pdf.js');
    const lib = mod?.default || mod;
    if (typeof lib !== 'function') {
      throw new Error('html2pdf.js did not export a function');
    }
    if (typeof window !== 'undefined') {
      window.__acctooHtml2Pdf = lib;
    }
    return lib;
  };

  const ensureJsPdf = async () => {
    if (typeof window !== 'undefined' && window.__acctooJsPdf) {
      return window.__acctooJsPdf;
    }
    const mod = await import('jspdf');
    const JsPdfCtor = mod?.jsPDF || mod?.default?.jsPDF || mod?.default;
    if (typeof JsPdfCtor !== 'function') {
      throw new Error('jsPDF constructor not found');
    }
    if (typeof window !== 'undefined') {
      window.__acctooJsPdf = JsPdfCtor;
    }
    return JsPdfCtor;
  };

  /** Same html2canvas pipeline as PDF export; higher scale = sharper PNG (clipboard). */
  const canvasFromInvoiceClone = async (tempContainer, scale = 2, { contentHeightOnly = false } = {}) => {
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready;
      } catch (err) {
        console.warn('Font readiness check failed:', err);
      }
    }
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const html2canvas = await ensureHtml2Canvas();
    // Do NOT pass windowWidth/windowHeight — values smaller than the full invoice cause html2canvas to crop.
    return html2canvas(tempContainer, {
      scale,
      useCORS: true,
      allowTaint: true,
      logging: false,
      backgroundColor: '#ffffff',
      scrollX: 0,
      scrollY: 0,
      letterRendering: false,
      foreignObjectRendering: false,
      removeContainer: false,
      ignoreElements: () => false,
      onclone: (clonedDoc) => {
        const clonedContainer = clonedDoc.querySelector('body > div:first-child');
        if (clonedContainer) {
          const originalStyle = getComputedStyle(tempContainer);
          clonedContainer.style.fontFamily = originalStyle.fontFamily;
          clonedContainer.style.fontSize = originalStyle.fontSize;
          clonedContainer.style.fontWeight = originalStyle.fontWeight;
        }
        applyBillExportCanvasFixes(clonedDoc, { contentHeightOnly });
      }
    });
  };

  const buildPdfBlobFromElement = async (exportRoot) => {
    const html2pdf = await ensureHtml2Pdf();
    // Margin 0 — template already pads ~8mm. Extra jsPDF margins + 210mm width crop sides.
    // Avoid only small rows; avoiding large blocks (header/post-items) clips when taller than a page.
    const opt = {
      margin: [0, 0, 0, 0],
      filename: 'invoice.pdf',
      image: { type: 'jpeg', quality: 0.96 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        letterRendering: false,
        backgroundColor: '#ffffff',
        scrollX: 0,
        scrollY: 0,
        // Do not set windowHeight/height — undersized values crop (see canvasFromInvoiceClone).
        onclone: (clonedDoc) => {
          applyBillExportCanvasFixes(clonedDoc);
        },
      },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: {
        mode: ['css', 'legacy'],
        avoid: ['tr', '.invoice-item-row'],
      },
    };

    return html2pdf().set(opt).from(exportRoot).outputPdf('blob');
  };

  const openPdfPreviewWindow = (pdfBlob, filename) => {
    const pdfUrl = URL.createObjectURL(pdfBlob);
    const previewWindow = window.open('', '_blank', 'width=1200,height=800');
    if (!previewWindow) {
      info('Please allow popups to preview the PDF. The file will download instead.');
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(pdfUrl);
      return;
    }

    previewWindow.document.write(`
      <html>
        <head>
          <title>${filename}</title>
          <style>
            html, body {
              margin: 0;
              padding: 0;
              height: 100%;
              width: 100%;
              background: #1f2937;
            }
            .toolbar {
              background: #111827;
              color: white;
              padding: 8px 16px;
              font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            iframe {
              border: none;
              width: 100%;
              height: calc(100% - 48px);
              background: #fff;
            }
            button {
              background: #10b981;
              color: white;
              border: none;
              padding: 6px 12px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
            }
            button:hover {
              background: #059669;
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <span>${filename}</span>
            <div>
              <button id="downloadBtn">Download</button>
              <button id="closeBtn" style="background:#ef4444;margin-left:8px;">Close</button>
            </div>
          </div>
          <iframe src="${pdfUrl}"></iframe>
          <script>
            const pdfUrl = '${pdfUrl}';
            document.getElementById('downloadBtn').addEventListener('click', () => {
              const link = document.createElement('a');
              link.href = pdfUrl;
              link.download = '${filename}';
              link.click();
            });
            document.getElementById('closeBtn').addEventListener('click', () => {
              window.close();
            });
            window.addEventListener('unload', () => {
              fetch(pdfUrl).catch(() => {});
            });
          </script>
        </body>
      </html>
    `);
    previewWindow.document.close();
  };

  const cloneInvoiceForExport = async ({ forImageExport = false } = {}) => {
    const { exportRoot, iframe, cleanup } = await prepareInvoicePrintExportDom({ forImageExport });
    return {
      tempContainer: iframe.contentDocument.body,
      exportRoot,
      cleanup,
    };
  };

  const cloneInvoiceForPdf = () => cloneInvoiceForExport({ forImageExport: false });

  // PDF download handler - Fixed for all document types with helper functions
  const handleInvoiceDownloadWithHelpers = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      warning('Please select a bill to download as PDF');
      return;
    }
    
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };
      
      const bankDetails = {
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };
      
      console.log('Preloading images for PDF generation...');
      await preloadBillImages(companyDetails, bankDetails);
      
      const { exportRoot, cleanup } = await cloneInvoiceForPdf();
      
      try {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
      const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill.number || 'Bill'}_${fmt(invoiceBill.invoiceDate || invoiceBill.date)}.pdf`;
      
      const pdfBlob = await buildPdfBlobFromElement(exportRoot);
        openPdfPreviewWindow(pdfBlob, filename);
      } finally {
        cleanup();
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      error('Error generating PDF. Please try again.');
    }
  };

  // Optimized PDF Share Handler - generates PDF as blob and shares via system - Fixed for all document types
  const handleInvoiceSharePdf = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      warning('Please select a bill to share as PDF');
      return;
    }
    
    try {
      // Preload images before PDF generation
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };
      
      const bankDetails = {
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };
      
      console.log('Preloading images for PDF share...');
      await preloadBillImages(companyDetails, bankDetails);
      
      const { exportRoot, cleanup } = await cloneInvoiceForPdf();
      
      let pdfBlob;
      let filename;
      try {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Generate filename
      const fmt = (d) => {
        try {
          const x = new Date(d);
          const day = String(x.getDate());
          const mon = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][x.getMonth()];
          const yr = x.getFullYear();
          return `${day}${mon}${yr}`;
        } catch {
          return d;
        }
      };
      
        filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document').toUpperCase()}_${invoiceBill.number || 'Bill'}_${fmt(invoiceBill.invoiceDate || invoiceBill.date)}.pdf`;
      
      // Generate PDF with optimized settings
      pdfBlob = await buildPdfBlobFromElement(exportRoot);
      } finally {
        cleanup();
      }
      
      // Share PDF via system share or download
      try {
        if (navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], filename, { type: 'application/pdf' })] })) {
          await navigator.share({
            title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
            text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
            files: [new File([pdfBlob], filename, { type: 'application/pdf' })]
          });
        } else {
          // Download fallback
          const url = URL.createObjectURL(pdfBlob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
        }
      } catch (shareError) {
        // Download fallback if sharing fails
        const url = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error generating PDF for sharing:', error);
      error('Error generating PDF for sharing. Please try again.');
    }
  };

  // Share: link to open this bill directly in preview (same as reports flow)
  const handleInvoiceShareLink = async () => {
    try {
      if (!invoiceBill || !invoiceRef.current) {
        warning('Please select a bill to share');
        return;
      }
      // Render a static HTML for public viewing (no app chrome)
      const container = document.createElement('html');
      const head = document.createElement('head');
      head.innerHTML = '<meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>Document</title><link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/tailwindcss/dist/tailwind.min.css">';
      const body = document.createElement('body');
      const doc = document.createElement('div');
      doc.style.background = 'white';
      doc.style.margin = '0 auto';
      doc.style.padding = '0';
      doc.innerHTML = invoiceRef.current.innerHTML;
      // Strip interactive controls
      Array.from(doc.querySelectorAll('button')).forEach(b => b.remove());
      // Keep images as-is (reports flow keeps minimal HTML and uploads)
      body.appendChild(doc);
      container.appendChild(head);
      container.appendChild(body);

      const blob = new Blob([container.outerHTML || container.innerHTML], { type: 'text/html' });
      const storage = getStorage(app);
      const token = Math.random().toString(36).slice(2);
      const path = `publicDocs/${userId || 'anon'}/${invoiceBill.id}-${token}.html`;
      const sref = storageRef(storage, path);
      await uploadBytes(sref, blob, { contentType: 'text/html' });
      const storageUrl = await getDownloadURL(sref);
      const hostingBase = 'https://acctoo.com';
      const wrapped = `${hostingBase}/doc-viewer.html?u=${encodeURIComponent(storageUrl)}`;
      if (navigator.share) {
        await navigator.share({ title: (docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'), text: (docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'), url: wrapped });
      } else {
        await navigator.clipboard.writeText(wrapped);
        success('Public link copied to clipboard', { title: 'Link Copied' });
      }
    } catch (e) {
      console.error('Share link failed:', e);
      error('Error sharing link. Please try again.');
    }
  };

  // Share: Image (PNG) — same clone + html2canvas pipeline as PDF / copy-to-clipboard (content height, no A4 padding).
  const handleInvoiceShareImage = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      warning('Please select a bill to share as image');
      return;
    }

    try {
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };

      const bankDetails = {
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };

      await preloadBillImages(companyDetails, bankDetails);

      const { exportRoot, cleanup } = await cloneInvoiceForExport({ forImageExport: true });
      try {
        await new Promise((resolve) => setTimeout(resolve, 300));
        const canvas = await canvasFromInvoiceClone(exportRoot, getInvoiceHtml2CanvasScale(true), { contentHeightOnly: true });
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not create image'))), 'image/png');
        });
        const filename = `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')}_${invoiceBill.number || 'Bill'}.png`;

        try {
          if (navigator.canShare && navigator.canShare({ files: [new File([blob], filename, { type: 'image/png' })] })) {
            await navigator.share({
              title: `${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
              text: `Sharing ${(docTypeOptions.find(opt => opt.value === docType)?.label || 'Document')} ${invoiceBill.number || 'Bill'}`,
              files: [new File([blob], filename, { type: 'image/png' })]
            });
          } else {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            link.click();
            URL.revokeObjectURL(url);
          }
        } catch (shareError) {
          if (shareError?.name === 'AbortError') return;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
        }
      } finally {
        cleanup();
      }
    } catch (shareGenError) {
      console.error('Error generating image for sharing:', shareGenError);
      error('Error generating image for sharing. Please try again.');
    }
  };

  /** High-res PNG to clipboard — web. On native Capacitor, image clipboard is unreliable → Share sheet. */
  const handleInvoiceCopyImageToClipboard = async () => {
    if (!invoiceRef.current || !invoiceBill) {
      warning('Please select a bill first');
      return;
    }

    // Native app: Web Clipboard image write is unsupported/broken — use system Share instead
    if (Capacitor.isNativePlatform()) {
      setCopyInvoiceImageBusy(true);
      try {
        await handleInvoiceShareImage();
      } finally {
        setCopyInvoiceImageBusy(false);
      }
      return;
    }

    if (!navigator.clipboard?.write || typeof ClipboardItem === 'undefined') {
      warning('Copying images is not supported in this browser. Use Share as Image instead.');
      return;
    }
    setCopyInvoiceImageBusy(true);
    try {
      const companyDetails = {
        logoUrl: currentCompany?.logoUrl || currentCompany?.details?.logoUrl,
        upiQrUrl: currentCompany?.upiQrUrl || currentCompany?.details?.upiQrUrl,
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };
      const bankDetails = {
        sealUrl: resolveCompanySealUrl(currentCompany, company),
        signUrl: resolveCompanySignUrl(currentCompany, company)
      };
      await preloadBillImages(companyDetails, bankDetails);
      const { exportRoot, cleanup } = await cloneInvoiceForExport({ forImageExport: true });
      try {
        await new Promise((resolve) => setTimeout(resolve, 150));
        const canvas = await canvasFromInvoiceClone(exportRoot, getInvoiceHtml2CanvasScale(true), { contentHeightOnly: true });
        const blob = await new Promise((resolve, reject) => {
          canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not create image'))), 'image/png');
        });
        await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        success('Invoice image copied — paste in WhatsApp, email, or any app', { title: 'Copied to clipboard', duration: 2200 });
      } finally {
        cleanup();
      }
    } catch (e) {
      console.error('Copy invoice image failed:', e);
      if (e?.name === 'NotAllowedError') {
        error('Clipboard permission denied. Allow clipboard access for this site or use Share as Image.');
      } else {
        error('Could not copy image. Try Share as Image or Save as PDF.');
      }
    } finally {
      setCopyInvoiceImageBusy(false);
    }
  };

  const handleInvoiceWheel = (e) => {
    // Only zoom when Ctrl key is held down, otherwise allow normal scrolling
    if (e.ctrlKey && showInvoiceModal) {
      try {
        e.preventDefault();
      } catch (err) {
        // Ignore passive listener errors
      }
      setInvoiceZoom(z => {
        let next = z + (e.deltaY < 0 ? 0.1 : -0.1);
        next = Math.max(0.5, Math.min(2, next));
        return Math.round(next * 100) / 100;
      });
    }
    // If Ctrl is not held, let the browser handle normal scrolling
  };









    // Show loading state if authentication or database is not ready
    if (!isAuthReady || !db || !userId) {
      return (
        <LoadingScreen 
          message="Loading Sales..."
          subMessage="Initializing..."
          fullScreen={true}
        />
      );
    }

    // Show loading screen while initial data is being fetched
    // Show if we're in initial loading state (set immediately on mount, before any data operations)
    const shouldShowLoading = isInitialLoading && db && userId && isAuthReady;
    
    if (shouldShowLoading) {
      console.log('🔄 [LOADING SCREEN] Sales: RENDERING LOADING SCREEN NOW', {
        isInitialLoading,
        partiesCount: parties.length,
        itemsCount: items.length,
        purchasesCount: purchases.length,
        hasDb: !!db,
        hasUserId: !!userId,
        isAuthReady,
        hasCompanyId: !!companyId,
        dataLoaded: dataLoadedRef.current
      });
      return (
        <LoadingScreen 
          message="Loading Sales..."
          subMessage="Preparing your sales records and forms"
          fullScreen={true}
        />
      );
    }

    return (
    <div className="min-h-screen bg-gray-100 flex flex-col items-center py-4 sm:py-8 max-w-full overflow-x-hidden">
      {itemsFetchError && (
        <div className="w-full max-w-7xl px-3 sm:px-4 mb-3">
          <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm">
            Items may not have loaded correctly ({itemsFetchError}). Refresh the page before creating bills, or item names may show as Unnamed.
          </div>
        </div>
      )}
      <style>
        {`
          /* Fix datalist dropdown z-index issues */
          input[list]::-webkit-calendar-picker-indicator {
            z-index: 9999 !important;
          }
          
          /* Ensure datalist dropdown appears above other elements */
          input[list] + datalist {
            z-index: 9999 !important;
            position: relative !important;
          }
          
          /* Fix table cell positioning context for dropdowns */
          .mobile-table td {
            position: relative;
          }
          
          .mobile-table td .relative {
            z-index: auto;
          }
          
          /* Ensure dropdowns in table cells appear above */
          .mobile-table td input[list] {
            position: relative;
            z-index: 10;
          }
          
          /* Custom dropdown styling */
          select[style*="backgroundImage"] {
            -webkit-appearance: none;
            -moz-appearance: none;
            appearance: none;
          }
          
          select[style*="backgroundImage"]::-ms-expand {
            display: none;
          }
          
          /* Ensure all select dropdowns appear above other content */
          select {
            z-index: 10 !important;
            position: relative;
          }
        `}
      </style>
      {errorMessage && (
        <div className="fixed top-6 left-1/2 transform -translate-x-1/2 bg-red-100 border border-red-400 text-red-700 px-4 sm:px-6 py-3 rounded shadow-lg z-50 flex items-center gap-4 max-w-[90vw]">
          <span className="text-sm sm:text-base">{errorMessage}</span>
          <button onClick={() => setErrorMessage('')} className="ml-4 text-red-700 font-bold text-lg min-w-[44px] min-h-[44px] flex items-center justify-center">&times;</button>
        </div>
      )}
      
      <div className="w-full max-w-5xl mb-4 sm:mb-6 px-4 sm:px-0">
        {/* Desktop: Full-width Tabs, Mobile: Dropdown */}
        <div className="mb-2">
          {/* Desktop View - Full-width Tabs */}
          <div className="hidden sm:block">
            <div className="flex w-full bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-100 rounded-xl p-1.5 justify-between shadow-sm gap-1">
              {docTypeOptions.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setDocType(opt.value)}
                  className={`flex-1 min-w-0 flex items-center justify-center gap-2 px-3 py-3 text-sm font-medium rounded-md transition-all duration-200 ${
                    docType === opt.value
                      ? 'bg-white text-blue-700 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
                  }`}
                >
                  {/* Icon for each document type */}
                  {opt.value === 'invoice' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {opt.value === 'challan' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                  )}
                  {opt.value === 'quotation' && (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  )}
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Mobile View - Dropdown */}
          <div className="sm:hidden flex items-center justify-center">
            <div className="relative">
              <button
                onClick={() => setShowDocTypeDropdown(!showDocTypeDropdown)}
                className="bg-white border border-blue-100 rounded-xl shadow-sm px-4 py-2.5 flex items-center gap-2 text-sm font-semibold text-slate-700 hover:bg-blue-50 transition-all duration-200 min-w-[120px] sm:min-w-[140px] justify-center"
              >
                {/* Icon for current document type */}
                {docType === 'invoice' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                )}
                {docType === 'challan' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                )}
                {docType === 'quotation' && (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                )}
                <span>{docTypeOptions.find(opt => opt.value === docType)?.label || 'INVOICE'}</span>
                <svg className={`w-4 h-4 transition-transform duration-200 ${showDocTypeDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              
              {showDocTypeDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                  {docTypeOptions.map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => {
                        setDocType(opt.value);
                        setShowDocTypeDropdown(false);
                      }}
                      className={`w-full px-4 py-2 text-left text-sm font-medium transition-all duration-200 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg ${
                        docType === opt.value
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {/* Icon for each document type */}
                      {opt.value === 'invoice' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                      {opt.value === 'challan' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                      )}
                      {opt.value === 'quotation' && (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      )}
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full px-4 sm:px-6">
        {/* Quick Items Section - 20-60-20 Layout */}
        <div className="mb-6">
          <div className="grid grid-cols-12 gap-4 items-stretch">
            {/* Left Panel - Most Used Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-blue-50 via-indigo-50 to-orange-50 border border-blue-100 rounded-2xl p-4 flex flex-col shadow-md">
              <h3 className="text-lg font-bold text-blue-800 mb-4 text-center bg-white/80 rounded-xl py-2 px-3 shadow-sm">Most Used Items</h3>

              <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                {mostUsedItems.map((itemData) => {
                  const item = items.find(it => it.id === itemData.id);
                  if (!item) return null;
                  
                  return (
                    <button
                      key={itemData.itemId || itemData.id}
                      onClick={() => handleQuickItemClick(itemData)}
                      className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded-md p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                      title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                    >
                      <div className="font-semibold text-sm text-blue-900 truncate">
                        {itemData.itemName || itemData.name || 'Unknown Item'}
                      </div>
                      <div className="text-xs text-blue-700 font-medium">
                        {formatCurrency(itemData.lastRate || 0, companyCurrency)} × {itemData.lastQty || 1}
                      </div>
                      <div className="text-xs text-blue-600">
                        Used {itemData.count} times
                      </div>
                    </button>
                  );
                })}
              </div>
              {mostUsedItems.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-4">
                  No items used yet
                </div>
              )}
            </div>

            {/* Center Panel - Sales Entry Form (60%) */}
            <div className="col-span-12 lg:col-span-8 bg-gradient-to-b from-white to-slate-50 border border-slate-200 rounded-2xl shadow-lg p-4 sm:p-6">
              {/* Form Reset Indicator */}
              {isResettingForm && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-2"></div>
                  <span className="text-blue-700 font-medium">Resetting form to initial state...</span>
                </div>
              )}
              {/* Fixed Header - Mobile & Desktop */}
              <div className="bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-100 rounded-xl p-3 mb-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-2 sm:gap-3">
                  <div className="flex-1 min-w-[40%]">
                    <div className="text-xs text-slate-500 mb-1">{numberLabel}</div>
                    <div className="text-sm font-semibold text-slate-800 break-all">{invoiceNumber || 'Auto'}</div>
                  </div>
                  <div className="flex-1 min-w-[40%] sm:ml-2">
                    <div className="text-xs text-slate-500 mb-1">Date</div>
                    <div className="text-sm font-semibold text-slate-800">{formatDateToCompanyFormat(invoiceDate, currentCompany)}</div>
                  </div>
                  {company.gstinType === 'Regular' && (
                    <div className="flex-1 min-w-[40%] sm:ml-2">
                      <div className="text-xs text-gray-500 mb-1">GST Calculation</div>
                      <div className="text-sm font-semibold text-gray-800">
                        {gstCalculationType === 'excluding' ? 'Excluding GST' : 'Including GST'}
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setShowMobileEditModal(true)}
                    className="ml-auto shrink-0 min-h-[44px] min-w-[44px] flex items-center justify-center p-2 text-gray-600 hover:text-blue-600 hover:bg-white/60 rounded-lg touch-manipulation"
                    title="Edit Details"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Party Selection - Mobile & Desktop */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Party (Buyer)</label>
                <div className="relative" ref={partyDropdownRef}>
                  <input
                    type="text"
                    value={partySearchText}
                    onChange={(e) => {
                      setPartySearchText(e.target.value);
                      if (e.target.value === '') {
                        setParty('');
                        setSelectedPartyForQuickItems('');
                      }
                    }}
                    onFocus={() => setShowPartyDropdown(true)}
                    className="w-full border border-slate-300 rounded-xl shadow-sm p-2.5 text-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                    placeholder="Search or type new party name..."
                  />
                  {showPartyDropdown && (
                    <div className="absolute z-10 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                      {filteredParties.map(p => (
                        <button
                          key={p.id}
                          onClick={() => {
                            setParty(p.id);
                            setSelectedPartyForQuickItems(p.id);
                            setPartySearchText(p.firmName);
                            setShowPartyDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                        >
                          {p.firmName}
                        </button>
                      ))}
                      {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                        <button
                          onClick={() => {
                            setNewPartyName(partySearchText);
                            setShowAddPartyModal(true);
                            setShowPartyDropdown(false);
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                        >
                          + Add "{partySearchText}" as new party
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {['invoice', 'challan', 'quotation'].includes(docType) && (
                <div className="mb-4">
                  <WorkOrderLinkSelect
                    db={db}
                    companyCtx={companyCtx}
                    workOrderId={workOrderLink.workOrderId}
                    phaseId={workOrderLink.phaseId}
                    costCategory={workOrderLink.costCategory}
                    onChange={setWorkOrderLink}
                  />
                </div>
              )}

              {/* Original Bill Reference and Reason Fields (for Returns and Notes) */}
              {(docType === 'salesReturn' || docType === 'creditNote' || docType === 'debitNote') && (
                <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-yellow-800 mb-3">
                    {docType === 'salesReturn' ? 'Return Details' : 'Note Details'}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Original {docType === 'salesReturn' ? 'Invoice' : 'Bill'} Number
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={originalBillNumber}
                          onChange={(e) => handleOriginalBillNumberChange(e.target.value)}
                          className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm pr-8"
                          placeholder="Enter or search original bill number"
                        />
                        {originalBillNumber && (
                          <button
                            type="button"
                            onClick={() => {
                              setOriginalBillNumber('');
                              setOriginalBillId('');
                              setRows(Array.from({ length: 5 }, () => ({ ...initialItemRow })));
                              setParty('');
                            }}
                            className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            title="Clear"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      {originalBillId && (
                        <p className="text-xs text-green-600 mt-1">
                          ✓ Original bill loaded. Items and party have been auto-populated.
                        </p>
                      )}
                    </div>
                    {docType === 'salesReturn' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Return Reason
                        </label>
                        <input
                          type="text"
                          value={returnReason}
                          onChange={(e) => setReturnReason(e.target.value)}
                          className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                          placeholder="e.g., Defective goods, Wrong item"
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}

          <div className="flex items-center justify-between mb-3">
            <h3 id="invoice-items-title" className="text-xl font-bold text-slate-800 tracking-tight">Invoice Items</h3>
            <button
              ref={quickItemButtonRef}
              onClick={(e) => {
                const position = calculateModalPosition(e.currentTarget);
                setQuickItemModalPosition(position);
                setShowQuickItemsModal(true);
              }}
              className="lg:hidden bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 text-white font-semibold py-1.5 px-3.5 rounded-lg text-sm flex items-center gap-1.5 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Quick Item
            </button>
          </div>
          
          
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 mb-4 max-h-[600px] custom-scrollbar touch-scrollbar">
            <table ref={tableRef} className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                                    <tr>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Item</th>
                  <th className="px-3 py-2.5 text-left align-top min-w-[12rem] max-w-[16rem] w-[14rem]">
                    <span className="block text-xs font-semibold text-gray-600 uppercase tracking-wide">Tags</span>
                  </th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Measurement / numbers</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Qty</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Rate</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Discount</th>
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Amount</th>
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">SGST</th>}
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">CGST</th>}
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">IGST</th>}
                  {company.gstinType === 'Regular' && <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Total</th>}
                  <th className="px-3 py-2.5 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Remove</th>
                                    </tr>
                                </thead>
              <tbody>
                {rows.map((row, idx) => {
  // Determine GST type for helper text
  const itemObj = findItemInMaster(row.item, items);
  const gstPercent = itemObj ? (itemObj.gstPercentage || 0) : 0;
  const sellerGstin = company.gstin || '';
  const partyObj = parties.find(p => p.id === party);
  const buyerGstin = partyObj ? (partyObj.gstin || '') : '';
  let gstType = '';
  if (sellerGstin && buyerGstin && sellerGstin.length >= 2 && buyerGstin.length >= 2) {
    if (sellerGstin.substring(0, 2) === buyerGstin.substring(0, 2)) {
      gstType = 'intra';
    } else {
      gstType = 'inter';
    }
  }
  return (
    <tr key={idx}>
                    <td className="px-2 py-1">
                      <div className="relative">
                        <input
                          type="text"
                          list={`items-list-${idx}`}
                          className="border border-gray-300 rounded-md p-1 w-48 relative"
                          style={{ zIndex: 1 }}
                          placeholder={t('selectItem')}
                          value={
                            itemSearchTextByRow[idx] !== undefined
                              ? itemSearchTextByRow[idx]
                              : (getItemDisplayName(findItemInMaster(row.item, items)) || row.itemName || '')
                          }
                          onChange={(e) => {
                            const text = e.target.value;
                            setItemSearchTextByRow(prev => ({ ...prev, [idx]: text }));
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              return;
                            }
                            
                            // Exact match only while typing (avoid wrong prefix binds)
                            const match = matchItemBySearchText(text, items, 'exact');
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: getItemDisplayName(match) }));
                            }
                          }}
                          onBlur={(e) => {
                            const text = e.target.value;
                            
                            // If the field is empty, clear the item selection
                            if (!text.trim()) {
                              handleRowChange(idx, 'item', '');
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: '' }));
                              return;
                            }
                            
                            // Prefer exact; only auto-bind unique prefix matches
                            const match = matchItemBySearchText(text, items, 'prefix');
                            if (match) {
                              handleRowChange(idx, 'item', match.id);
                              setItemSearchTextByRow(prev => ({ ...prev, [idx]: getItemDisplayName(match) }));
                            } else if (!row.item) {
                              // Keep typed text but warn that item is not linked yet
                              warning('Item name not found in master. Pick an item from the list before saving.', {
                                title: 'Item not linked',
                                duration: 4000,
                              });
                            }
                          }}
                        />
                        <datalist id={`items-list-${idx}`}>
                          {filterItemsForPicker(items, itemSearchTextByRow[idx] || '', 500).map(it => (
                            <option key={it.id} value={getItemDisplayName(it)} />
                          ))}
                        </datalist>
                        {(() => {
                          const measureSummary = formatLineMeasurement(row);
                          return measureSummary ? (
                            <span className="text-xs text-slate-500 mt-1 block max-w-[12rem] truncate" title={measureSummary}>
                              {measureSummary}
                            </span>
                          ) : null;
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top min-w-[12rem] max-w-[18rem]">
                      <div className="relative min-w-0">
                        <div className="flex flex-wrap gap-2 mb-2 min-h-[28px] max-w-full">
                          {(row.tags || []).map((tag, tagIdx) => (
                            <span
                              key={tagIdx}
                              className="inline-flex items-center max-w-full min-h-[32px] sm:min-h-[28px] px-2.5 py-1.5 rounded-md text-sm font-medium bg-blue-100 text-blue-900 cursor-pointer hover:bg-blue-200 border border-blue-200/60 touch-manipulation"
                              onClick={() => openServiceEditor(idx, tag)}
                              title="Optional: vendor and cost (full tracking in Service Ops)"
                            >
                              <span className="truncate">{getTagDisplayText(row, tag)}</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveTag(idx, tagIdx);
                                }}
                                className="ml-1.5 shrink-0 min-w-[1.25rem] text-center text-blue-700 hover:text-blue-900 text-lg leading-none"
                                aria-label="Remove tag"
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                        <div
                          className="relative min-w-0"
                          data-tag-picker-container="true"
                          ref={(el) => {
                            if (el) tagPickerContainerRefs.current[idx] = el;
                            else delete tagPickerContainerRefs.current[idx];
                          }}
                        >
                          <button
                            type="button"
                            onClick={(e) => toggleTagPicker(idx, e.currentTarget)}
                            className="text-sm text-blue-700 hover:text-blue-900 border border-blue-300 rounded-lg px-3 py-2 bg-blue-50 min-h-[40px] sm:min-h-[36px] touch-manipulation w-full sm:w-auto text-left sm:text-center font-medium shadow-sm"
                          >
                            + Add tag
                          </button>
                          {showTagPicker[idx] && (
                            tagPickerPosition[idx] && createPortal(
                              <div
                                data-tag-picker-container="true"
                                className="fixed z-[99999] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden touch-manipulation ring-1 ring-black/5"
                                style={{
                                  top: `${tagPickerPosition[idx]?.top}px`,
                                  left: `${tagPickerPosition[idx]?.left}px`,
                                  width: `${tagPickerPosition[idx]?.width}px`,
                                  maxHeight: `${tagPickerPosition[idx]?.maxHeight ?? 720}px`,
                                }}
                              >
                                {renderTagPickerDropdown(idx)}
                              </div>,
                              document.body
                            )
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-col">
                        <input
                          type="text"
                          value={row.qtyExpr || ''}
                          onChange={e => handleRowChange(idx, 'qtyExpr', e.target.value)}
                          placeholder="e.g. 5x3x2 or 10*2"
                          className="border border-gray-300 rounded-md p-1 w-40"
                        />
                        {row.qtyDisplay && (
                          <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                        )}
                        {/* Show original quantity for returns/notes */}
                        {(docType === 'salesReturn' || docType === 'creditNote' || docType === 'debitNote') && row.originalQty && (
                          <span className="text-xs text-blue-600 mt-1 font-medium">
                            Original: {row.originalQtyDisplay || row.originalQtyExpr || row.originalQty} {row.unit ? `(${row.unit})` : ''}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-col">
                        <input type="number" value={row.qty || ''} min={0} readOnly
                          className="border border-gray-300 rounded-md p-1 w-16 bg-gray-100" />
                        {/* Show original quantity badge for partial returns */}
                        {(docType === 'salesReturn' || docType === 'creditNote' || docType === 'debitNote') && row.originalQty && parseFloat(row.qty || 0) < parseFloat(row.originalQty || 0) && (
                          <span className="text-xs text-orange-600 mt-1 font-semibold" title="Partial Return">
                            ⚠ Partial
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" value={row.rate || ''} min={0} onChange={e => handleRowChange(idx, 'rate', e.target.value)}
                        className="border border-gray-300 rounded-md p-1 w-16" />
                    </td>
                    {/* Line Discount controls */}
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1">
                        <input
                          type="number"
                          min={0}
                          value={row.lineDiscountValue || ''}
                          onChange={e => handleRowChange(idx, 'lineDiscountValue', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-20"
                          placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                        />
                        <select
                          value={row.lineDiscountType || 'amount'}
                          onChange={e => handleRowChange(idx, 'lineDiscountType', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 relative z-10 w-12 text-center appearance-none cursor-pointer bg-white"
                          style={{
                            backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 4px center',
                            backgroundSize: '12px',
                            paddingRight: '20px'
                          }}
                        >
                          <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                          <option value="percent">%</option>
                        </select>
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <input type="number" value={row.amount || ''} min={0} readOnly
                        className="border border-gray-300 rounded-md p-1 w-20 bg-gray-100" />
                    </td>
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.sgst || ''} min={0} onChange={e => handleRowChange(idx, 'sgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                        {gstType === 'intra' && <div className="text-xs text-green-600">Auto: Intra-state (SGST)</div>}
                        {gstType === 'inter' && <div className="text-xs text-gray-400">(Not used for Inter-state)</div>}
                      </td>
                    )}
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.cgst || ''} min={0} onChange={e => handleRowChange(idx, 'cgst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                        {gstType === 'intra' && <div className="text-xs text-green-600">Auto: Intra-state (CGST)</div>}
                        {gstType === 'inter' && <div className="text-xs text-gray-400">(Not used for Inter-state)</div>}
                      </td>
                    )}
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.igst || ''} min={0} onChange={e => handleRowChange(idx, 'igst', e.target.value)}
                          className="border border-gray-300 rounded-md p-1 w-14" />
                        {gstType === 'inter' && <div className="text-xs text-blue-600">Auto: Inter-state (IGST)</div>}
                        {gstType === 'intra' && <div className="text-xs text-gray-400">(Not used for Intra-state)</div>}
                      </td>
                    )}
                    {company.gstinType === 'Regular' && (
                      <td className="px-2 py-1">
                        <input type="number" value={row.total || ''} min={0} readOnly
                          className="border border-gray-300 rounded-md p-1 w-20 bg-green-50 font-semibold" />
                      </td>
                    )}
                    <td className="px-2 py-1">
                      <button type="button" onClick={() => removeRow(idx)} className="text-red-600 font-bold px-2 py-1 rounded hover:bg-red-100">X</button>
                                            </td>
                                        </tr>
                                    );
                 })}
                 {/* Removed bill-level discount row in table; discount handled per line */}
                                </tbody>
                            </table>
          </div>
          
          {/* Mobile Card View - Carousel Style (same UX as Purchases) */}
          <div className="md:hidden mb-5">
            <div
              className="relative bg-gradient-to-b from-blue-50/40 to-white border border-blue-100 rounded-xl p-4 shadow-sm transition-all duration-200 ease-in-out"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div className="text-center text-[11px] text-slate-500 mb-3">
                Swipe left/right to navigate between items
              </div>

              <div className="flex justify-between items-center mb-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={goToPreviousItem}
                    disabled={currentMobileItemIndex === 0}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm touch-manipulation"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <h4 className="text-sm font-semibold text-slate-800 bg-white px-2.5 py-1 rounded-full border border-slate-200">
                    Item #{currentMobileItemIndex + 1}
                  </h4>
                  <button
                    type="button"
                    onClick={goToNextItem}
                    disabled={currentMobileItemIndex === rows.length - 1}
                    className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm touch-manipulation"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => removeRow(currentMobileItemIndex)}
                  className="text-rose-600 hover:text-rose-700 text-lg bg-rose-50 hover:bg-rose-100 rounded-lg min-h-[44px] min-w-[44px] flex items-center justify-center transition-colors touch-manipulation"
                  aria-label="Remove item"
                >
                  🗑️
                </button>
              </div>

              {rows[currentMobileItemIndex] && (() => {
                const row = rows[currentMobileItemIndex];
                const index = currentMobileItemIndex;
                return (
                <div key={currentMobileItemIndex}>

                {/* Item Selection — custom picker (avoids native dark datalist on mobile) */}
                <div className="mb-3 relative z-30" ref={mobileItemPickerRef}>
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Item</label>
                  {(() => {
                    const searchQ = itemSearchTextByRow[index] || '';
                    const mobileFilteredItems = filterItemsForPicker(items, searchQ, 120).slice(0, 120);
                    return (
                      <>
                        <div className="relative">
                          <input
                            type="text"
                            autoComplete="off"
                            value={itemSearchTextByRow[index] || ''}
                            onChange={(e) => {
                              const text = e.target.value;
                              setItemSearchTextByRow((prev) => ({ ...prev, [index]: text }));
                              setMobileItemPickerOpen(true);
                              if (!text.trim()) {
                                handleRowChange(currentMobileItemIndex, 'item', '');
                                return;
                              }
                              const match = matchItemBySearchText(text, items, 'exact');
                              if (match) {
                                handleRowChange(currentMobileItemIndex, 'item', match.id);
                                setItemSearchTextByRow((prev) => ({
                                  ...prev,
                                  [currentMobileItemIndex]: getItemDisplayName(match),
                                }));
                              }
                            }}
                            onFocus={() => setMobileItemPickerOpen(true)}
                            onBlur={(e) => {
                              const text = e.target.value;
                              if (!text.trim()) {
                                handleRowChange(currentMobileItemIndex, 'item', '');
                                setItemSearchTextByRow((prev) => ({ ...prev, [currentMobileItemIndex]: '' }));
                                return;
                              }
                              const match = matchItemBySearchText(text, items, 'prefix');
                              if (match) {
                                handleRowChange(currentMobileItemIndex, 'item', match.id);
                                setItemSearchTextByRow((prev) => ({
                                  ...prev,
                                  [currentMobileItemIndex]: getItemDisplayName(match),
                                }));
                              }
                            }}
                            className="w-full p-2.5 pr-12 border border-slate-300 rounded-xl text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                            placeholder="Search item..."
                          />
                          <button
                            type="button"
                            tabIndex={-1}
                            onClick={() => setMobileItemPickerOpen((o) => !o)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg text-slate-500 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                            aria-label={mobileItemPickerOpen ? 'Close item list' : 'Open item list'}
                          >
                            <ChevronDown
                              className={`w-4 h-4 transition-transform duration-200 ${mobileItemPickerOpen ? 'rotate-180' : ''}`}
                              aria-hidden
                            />
                          </button>
                        </div>
                        {mobileItemPickerOpen && (
                          <div className="absolute left-0 right-0 mt-1.5 max-h-60 overflow-y-auto rounded-xl border border-slate-200/90 bg-white shadow-xl shadow-slate-300/40 ring-1 ring-slate-100 py-1 overscroll-contain">
                            {mobileFilteredItems.length === 0 ? (
                              <div className="px-3 py-4 text-center text-sm text-slate-500">No matching items</div>
                            ) : (
                              mobileFilteredItems.map((it) => {
                                const displayName = it.itemName || it.name || 'Item';
                                return (
                                  <button
                                    key={it.id}
                                    type="button"
                                    onMouseDown={(ev) => ev.preventDefault()}
                                    onClick={() => {
                                      handleRowChange(currentMobileItemIndex, 'item', it.id);
                                      setItemSearchTextByRow((prev) => ({
                                        ...prev,
                                        [currentMobileItemIndex]: displayName,
                                      }));
                                      setMobileItemPickerOpen(false);
                                    }}
                                    className="w-full text-left px-3 py-2.5 hover:bg-sky-50 active:bg-sky-100/80 border-b border-slate-100 last:border-0 transition-colors"
                                  >
                                    <span className="block font-semibold text-slate-900 text-sm leading-snug truncate">
                                      {displayName}
                                    </span>
                                  </button>
                                );
                              })
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Tags Section (Sales-only) */}
                <div className="mb-4 min-w-0">
                  <label className="block text-xs font-semibold text-slate-700 mb-1.5">Service tags</label>
                  <p className="text-[11px] text-slate-500 mb-2 leading-snug">Tap a tag for vendor &amp; costs. Add opens the same picker as on desktop.</p>
                  <div className="flex flex-wrap gap-2 mb-2 min-h-[32px] max-w-full">
                    {(row.tags || []).map((tag, tagIdx) => (
                      <span
                        key={tagIdx}
                        className="inline-flex items-center max-w-full min-h-[36px] px-2.5 py-1.5 rounded-md text-sm font-medium bg-blue-100 text-blue-900 cursor-pointer hover:bg-blue-200 border border-blue-200/60 touch-manipulation"
                        onClick={() => openServiceEditor(index, tag)}
                        title="Tap to edit vendor, costs & status"
                      >
                        <span className="truncate">{getTagDisplayText(row, tag)}</span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveTag(index, tagIdx);
                          }}
                          className="ml-1.5 shrink-0 min-w-[1.25rem] text-center text-blue-700 hover:text-blue-900 text-lg leading-none"
                          aria-label="Remove tag"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div
                    className="relative min-w-0"
                    data-tag-picker-container="true"
                    ref={(el) => {
                      if (el) tagPickerContainerRefs.current[index] = el;
                      else delete tagPickerContainerRefs.current[index];
                    }}
                  >
                    <button
                      type="button"
                      onClick={(e) => toggleTagPicker(index, e.currentTarget)}
                      className="text-sm text-blue-700 hover:text-blue-900 border border-blue-300 rounded-lg px-3 py-2.5 bg-blue-50 w-full min-h-[44px] touch-manipulation active:bg-blue-100 font-medium shadow-sm"
                    >
                      + Add tag
                    </button>
                    {showTagPicker[index] && (
                      tagPickerPosition[index] && createPortal(
                        <div
                          data-tag-picker-container="true"
                          className="fixed z-[99999] bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden touch-manipulation ring-1 ring-black/5"
                          style={{
                            top: `${tagPickerPosition[index]?.top}px`,
                            left: `${tagPickerPosition[index]?.left}px`,
                            width: `${tagPickerPosition[index]?.width}px`,
                            maxHeight: `${tagPickerPosition[index]?.maxHeight ?? 720}px`,
                          }}
                        >
                          {renderTagPickerDropdown(index)}
                        </div>,
                        document.body
                      )
                    )}
                  </div>
                </div>

                {/* Measurement / Numbers Row */}
                <div className="mb-3">
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Measurement / Numbers</label>
                  <div className="flex flex-col">
                    <input
                      type="text"
                      value={row.qtyExpr || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qtyExpr', e.target.value)}
                      className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                      placeholder="e.g. 5x3x3, 4x5, 50"
                    />
                    {row.qtyDisplay && (
                      <span className="text-xs text-gray-500 mt-1">= {row.qty} ({row.qtyDisplay})</span>
                    )}
                    {!row.qtyDisplay && formatLineMeasurement(row) && (
                      <span className="text-xs text-indigo-700 mt-1 font-medium">{formatLineMeasurement(row)}</span>
                    )}
                  </div>
                </div>

                {/* Quantity and Rate Row */}
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Quantity</label>
                    <input
                      type="number"
                      value={row.qty || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'qty', e.target.value)}
                      className="w-full p-2.5 border border-slate-200 rounded-lg text-sm bg-slate-100"
                      placeholder="0"
                      min="0"
                      step="0.01"
                      readOnly
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Rate</label>
                    <input
                      type="number"
                      value={row.rate || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'rate', e.target.value)}
                      className="w-full p-2.5 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>

                {/* Discount Row */}
                <div className="mb-3">
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Discount</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      value={row.lineDiscountValue || ''}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountValue', e.target.value)}
                      className="flex-1 p-2.5 border border-slate-300 rounded-lg text-sm bg-white shadow-sm focus:ring-2 focus:ring-blue-100 focus:border-blue-400"
                      placeholder={row.lineDiscountType === 'percent' ? 'Percent' : 'Amount'}
                    />
                    <select
                      value={row.lineDiscountType || 'amount'}
                      onChange={(e) => handleRowChange(currentMobileItemIndex, 'lineDiscountType', e.target.value)}
                      className="border border-slate-300 rounded-lg text-sm p-2.5 w-20 relative z-10 appearance-none cursor-pointer bg-white text-center shadow-sm"
                      style={{
                        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6,9 12,15 18,9'%3e%3c/polyline%3e%3c/svg%3e")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 6px center',
                        backgroundSize: '14px',
                        paddingRight: '24px'
                      }}
                    >
                      <option value="amount">{getCurrencySymbol(companyCurrency)}</option>
                      <option value="percent">%</option>
                    </select>
                  </div>
                </div>

                {/* Amount Row */}
                <div className="mb-3">
                  <label className="block text-[11px] font-semibold text-slate-600 mb-1 uppercase tracking-wide">Amount</label>
                  <div className="w-full p-2.5 bg-slate-100 border border-slate-200 rounded-lg text-sm font-semibold text-blue-700">
                    {formatCurrency(parseFloat(row.amount) || 0, companyCurrency)}
                  </div>
                </div>

                {/* GST Row (if applicable) */}
                {company.gstinType === 'Regular' && (
                  <div className="grid grid-cols-3 gap-2 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">SGST</label>
                      <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                        {formatCurrency(parseFloat(row.sgst) || 0, companyCurrency)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">CGST</label>
                      <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                        {formatCurrency(parseFloat(row.cgst) || 0, companyCurrency)}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">IGST</label>
                      <div className="w-full p-2 bg-gray-100 border border-gray-300 rounded text-xs text-center">
                        {formatCurrency(parseFloat(row.igst) || 0, companyCurrency)}
                      </div>
                    </div>
                  </div>
                )}

                {/* Total Row */}
                <div className="bg-gradient-to-r from-blue-50 to-orange-50 p-3 rounded-lg border border-blue-100 shadow-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-slate-700">Total:</span>
                    <span className="text-lg font-bold text-blue-700">
                      {formatCurrency(parseFloat(row.total) || parseFloat(row.amount) || 0, companyCurrency)}
                    </span>
                  </div>
                </div>
              </div>
                );
              })()}

              {/* Item Numbers Pagination */}
              <div className="mt-4 flex justify-center">
                <div className="flex gap-2 flex-wrap">
                  {rows.map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => goToItem(index)}
                      className={`w-8 h-8 rounded-full text-sm font-semibold transition-colors ${
                        index === currentMobileItemIndex
                          ? 'bg-blue-600 text-white shadow-sm'
                          : 'bg-slate-200 text-slate-600 hover:bg-slate-300'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:justify-between items-start md:items-center mb-6">
            <div className="flex flex-col md:flex-row gap-2 mb-4 md:mb-0">
              <button onClick={addRow} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg shadow-sm">Add Item Row</button>
              <button onClick={() => setShowAddItemModal(true)} className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2.5 px-4 rounded-lg shadow-sm">Add New Item</button>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 w-full md:w-80 max-w-full">
              <div className="flex justify-between mb-1"><span>Subtotal (Excl. GST):</span><span>{formatCurrency(rows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0), companyCurrency)}</span></div>
              <div className="flex justify-between mb-1"><span>Discount:</span><span>-{formatCurrency(roundTo2Decimals(discount), companyCurrency)}</span></div>
              <div className="flex justify-between mb-1 font-semibold"><span>Net Subtotal:</span><span>{formatCurrency(discountedSubtotal, companyCurrency)}</span></div>
              <div className="flex justify-between mb-1"><span>Total SGST:</span><span>{formatCurrency(totalSGST, companyCurrency)}</span></div>
              <div className="flex justify-between mb-1"><span>Total CGST:</span><span>{formatCurrency(totalCGST, companyCurrency)}</span></div>
              <div className="flex justify-between mb-1"><span>Total IGST:</span><span>{formatCurrency(totalIGST, companyCurrency)}</span></div>
              <div className="flex justify-between mb-1"><span>Round Off:</span><span>{roundOffAmount >= 0 ? '+' : ''}{formatCurrency(roundOffAmount, companyCurrency)}</span></div>
              <div className="flex justify-between mt-2 font-bold text-lg"><span>Grand Total (Incl. GST):</span><span className="text-blue-700">{formatCurrency(grandTotal, companyCurrency)}</span></div>
            </div>
          </div>

          {/* Sub parties + Manage tags — directly above Add Note (same layout as SalesDev) */}
          <div className="mb-3 print:hidden">
            <div className="flex flex-col gap-2">
              <div className="flex gap-2 sm:gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={openSubPartyModal}
                  title="Split invoice total across sub parties and assign lines"
                  className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 active:bg-indigo-200 border border-indigo-200 rounded-md transition-colors touch-manipulation"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  Sub parties
                  {subPartyBreakdown.length > 0 && (
                    <span className="text-xs font-normal text-indigo-600 tabular-nums">({subPartyBreakdown.length})</span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/service-tags')}
                  title="Manage service tags, default rates, and vendor assignments"
                  className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-amber-800 bg-amber-50 hover:bg-amber-100 active:bg-amber-200 border border-amber-200 rounded-md transition-colors touch-manipulation"
                >
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  Service tags
                </button>
              </div>
              {subPartyBreakdown.length > 0 && (
                <div className={`text-xs sm:text-sm ${subPartyAppliedMatches ? 'text-green-700' : 'text-amber-700'}`}>
                  Sum {formatCurrency(subPartyAppliedSum, companyCurrency)} → invoice total{' '}
                  {formatCurrency(grandTotal, companyCurrency)}
                  {subPartyAppliedMatches ? ' ✓' : ' — adjust in modal to match'}
                </div>
              )}
            </div>
          </div>

          {/* Add Note, E-way Bill, Service Ops */}
          <div className="mb-6 print:hidden">
            <div className="flex gap-2 sm:gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => setShowNotesModal(true)}
                className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 active:bg-blue-200 border border-blue-200 rounded-md transition-colors touch-manipulation"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Note
              </button>
              <button
                type="button"
                onClick={() => setShowEwayBillModal(true)}
                className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-green-600 bg-green-50 hover:bg-green-100 active:bg-green-200 border border-green-200 rounded-md transition-colors touch-manipulation"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add E-way Bill
              </button>
              <button
                type="button"
                onClick={openServiceOperations}
                className="flex flex-1 min-w-[140px] sm:flex-initial items-center justify-center gap-2 px-3 sm:px-4 py-2.5 sm:py-2 text-sm font-medium text-purple-600 bg-purple-50 hover:bg-purple-100 active:bg-purple-200 border border-purple-200 rounded-md transition-colors touch-manipulation"
              >
                <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-6m3 6V7m3 10v-4M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                Service Ops
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-4 md:mt-4 sticky md:static bottom-[calc(var(--navigation-bar-height,env(safe-area-inset-bottom,0px))+8px)] z-30 bg-white/95 backdrop-blur-sm border-t border-gray-200 md:border-0 rounded-t-xl md:rounded-none p-3 md:p-0 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] md:shadow-none md:bg-transparent md:backdrop-blur-0 -mx-4 sm:-mx-6 px-4 sm:px-6 md:mx-0">
            <button
              id="save-invoice-button"
              onClick={handleSaveInvoice}
              disabled={isSavingInvoice}
              className="flex-1 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0 text-white font-semibold py-2.5 px-4 rounded-xl shadow-md transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] print:hidden"
            >
              {isSavingInvoice
                ? 'Saving…'
                : editingBillId
                  ? 'Update Invoice'
                  : 'Save Complete Invoice'}
            </button>
            <button
              onClick={resetFormToInitialState}
              className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white font-semibold py-2.5 px-4 rounded-xl shadow-md transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] print:hidden"
            >
              Clear Data
            </button>
            {editingBillId && (
              <button
                onClick={() => handleAddPayment(bills.find(b => b.id === editingBillId))}
                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-semibold py-2.5 px-4 rounded-xl shadow-md transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] print:hidden"
              >
                Add Payment
              </button>
            )}
            <button
              onClick={handleOpenFIFOPayment}
              className="flex-1 bg-gradient-to-r from-blue-600 to-orange-500 hover:from-blue-700 hover:to-orange-600 text-white font-semibold py-2.5 px-4 rounded-xl shadow-md transition-all duration-300 hover:-translate-y-0.5 active:scale-[0.98] print:hidden"
            >
              FIFO Payment
            </button>
          </div>
          </div> {/* Close Center Panel */}
          
                      {/* Right Panel - Party-wise Items (20%) */}
            <div className="hidden lg:block col-span-12 lg:col-span-2 bg-gradient-to-br from-green-50 via-green-50 to-green-100 border border-green-200 rounded-lg p-4 flex flex-col shadow-sm">
            <h3 className="text-lg font-bold text-green-800 mb-4 text-center bg-white bg-opacity-70 rounded-lg py-2 px-3 shadow-sm">Party Items</h3>

            {!selectedPartyForQuickItems ? (
              <div className="text-center text-gray-500 text-sm py-4">
                Select a party to see their items
              </div>
            ) : (
              <>
                            <div className="text-xs text-gray-600 mb-3 text-center">
              {parties.find(p => p.id === selectedPartyForQuickItems)?.firmName}
            </div>
                <div className={`grid grid-cols-${getResponsiveColumns()} gap-2 flex-1 overflow-y-auto`}>
                  {partyWiseItems.map((itemData) => {
                    const item = items.find(it => it.id === itemData.itemId);
                    if (!item) return null;
                    
                    return (
                      <button
                        key={itemData.itemId || itemData.id}
                        onClick={() => handleQuickItemClick(itemData, true)}
                        className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded-md p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                        title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times with this party`}
                      >
                        <div className="font-semibold text-sm text-green-900 truncate">
                          {itemData.itemName || itemData.name || 'Unknown Item'}
                        </div>
                        <div className="text-xs text-green-700 font-medium">
                          {formatCurrency(itemData.lastRate || 0, companyCurrency)} × {itemData.lastQty || 0}
                        </div>
                        <div className="text-xs text-green-600">
                          Used {itemData.count} times
                        </div>
                      </button>
                    );
                  })}
                </div>
                {partyWiseItems.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-4">
                    No items used with this party
                  </div>
                )}
              </>
            )}
          </div>
        </div> {/* Close Quick Items Grid */}
        
        {/* Quick Items Modal - Compact */}
        {showQuickItemsModal && createPortal(
          <div
            className="fixed inset-0 z-[10000] bg-slate-900/35 backdrop-blur-[1px] flex items-center justify-center p-3 sm:p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowQuickItemsModal(false);
              }
            }}
          >
            <div
              className="relative bg-white rounded-2xl border border-blue-100 shadow-2xl w-full max-w-3xl max-h-[88vh] overflow-hidden"
              style={quickItemModalPosition}
            >
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <h3 className="text-base sm:text-lg font-semibold text-gray-800">Quick Items</h3>
                <button
                  onClick={() => setShowQuickItemsModal(false)}
                  className="p-1 hover:bg-gray-200 rounded-full transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-3 overflow-y-auto max-h-[calc(85vh-60px)]">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Most Used Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Most Used ({mostUsedItems.length})
                    </h4>
                    <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                      gridTemplateColumns: 
                        window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                        window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                        '1fr'
                    }}>
                      {mostUsedItems.map((itemData) => {
                        const item = items.find(it => it.id === itemData.id);
                        if (!item) return null;
                        
                        return (
                          <button
                            key={itemData.itemId || itemData.id}
                            onClick={() => {
                              handleQuickItemClick(itemData);
                              setShowQuickItemsModal(false);
                            }}
                            className="bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 border border-blue-200 rounded p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                            title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                          >
                            <div className="font-medium text-xs text-blue-900 leading-tight break-words">
                              {itemData.itemName || itemData.name || 'Unknown Item'}
                            </div>
                            <div className="text-xs text-blue-700 font-medium mt-0.5">
                              {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                            </div>
                            <div className="text-xs text-blue-600">
                              {itemData.count}×
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {mostUsedItems.length === 0 && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items used yet
                      </div>
                    )}
                  </div>

                  {/* Party-wise Items */}
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 pb-1 border-b border-gray-200">
                      Party Items ({partyWiseItems.length})
                    </h4>
                    <AppSelect
                      value={selectedPartyForQuickItems}
                      onChange={(e) => setSelectedPartyForQuickItems(e.target.value)}
                      placeholder="Select Party"
                      aria-label="Party for quick items"
                      sheetTitle="Select party"
                      options={[
                        { value: '', label: 'Select Party' },
                        ...Array.from(new Set(bills.map((bill) => bill.party).filter(Boolean))).map((partyId) => {
                          const partyName = parties.find((p) => p.id === partyId)?.firmName || partyId;
                          return { value: partyId, label: partyName };
                        }),
                      ]}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} text-xs mb-2 rounded p-1.5`}
                    />
                    
                    {!selectedPartyForQuickItems ? (
                      <div className="text-center text-gray-500 text-xs py-3">
                        Select a party to see their items
                      </div>
                    ) : (
                      <div className="grid gap-1.5 max-h-80 overflow-y-auto" style={{
                        gridTemplateColumns: 
                          window.innerWidth >= 360 ? 'repeat(3, 1fr)' : 
                          window.innerWidth >= 144 ? 'repeat(2, 1fr)' : 
                          '1fr'
                      }}>
                        {partyWiseItems.map((itemData) => {
                          const item = items.find(it => it.id === itemData.itemId);
                          if (!item) return null;
                          
                          return (
                            <button
                              key={itemData.itemId || itemData.id}
                              onClick={() => {
                                handleQuickItemClick(itemData);
                                setShowQuickItemsModal(false);
                              }}
                              className="bg-gradient-to-r from-green-50 to-green-100 hover:from-green-100 hover:to-green-200 border border-green-200 rounded p-1.5 text-left transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-0.5 active:scale-[0.98]"
                              title={`${itemData.itemName || itemData.name || 'Unknown Item'} - Used ${itemData.count} times`}
                            >
                              <div className="font-medium text-xs text-green-900 leading-tight break-words">
                                {itemData.itemName || itemData.name || 'Unknown Item'}
                              </div>
                              <div className="text-xs text-green-700 font-medium mt-0.5">
                                {formatCurrency(itemData.lastRate || 0, companyCurrency)}×{itemData.lastQty || 1}
                              </div>
                              <div className="text-xs text-green-600">
                                {itemData.count}×
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {partyWiseItems.length === 0 && selectedPartyForQuickItems && (
                      <div className="text-center text-gray-500 text-xs py-3">
                        No items found for this party
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
        
        {/* Mobile Edit Modal */}
        {showMobileEditModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-xl max-w-md w-full max-h-[90dvh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white z-10">
                <h3 className="text-lg font-semibold text-gray-800">Edit Details</h3>
                <button
                  onClick={() => setShowMobileEditModal(false)}
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center hover:bg-gray-200 rounded-full transition-colors touch-manipulation"
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{numberLabel}</label>
                  <input 
                    type="text" 
                    value={invoiceNumber} 
                    onChange={e => setInvoiceNumber(e.target.value)}
                    readOnly={!!editingBillId}
                    className={`w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm ${editingBillId ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    title={editingBillId ? 'Invoice number cannot be changed when editing' : ''}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Date</label>
                  <input 
                    type="date" 
                    value={invoiceDate} 
                    onChange={e => setInvoiceDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm" 
                  />
                </div>
                {company.gstinType === 'Regular' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST Calculation</label>
                    <AppSelect
                      value={gstCalculationType}
                      onChange={(e) => setGstCalculationType(e.target.value)}
                      aria-label="GST calculation"
                      sheetTitle="GST calculation"
                      options={[
                        { value: 'excluding', label: 'Rate Excluding GST' },
                        { value: 'including', label: 'Rate Including GST' },
                      ]}
                      triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-2 text-sm`}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {gstCalculationType === 'excluding' 
                        ? 'GST will be added to the rate' 
                        : 'GST is included in the rate'}
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Party (Buyer)</label>
                  <div className="relative" ref={partyDropdownRef}>
                    <input
                      type="text"
                      value={partySearchText}
                      onChange={(e) => {
                        setPartySearchText(e.target.value);
                        if (e.target.value === '') {
                          setParty('');
                          setSelectedPartyForQuickItems('');
                        }
                      }}
                      onFocus={() => setShowPartyDropdown(true)}
                      className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                      placeholder="Search or type new party name..."
                    />
                    {showPartyDropdown && (
                      <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-32 overflow-y-auto">
                        {filteredParties.map(p => (
                          <button
                            key={p.id}
                            onClick={() => {
                              setParty(p.id);
                              setSelectedPartyForQuickItems(p.id);
                              setPartySearchText(p.firmName);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                          >
                            {p.firmName}
                          </button>
                        ))}
                        {partySearchText && !filteredParties.some(p => p.firmName.toLowerCase() === partySearchText.toLowerCase()) && (
                          <button
                            onClick={() => {
                              setNewPartyName(partySearchText);
                              setShowAddPartyModal(true);
                              setShowPartyDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 text-sm text-blue-600 border-t border-gray-200"
                          >
                            + Add "{partySearchText}" as new party
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex gap-2 pt-2 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-medium py-3 px-4 rounded-md text-sm touch-manipulation min-h-[44px]"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => setShowMobileEditModal(false)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md text-sm touch-manipulation min-h-[44px]"
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Success Modal - Desktop: over form, Mobile: after form */}
        {showSuccessModal && (
          <>
            {/* Mobile View - Inline */}
            <div className="mt-6 w-full mx-auto lg:hidden">
              <div ref={successModalRef} className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 max-w-md w-full mx-4 sm:mx-auto relative">
                <div className="absolute top-2 right-2 flex items-center space-x-2">
                  <span className="text-xs text-gray-500">Press ESC to close</span>
                  <button 
                    onClick={() => setShowSuccessModal(false)} 
                    className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                  >
                    ×
                  </button>
                </div>
                
                <div className="text-center mb-4">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                    </svg>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                  </h3>
                  <p className="text-gray-600">What would you like to do next?</p>
                </div>
                
                <div className="flex flex-col gap-3">
                  <button
                    onClick={() => {
                      const bill = bills.find(b => b.id === savedBillId);
                      if (bill) {
                        const invoiceData = {
                          ...bill,
                          companyDetails: currentCompany || company,
                          partyDetails: parties.find(p => p.id === bill.party) || {},
                          items: items,
                          docType: docType
                        };
                        handleMobileInvoiceView(invoiceData);
                      }
                      setShowSuccessModal(false);
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                    </svg>
                    Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowSuccessModal(false);
                      resetFormToInitialState();
                      const prefixMap = { 
      invoice: 'INV', 
      challan: 'CHA', 
      quotation: 'QUO',
      salesReturn: 'SR',
      creditNote: 'CN',
      debitNote: 'DN'
    };
                      const prefix = prefixMap[docType] || 'INV';
                      const fy = getFinancialYear(new Date());
                      const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
                      const serials = bills
                        .filter(bill => (bill.number || '').startsWith(`${prefix}${fyShort}/`))
                        .map(bill => parseInt((bill.number || '').split('/')[1], 10))
                        .filter(n => !isNaN(n));
                      const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
                      setInvoiceNumber(`${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`);
                    }}
                    className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                  </button>
                  
                  <button
                    onClick={() => {
                      const bill = bills.find(b => b.id === savedBillId);
                      if (bill) {
                        handleAddPayment(bill);
                      }
                      setShowSuccessModal(false);
                    }}
                    className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                    </svg>
                    Add Payment
                  </button>
                  
                  <button
                    onClick={() => {
                      const bill = bills.find(b => b.id === savedBillId);
                      if (bill) {
                        handleEditBill(bill);
                      }
                      setShowSuccessModal(false);
                    }}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                  >
                    Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                  </button>
                </div>
              </div>
            </div>

            {/* Desktop View - Overlay */}
            <div className="hidden lg:fixed lg:inset-0 lg:flex lg:items-center lg:justify-center lg:z-50">
              <div ref={successModalRef} className="bg-white rounded-lg shadow-2xl border-2 border-gray-200 p-6 max-w-md w-full mx-auto relative">
              <div className="absolute top-2 right-2 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Press ESC to close</span>
                <button 
                  onClick={() => setShowSuccessModal(false)} 
                  className="text-gray-500 hover:text-gray-800 text-xl font-bold w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-100"
                >
                  ×
                </button>
              </div>
              
              <div className="text-center mb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Saved Successfully!
                </h3>
                <p className="text-gray-600">What would you like to do next?</p>
              </div>
              
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    const bill = bills.find(b => b.id === savedBillId);
                    if (bill) {
                      const invoiceData = {
                        ...bill,
                        companyDetails: currentCompany || company,
                        partyDetails: parties.find(p => p.id === bill.party) || {},
                        items: items,
                        docType: docType
                      };
                      handleMobileInvoiceView(invoiceData);
                    }
                    setShowSuccessModal(false);
                  }}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"></path>
                  </svg>
                  Print {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                </button>
                
                <button
                  onClick={() => {
                    setShowSuccessModal(false);
                    resetFormToInitialState();
                    const prefixMap = { 
      invoice: 'INV', 
      challan: 'CHA', 
      quotation: 'QUO',
      salesReturn: 'SR',
      creditNote: 'CN',
      debitNote: 'DN'
    };
                    const prefix = prefixMap[docType] || 'INV';
                    const fy = getFinancialYear(new Date());
                    const fyShort = fy.split('-').map(y => y.slice(-2)).join('-');
                    const serials = bills
                      .filter(bill => (bill.number || '').startsWith(`${prefix}${fyShort}/`))
                      .map(bill => parseInt((bill.number || '').split('/')[1], 10))
                      .filter(n => !isNaN(n));
                    const nextSerial = (serials.length ? Math.max(...serials) : 0) + 1;
                    setInvoiceNumber(`${prefix}${fyShort}/${nextSerial.toString().padStart(3, '0')}`);
                  }}
                  className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  Create New {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                </button>
                
                <button
                  onClick={() => {
                    const bill = bills.find(b => b.id === savedBillId);
                    if (bill) {
                      handleAddPayment(bill);
                    }
                    setShowSuccessModal(false);
                  }}
                  className="w-full bg-green-100 hover:bg-green-200 text-green-700 font-bold py-2 px-4 rounded-md flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                  </svg>
                  Add Payment
                </button>
                
                <button
                  onClick={() => {
                    const bill = bills.find(b => b.id === savedBillId);
                    if (bill) {
                      handleEditBill(bill);
                    }
                    setShowSuccessModal(false);
                  }}
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold py-2 px-4 rounded-md"
                >
                  Edit This {docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}
                </button>
              </div>
            </div>
          </div>
          </>
        )}
        
        {/* Sales Bill List Section - 100% Width Centered */}
        <div className="mt-10 w-full mx-auto">
            <div className="flex justify-between items-center mb-4 px-4">
              <h3 className="text-xl font-bold text-gray-800">Sales Bill List</h3>
              <div className="flex items-center space-x-4">
                {selectedForDeletion.size > 0 && (
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-600">
                      {selectedForDeletion.size} selected
                    </span>
                    <button
                      onClick={() => setShowBulkDeleteConfirm(true)}
                      disabled={isBulkDeleting}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 disabled:opacity-50"
                    >
                      {isBulkDeleting ? 'Deleting...' : 'Delete Selected'}
                    </button>
                  </div>
                )}
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-md transition duration-300 ease-in-out transform hover:scale-105"
                >
                  {showFilters ? 'Hide Table & Filters' : 'Show Table & Filters'}
                </button>
              </div>
            </div>
            
            {/* Filter Section and Bill List - Both controlled by showFilters */}
            {showFilters ? (
              <>
              <CompactFilterBar
                filters={[
                  {
                    key: 'search',
                    label: 'Search Bills & Parties',
                    type: 'text',
                    value: filterSearch,
                    placeholder: 'Search by bill number or party name...',
                    fullWidth: true
                  },
                  {
                    key: 'date',
                    label: 'Date',
                    type: 'text',
                    value: filterDate,
                    placeholder: 'DD/MM/YYYY or partial'
                  },
                  {
                    key: 'amount',
                    label: 'Amount',
                    type: 'text',
                    value: filterAmount,
                    placeholder: t('filterByAmount')
                  },
                  {
                    key: 'billNo',
                    label: 'Bill No.',
                    type: 'text',
                    value: filterBillNo,
                    placeholder: 'Filter by bill number'
                  },
                  {
                    key: 'party',
                    label: 'Party',
                    type: 'text',
                    value: filterParty,
                    placeholder: 'Filter by party name'
                  },
                  {
                    key: 'fromDate',
                    label: 'From Date',
                    type: 'date',
                    value: filterFromDate,
                    placeholder: 'Start date'
                  },
                  {
                    key: 'toDate',
                    label: 'To Date',
                    type: 'date',
                    value: filterToDate,
                    placeholder: 'End date'
                  }
                ]}
                onFilterChange={handleFilterChange}
                onClearFilters={clearFilters}
                showClearButton={true}
                inlineSearch={true}
                className="bg-gray-50"
              />
              <div className="mb-3 px-2 flex items-center justify-end">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={prioritizeCurrentFY}
                    onChange={(e) => setPrioritizeCurrentFY(e.target.checked)}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  Current FY first
                </label>
              </div>
            <div className="rounded-xl border border-gray-200 mb-4 w-full shadow-sm bg-white overflow-hidden">
              {/* Desktop Dropdown Form View */}
              <div className="hidden lg:block overflow-x-auto">
                {/* Header Row */}
                <div className="bg-slate-50 border-b border-gray-200 rounded-t-xl p-3 lg:p-4 sticky top-0 z-10">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-[32px_minmax(120px,1.2fr)_minmax(92px,0.9fr)_minmax(140px,1.5fr)_minmax(100px,1fr)_minmax(100px,1fr)_minmax(110px,1fr)] gap-2 lg:gap-4 items-center justify-items-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide min-w-0">
                      <div className="text-center w-8">
                        <input
                          type="checkbox"
                          checked={selectedForDeletion.size === bills.length && bills.length > 0}
                          onChange={handleSelectAllForDeletion}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </div>
                      <SortableHeader
                        columnKey="number"
                        label={numberLabel.split(' ')[0] + ' No.'}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600 min-w-0"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="invoiceDate"
                        label={t('date')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600 min-w-0"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="partyName"
                        label={t('party')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600 min-w-0"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="amount"
                        label={t('amount')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600 min-w-0"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="paid"
                        label={t('paid')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600 min-w-0"
                        as="div"
                      />
                      <SortableHeader
                        columnKey="outstanding"
                        label={t('outstanding')}
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="text-center truncate cursor-pointer hover:text-blue-600 min-w-0"
                        as="div"
                      />
                    </div>
                    <div className="ml-2 lg:ml-4 w-24 lg:w-28 flex-shrink-0 text-center text-[11px] font-semibold text-slate-500 uppercase tracking-wide">
                      Actions
                    </div>
                  </div>
                </div>
                
                {/* Dropdown Form Rows */}
                <div className="max-h-[600px] overflow-y-auto custom-scrollbar">
                  {pagination.currentData.map((bill, idx) => (
                    <DropdownFormRow
                      key={idx}
                      bill={bill}
                      docType={docType}
                      docTypeOptions={docTypeOptions}
                      companyCurrency={companyCurrency}
                      employees={employees}
                      onQuickEmployeeCashFlow={handleQuickEmployeeCashFlow}
                      currentCompany={currentCompany}
                      onView={handleViewBill}
                      onEdit={handleEditBill}
                      onDelete={handleDeleteBill}
                      onSelectForDeletion={handleDeleteSelection}
                      isSelectedForDeletion={selectedForDeletion.has(bill.id)}
                      onPrint={() => {
                                  const invoiceData = {
                            ...bill,
                            companyDetails: currentCompany || company,
                            partyDetails: parties.find(p => p.id === bill.party) || {},
                            items: items,
                                    docType: docType
                                  };
                                  handleMobileInvoiceView(invoiceData);
                      }}
                      onPayment={handleAddPayment}
                      onReceipts={handleViewPaymentReceipts}
                      receiptsCount={getBillPayments(bill).length}
                      receiptNumbers={getBillPayments(bill).map(payment => payment.receiptNumber)}
                      items={items}
                      index={idx}
                      isExpanded={expandedRows[idx] || false}
                      onToggle={toggleRowExpansion}
                      onConvertToInvoice={handleConvertToInvoice}
                    />
                  ))}
                </div>
              </div>
                        
                        {/* Mobile Card View with Collapsible Dropdown */}
                        <div className="lg:hidden overflow-x-hidden">
                            {pagination.currentData.map((bill, idx) => (
                                <SalesCard
                                    employees={employees}
                                    onQuickEmployeeCashFlow={handleQuickEmployeeCashFlow} 
                                    key={idx} 
                                    bill={bill}
                                    docType={docType}
                                    docTypeOptions={docTypeOptions}
                                    companyCurrency={companyCurrency}
                                    currentCompany={currentCompany}
                                    items={items}
                                    index={idx}
                                    isExpanded={expandedRows[idx]}
                                    onToggle={toggleRowExpansion}
                                    onView={handleViewBill}
                                    onEdit={handleEditBill}
                                    onDelete={handleDeleteBill}
                                    onPayment={handleAddPayment}
                                    onReceipts={handleViewPaymentReceipts}
                                    receiptsCount={getBillPayments(bill).length}
                                    receiptNumbers={getBillPayments(bill).map(payment => payment.receiptNumber)}
                                    onConvertToInvoice={handleConvertToInvoice}
                                    onPrint={() => {
                                        const invoiceData = {
                                            ...bill,
                                            companyDetails: currentCompany || company,
                                            partyDetails: parties.find(p => p.id === bill.party) || {},
                                            items: items,
                                            docType: docType
                                        };
                                        handleMobileInvoiceView(invoiceData);
                                    }}
                                />
                            ))}
                        </div>
                        
                                                {/* Pagination Controls */}
                        <PaginationControls {...pagination} />
                        </div>
              </>
            ) : (
              <div className="text-center text-gray-500 py-8">
                <p className="text-lg">Table and filters are hidden</p>
                <p className="text-sm">Click "Show Table & Filters" to view sales bills</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Bill Modal */}
      {showViewModal && viewBill && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
          <div className="bg-white rounded-t-2xl sm:rounded-lg shadow-lg p-4 sm:p-6 max-w-md w-full relative max-h-[92dvh] overflow-y-auto">
            <div className="absolute top-2 right-2 flex items-center space-x-2">
              <span className="text-xs text-gray-500 hidden sm:inline">Press ESC to close</span>
              <button onClick={() => setShowViewModal(false)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500 hover:text-gray-800 text-xl touch-manipulation" aria-label="Close">&times;</button>
            </div>
            <h3 className="text-xl font-bold mb-4 text-center">Sales Bill Summary</h3>
            <div className="mb-2 flex flex-col gap-1">
              <div><span className="font-semibold">{numberLabel}:</span> {viewBill.number}</div>
              <div><span className="font-semibold">Date:</span> {formatDateToCompanyFormat(viewBill.invoiceDate || viewBill.date, currentCompany)}</div>
              <div><span className="font-semibold">Party:</span> {(parties.find(p => p.id === viewBill.party)?.firmName) || viewBill.party || 'Unknown'}</div>
              <div><span className="font-semibold">Amount:</span> {typeof viewBill.amount === 'number' ? formatCurrency(viewBill.amount, companyCurrency) : viewBill.amount}</div>
              {viewBill.notes && <div><span className="font-semibold">Notes:</span> {viewBill.notes}</div>}
            </div>
            <div className="mt-4">
              <div className="font-semibold mb-1">Items Sold:</div>
              <ul className="list-disc list-inside text-sm">
                {(viewBill.rows || []).map((row, i) => {
                  const itemName = resolveBillLineItemName(row, items, '?');
                  return (
                    <li key={i}>{itemName} (Qty: {row.qty}, Rate: {formatCurrency(row.rate, companyCurrency)})</li>
                  );
                })}
              </ul>
            </div>
          </div>
                    </div>
                )}
             {/* Delete Confirmation Modal */}
       <ConfirmationModal section={MODAL_SECTIONS.SALES}
         isOpen={showDeleteConfirm}
         onClose={cancelDeleteBill}
         title={t('confirmDelete')}
         message="Are you sure you want to delete this sales bill?"
         onConfirm={confirmDeleteBill}
         confirmText={t('delete')}
         cancelText={t('cancel')}
         variant="danger"
       />
             {/* Invoice Preview Modal */}
       <PreviewModal section={MODAL_SECTIONS.SALES}
         isOpen={showInvoiceModal && invoiceBill}
         onClose={() => setShowInvoiceModal(false)}
         title={`${docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'} Preview`}
         maxWidth={BILL_PREVIEW_MODAL_MAX_WIDTH}
         maxHeight="max-h-[85vh] sm:max-h-[90vh]"
         showPrintButton={true}
         onPrint={handleInvoicePrint}
         showPdfButton={true}
         onPdf={handleInvoiceDownloadWithHelpers}
         extraActions={
           <>
             <StandardButton
               variant="secondary"
               onClick={handleInvoiceCopyImageToClipboard}
               disabled={!invoiceBill || copyInvoiceImageBusy}
               className="flex items-center gap-1.5"
               title={
                 Capacitor.isNativePlatform()
                   ? 'Share bill image (clipboard copy is not available on mobile)'
                   : 'Copy a high-resolution PNG to the clipboard (same layout as PDF export)'
               }
             >
               <ClipboardCopy className="w-4 h-4 shrink-0" aria-hidden />
               {copyInvoiceImageBusy
                 ? Capacitor.isNativePlatform()
                   ? 'Sharing…'
                   : 'Copying…'
                 : Capacitor.isNativePlatform()
                   ? 'Share image'
                   : 'Copy image'}
             </StandardButton>
             <SalesShareButton
               onExportPDF={handleInvoiceSharePdf}
               onExportImage={handleInvoiceShareImage}
               disabled={!invoiceBill}
             />
           </>
         }
         showZoomControls={true}
         zoom={invoiceZoom}
         onZoomIn={() => setInvoiceZoom(z => Math.min(3, z + 0.1))}
         onZoomOut={() => setInvoiceZoom(z => Math.max(0.5, z - 0.1))}
         onZoomReset={() => setInvoiceZoom(getBillPreviewInitialZoom())}
         zIndex="z-[10050]"
       >
         <div 
           className="w-full min-h-0 flex-1 overflow-y-auto overflow-x-hidden bg-gray-50 print:bg-white relative" 
           onWheel={handleInvoiceWheel}
         >
           <div className="absolute top-2 left-2 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md z-10 hidden sm:block">
             Hold Ctrl + Scroll to zoom
           </div>
           <div className={`${BILL_PREVIEW_INNER_CLASS} px-1 sm:px-2 py-2 w-full`}>
               <div 
                 ref={invoiceRef} 
                data-invoice-number={invoiceBill?.number || invoiceBill?.invoiceNumber || ''}
                data-invoice-id={invoiceBill?.id || ''}
                data-acctoo-bill-preview="true"
                 style={BILL_PREVIEW_SHELL_STYLE}
                 className={BILL_PREVIEW_SHELL_CLASS}
               >
                 {invoiceBill?.docType === 'challan' ? (
                   <ChallanTemplate
                     billData={{
                       ...invoiceBill,
                       challanNumber: invoiceBill?.number || invoiceBill?.challanNumber || '',
                       challanDate: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       items: getBillLineItems(invoiceBill).map((row) => enrichBillRowForPrint(row, items)),
                     }}
                     companyDetails={{
                       name: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered'
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{
                       name: company.bankName,
                       account: company.bankAccount,
                       ifsc: company.bankIfsc,
                       upi: company.upiId,
                       upiQrUrl: company.upiQrUrl,
                       paymentGatewayLink: company.paymentGatewayLink,
                       sealUrl: resolveCompanySealUrl(currentCompany, company),
                       signUrl: resolveCompanySignUrl(currentCompany, company),
                     }}
                     payments={invoiceBill?.payments || []}
                     pageSize={'a4'}
                     orientation={'portrait'}
                     previewMode={false}
                   />
                 ) : invoiceBill?.docType === 'salesReturn' ? (
                   <SalesReturnTemplate
                     billData={{
                       ...invoiceBill,
                       number: invoiceBill?.number || '',
                       invoiceDate: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       originalBillNumber: invoiceBill?.originalBillNumber || '',
                       returnReason: invoiceBill?.returnReason || '',
                       items: getBillLineItems(invoiceBill).map((row) => enrichBillRowForPrint(row, items)),
                     }}
                     companyDetails={{
                       name: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered'
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{}}
                   />
                 ) : invoiceBill?.docType === 'creditNote' ? (
                   <CreditNoteTemplate
                     billData={{
                       ...invoiceBill,
                       number: invoiceBill?.number || '',
                       invoiceDate: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       originalBillNumber: invoiceBill?.originalBillNumber || '',
                       notes: invoiceBill?.notes || '',
                       items: getBillLineItems(invoiceBill).map((row) => enrichBillRowForPrint(row, items)),
                     }}
                     companyDetails={{
                       name: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered'
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{}}
                   />
                 ) : invoiceBill?.docType === 'debitNote' ? (
                   <DebitNoteTemplate
                     billData={{
                       ...invoiceBill,
                       number: invoiceBill?.number || '',
                       invoiceDate: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       originalBillNumber: invoiceBill?.originalBillNumber || '',
                       notes: invoiceBill?.notes || '',
                       items: getBillLineItems(invoiceBill).map((row) => enrichBillRowForPrint(row, items)),
                     }}
                     companyDetails={{
                       name: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered'
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{}}
                   />
                 ) : invoiceBill?.docType === 'quotation' ? (
                   (() => {
                     const QuotationTemplateComponent = getQuotationTemplate();
                     return <QuotationTemplateComponent
                       billData={{
                         ...invoiceBill,
                         quotationNumber: invoiceBill?.number || invoiceBill?.quotationNumber || '',
                         date: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                         items: getBillLineItems(invoiceBill).map((row) => enrichBillRowForPrint(row, items)),
                       }}
                    companyDetails={{
                      firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                      logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                      address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                      city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                      pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                      state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                      gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                      contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                      email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                      gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered',
                      bankName: company?.bankName || currentCompany?.bankName || '',
                      bankAccount: company?.bankAccount || currentCompany?.bankAccount || '',
                      bankIfsc: company?.bankIfsc || currentCompany?.bankIfsc || '',
                      upiId: company?.upiId || currentCompany?.upiId || '',
                      upiQrUrl: company?.upiQrUrl || currentCompany?.upiQrUrl || '',
                      paymentGatewayLink: company?.paymentGatewayLink || currentCompany?.paymentGatewayLink || '',
                      sealUrl: resolveCompanySealUrl(currentCompany, company),
                      signUrl: resolveCompanySignUrl(currentCompany, company),
                      quotationTerms: company?.quotationTerms || currentCompany?.quotationTerms || currentCompany?.termsAndConditions?.quotationTerms || '',
                      footer: company?.footer || currentCompany?.footer || currentCompany?.termsAndConditions?.footer || ''
                    }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{
                       name: company.bankName,
                       account: company.bankAccount,
                       ifsc: company.bankIfsc,
                       upi: company.upiId,
                       upiQrUrl: company.upiQrUrl,
                       paymentGatewayLink: company.paymentGatewayLink,
                       sealUrl: resolveCompanySealUrl(currentCompany, company),
                       signUrl: resolveCompanySignUrl(currentCompany, company),
                     }}
                     payments={invoiceBill?.payments || []}
                     pageSize={'a4'}
                     orientation={'portrait'}
                     previewMode={false}
                     sharePreview={true}
                   />;
                   })()
                 ) : invoiceBill ? (
                   (() => {
                     const InvoiceTemplateComponent = getInvoiceTemplate();
                     return <InvoiceTemplateComponent
                       billData={{
                       ...invoiceBill,
                       invoiceNumber: invoiceBill?.number || invoiceBill?.invoiceNumber || '',
                       date: invoiceBill?.invoiceDate || invoiceBill?.date || '',
                       items: getBillLineItems(invoiceBill).map((row) => enrichBillRowForPrint(row, items)),
                     }}
                     companyDetails={{
                       name: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       firmName: currentCompany?.name || currentCompany?.firmName || company?.name || company?.firmName || '',
                       logoUrl: currentCompany?.logoUrl || currentCompany?.branding?.logoUrl || currentCompany?.details?.logoUrl || company?.logoUrl || company?.branding?.logoUrl || '',
                       address: currentCompany?.address || currentCompany?.details?.address || company?.address || '',
                       city: currentCompany?.city || currentCompany?.details?.city || company?.city || '',
                       pincode: currentCompany?.pincode || currentCompany?.details?.pincode || company?.pincode || '',
                       state: currentCompany?.state || currentCompany?.details?.state || company?.state || '',
                       gstin: currentCompany?.basicInfo?.gstNumber || currentCompany?.gstin || currentCompany?.gstNumber || currentCompany?.details?.gstin || company?.basicInfo?.gstNumber || company?.gstin || company?.gstNumber || '',
                       contactNumber: currentCompany?.basicInfo?.phone || currentCompany?.phone || currentCompany?.contactNumber || company?.basicInfo?.phone || company?.contactNumber || '',
                       email: currentCompany?.basicInfo?.email || currentCompany?.email || company?.basicInfo?.email || company?.email || '',
                       gstinType: currentCompany?.basicInfo?.gstType || currentCompany?.gstType || company?.basicInfo?.gstType || company?.gstType || 'Unregistered',
                       bankName: currentCompany?.bankName || company?.bankName || '',
                       bankAccount: currentCompany?.bankAccount || company?.bankAccount || '',
                       bankIfsc: currentCompany?.bankIfsc || company?.bankIfsc || '',
                       upiId: currentCompany?.upiId || company?.upiId || '',
                       upiQrUrl: currentCompany?.upiQrUrl || company?.upiQrUrl || '',
                       salesTerms: company?.salesTerms || currentCompany?.salesTerms || currentCompany?.termsAndConditions?.salesTerms || company?.terms || currentCompany?.terms || '',
                       terms: company?.salesTerms || currentCompany?.salesTerms || currentCompany?.termsAndConditions?.salesTerms || company?.terms || currentCompany?.terms || '',
                       footer: company?.footer || currentCompany?.footer || currentCompany?.termsAndConditions?.footer || ''
                     }}
                     partyDetails={{
                       ...parties.find(p => p.id === invoiceBill?.party) || {},
                       name: (parties.find(p => p.id === invoiceBill?.party)?.firmName || parties.find(p => p.id === invoiceBill?.party)?.name || ''),
                     }}
                     bankDetails={{
                       name: company.bankName,
                       account: company.bankAccount,
                       ifsc: company.bankIfsc,
                       upi: company.upiId,
                       upiQrUrl: company.upiQrUrl,
                       paymentGatewayLink: company.paymentGatewayLink,
                       sealUrl: resolveCompanySealUrl(currentCompany, company),
                       signUrl: resolveCompanySignUrl(currentCompany, company),
                     }}
                     payments={invoiceBill?.payments || []}
                     pageSize={'a4'}
                     orientation={'portrait'}
                     previewMode={false}
                     sharePreview={true}
                   />;
                   })()
                 ) : (
                   <div className="flex items-center justify-center h-64 text-gray-500">
                     <div className="text-center">
                       <div className="text-lg font-semibold mb-2">No Invoice Data</div>
                       <div className="text-sm">Please select a valid invoice to preview</div>
                     </div>
                   </div>
                 )}
                 <style>{`${BILL_PREVIEW_SCREEN_CSS}
                   @media print {
                     html, body, #root, .print\\:w-[210mm] { width: 210mm !important; }
                     .print\\:min-h-[297mm] { min-height: 297mm !important; }
                     .print\\:max-w-none { max-width: none !important; }
                     .print\\:p-0 { padding: 0 !important; }
                     .print\\:m-0 { margin: 0 !important; }
                   }
                 `}</style>
               </div>
             </div>
         </div>
       </PreviewModal>
      
             {/* ADD ITEM Modal */}
       <FormModal section={MODAL_SECTIONS.SALES}
         isOpen={showAddItemModal}
         onClose={() => { setShowAddItemModal(false); clearAddItemForm(); }}
         title={t('addNewItem')}
         size="2xl"
         onSubmit={(e) => {
           e.preventDefault();
           handleAddItem();
         }}
         submitText={t('addItem')}
         cancelText={t('cancel')}
         submitVariant="success"
       >
         {addItemMessage && (
           <div className={`mb-4 p-3 rounded ${addItemMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
             {addItemMessage}
           </div>
         )}
         
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Item Name *</label>
             <input
               type="text"
               value={newItemName}
               onChange={(e) => setNewItemName(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder={t('enterItemName')}
               required
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Item Type</label>
             <AppSelect
               value={newItemType}
               onChange={(e) => setNewItemType(e.target.value)}
               aria-label="Item type"
               sheetTitle="Item type"
               options={[
                 { value: 'Service', label: t('service') },
                 { value: 'Goods', label: t('goods') },
               ]}
               triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-2`}
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Quantity Measurement *</label>
             <AppSelect
               value={newItemMeasurement}
               onChange={(e) => setNewItemMeasurement(e.target.value)}
               placeholder="Select Unit"
               aria-label="Quantity measurement"
               sheetTitle="Unit"
               options={[
                 { value: '', label: 'Select Unit' },
                 ...predefinedUnits.map((unit) => ({ value: unit, label: unit })),
               ]}
               triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-2`}
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Custom Unit (if not in list)</label>
             <input
               type="text"
               value={newItemCustomUnit}
               onChange={(e) => setNewItemCustomUnit(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder={t('enterCustomUnit')}
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Default Rate</label>
             <input
               type="number"
               value={newItemRate}
               onChange={(e) => setNewItemRate(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0.00"
               min="0"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">GST Percentage</label>
             <input
               type="number"
               value={newItemGstPercentage}
               onChange={(e) => setNewItemGstPercentage(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0"
               min="0"
               max="100"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">HSN Code</label>
             <input
               type="text"
               value={newItemHsnCode}
               onChange={(e) => setNewItemHsnCode(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder={t('enterHsnCode')}
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price</label>
             <input
               type="number"
               value={newItemPurchasePrice}
               onChange={(e) => setNewItemPurchasePrice(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0.00"
               min="0"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Sale Price</label>
             <input
               type="number"
               value={newItemSalePrice}
               onChange={(e) => setNewItemSalePrice(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0.00"
               min="0"
               step="0.01"
             />
           </div>
           
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
             <input
               type="number"
               value={newItemCurrentStock}
               onChange={(e) => setNewItemCurrentStock(e.target.value)}
               className="w-full border border-gray-300 rounded-md shadow-sm p-2"
               placeholder="0"
               min="0"
               step="0.01"
             />
           </div>
           
           <div className="flex items-center">
             <input
               type="checkbox"
               id="isActive"
               checked={newItemIsActive}
               onChange={(e) => setNewItemIsActive(e.target.checked)}
               className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
             />
             <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900">
               Active Item
             </label>
           </div>
         </div>
         
         <div className="mt-4">
           <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
           <textarea
             value={newItemDescription}
             onChange={(e) => setNewItemDescription(e.target.value)}
             className="w-full border border-gray-300 rounded-md shadow-sm p-2"
             rows="3"
             placeholder={t('enterItemDescription')}
           />
         </div>
       </FormModal>
      {docType === 'quotation' && (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700">Terms and Conditions (Override)</label>
          <textarea
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
            rows={3}
            placeholder="Enter terms and conditions for this quotation (leave blank to use company default)"
            value={quotationTermsOverride}
            onChange={e => setQuotationTermsOverride(e.target.value)}
          />
          <div className="text-xs text-gray-500 mt-1">If left blank, the company's default Quotation Terms and Conditions will be used.</div>
        </div>
      )}

             {/* Payment Modal */}
       <FormModal section={MODAL_SECTIONS.SALES}
         isOpen={showPaymentModal}
         onClose={() => {
           setShowPaymentModal(false);
           setSelectedBillForPayment(null);
           setRecordInEmployeeCashFlow(false);
           setSelectedEmployeeForCashFlow('');
         }}
         title={t('addPayment')}
         size="md"
         className="!w-[90vw] sm:!w-[60vw] !max-w-2xl"
         onSubmit={(e) => {
           e.preventDefault();
           handleSavePayment();
         }}
         submitText={t('savePayment')}
         cancelText={t('cancel')}
         submitVariant="success"
       >
         {/* Bill Information */}
         {selectedBillForPayment && (
           <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
             <h4 className="font-semibold text-blue-800 mb-1 text-xs sm:text-sm">Bill Information</h4>
             <div className="grid grid-cols-2 sm:grid-cols-4 gap-1 sm:gap-2 text-xs">
               <div><span className="font-medium">Bill:</span> {selectedBillForPayment.number}</div>
               <div><span className="font-medium">Party:</span> {getPartyName(selectedBillForPayment.party)}</div>
               <div><span className="font-medium">Total:</span> {formatCurrency(selectedBillForPayment.amount, companyCurrency)}</div>
               <div><span className="font-medium">Outstanding:</span> {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
             </div>
           </div>
         )}
         
         {/* Advance Information - Only show if advance is available */}
         {selectedBillForPayment && getPartyAdvance(selectedBillForPayment.party, payments) > 0 && (
           <div className="bg-green-50 border border-green-200 rounded-lg p-2 sm:p-3 mb-2 sm:mb-3">
             <h4 className="font-semibold text-green-800 mb-1 text-xs sm:text-sm">Advance Information</h4>
             <div className="text-xs space-y-1">
               <div><span className="font-medium">Available Advance:</span> {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
               <div className="text-xs text-green-600">
                 Note: Excess payment will be allocated to other bills using FIFO method.
               </div>
             </div>
           </div>
         )}
         
         <div className="space-y-2 sm:space-y-3">
           {/* Receipt Number and Date - Inline Edit */}
           <div className="bg-gray-50 border border-gray-200 rounded-lg p-2 mb-2">
             <div className="flex justify-between items-center mb-2">
               <h4 className="text-xs font-medium text-gray-700">Payment Details</h4>
               <button
                 type="button"
                 onClick={() => {
                   setReceiptNumber('');
                   setPaymentDate('');
                 }}
                 className="text-gray-400 hover:text-gray-600 transition-colors text-xs"
                 title="Clear and regenerate"
               >
                 Regenerate
               </button>
             </div>
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
           <div>
                 <label className="block text-xs font-medium text-gray-700 mb-1">Receipt Number</label>
             <input
               type="text"
               value={receiptNumber}
               onChange={(e) => setReceiptNumber(e.target.value)}
                   className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                   placeholder="Auto-generated"
             />
           </div>
           
           <div>
                 <label className="block text-xs font-medium text-gray-700 mb-1">Payment Date</label>
             <input
               type="date"
               value={paymentDate}
               onChange={(e) => setPaymentDate(e.target.value)}
                   className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
               required
             />
               </div>
             </div>
           </div>
           
           {/* Amount and Payment Mode in one line */}
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Amount</label>
             <input
               type="number"
               value={paymentAmount}
               onChange={(e) => setPaymentAmount(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
               placeholder={t('enterPaymentAmount')}
               step="0.01"
               min="0"
               required
             />
           </div>
           
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Payment Mode</label>
             <AppSelect
               value={paymentMode}
               onChange={(e) => setPaymentMode(e.target.value)}
               aria-label="Payment mode"
               sheetTitle="Payment mode"
               options={[
                 { value: 'Cash', label: t('cash') },
                 { value: 'Cheque', label: t('cheque') },
                 { value: 'Bank Transfer', label: t('bankTransfer') },
                 { value: 'UPI', label: 'UPI' },
                 { value: 'Card', label: t('card') },
               ]}
               triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-1.5 text-xs`}
             />
             </div>
           </div>
           
           {/* Payment calculation info */}
           {selectedBillForPayment && paymentAmount && (
             <div className="bg-gray-50 p-2 rounded text-xs text-gray-600 space-y-1">
               <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                 <div>Outstanding: {formatCurrency(getBillOutstanding(selectedBillForPayment), companyCurrency)}</div>
                 <div>Advance: {formatCurrency(getPartyAdvance(selectedBillForPayment.party, payments), companyCurrency)}</div>
                 <div className="font-medium text-blue-600 col-span-2 sm:col-span-1">
                   Payment needed: {formatCurrency(Math.max(0, getBillOutstanding(selectedBillForPayment) - getPartyAdvance(selectedBillForPayment.party, payments)), companyCurrency)}
                 </div>
               </div>
             </div>
           )}
           
           {/* Reference and Notes in one line */}
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Reference</label>
             <input
               type="text"
               value={paymentReference}
               onChange={(e) => setPaymentReference(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
               placeholder="Cheque number, UPI reference, etc."
             />
           </div>
           
           <div>
               <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
             <textarea
               value={paymentNotes}
               onChange={(e) => setPaymentNotes(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-1.5 text-xs"
                 rows="2"
               placeholder={t('additionalNotes')}
             />
             </div>
           </div>
           
           {/* Employee Cash Flow Recording Section */}
           <div className="border-t pt-3 mt-3">
             <div className="flex items-center mb-3">
               <input
                 type="checkbox"
                 id="recordInEmployeeCashFlow"
                 checked={recordInEmployeeCashFlow}
                 onChange={(e) => {
                   setRecordInEmployeeCashFlow(e.target.checked);
                   if (!e.target.checked) {
                     setSelectedEmployeeForCashFlow('');
                   }
                 }}
                 className="mr-2"
               />
               <label htmlFor="recordInEmployeeCashFlow" className="text-xs sm:text-sm font-medium text-gray-700 cursor-pointer">
                 Also record in employee cash flow
               </label>
             </div>
             
             {recordInEmployeeCashFlow && (
               <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                 <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                   Select Employee *
                 </label>
                 <AppSelect
                   value={selectedEmployeeForCashFlow}
                   onChange={(e) => setSelectedEmployeeForCashFlow(e.target.value)}
                   placeholder="Select Employee"
                   aria-label="Select employee"
                   sheetTitle="Select employee"
                   options={[
                     { value: '', label: 'Select Employee' },
                     ...(employees?.filter((emp) => emp.status === 'active') || []).map((employee) => {
                       const balance = employee.cashBalance || { cash: 0, upi: 0, total: 0 };
                       return {
                         value: employee.id,
                         label: `${employee.name} (${employee.employeeId || 'N/A'}) — Cash: ₹${balance.cash?.toLocaleString('en-IN') || 0} | UPI: ₹${balance.upi?.toLocaleString('en-IN') || 0}`,
                       };
                     }),
                   ]}
                   triggerClassName={`${APP_SELECT_FILTER_TRIGGER} text-xs px-2 py-1.5`}
                 />
                 {selectedEmployeeForCashFlow && (() => {
                   const selectedEmp = employees?.find(emp => emp.id === selectedEmployeeForCashFlow);
                   const balance = selectedEmp?.cashBalance || { cash: 0, upi: 0, total: 0 };
                   return (
                     <div className="mt-2 text-xs text-gray-600">
                       <div className="font-medium text-gray-700 mb-1">
                         Selected: {selectedEmp?.name} ({selectedEmp?.employeeId || 'N/A'})
                       </div>
                       <div className="text-xs">
                         <span className="font-medium">Current Balance:</span> Cash ₹{balance.cash?.toLocaleString('en-IN') || 0} | UPI ₹{balance.upi?.toLocaleString('en-IN') || 0} | Total ₹{balance.total?.toLocaleString('en-IN') || 0}
                       </div>
                       <div className="text-blue-600 mt-1 font-medium">
                         This will record as inflow transaction (receiving payment)
                       </div>
                     </div>
                   );
                 })()}
               </div>
             )}
           </div>
         </div>
       </FormModal>

       {/* FIFO Payment Modal */}
       <FormModal section={MODAL_SECTIONS.SALES}
         isOpen={showFIFOPaymentModal}
         onClose={() => {
           setShowFIFOPaymentModal(false);
           setSelectedPartyForFIFO('');
           setFifoPaymentAmount('');
         }}
         title="Add FIFO Payment"
         size="lg"
         className="!w-[90vw] sm:!w-[70vw] !max-w-4xl"
         onSubmit={(e) => {
           e.preventDefault();
           handleFIFOPayment();
         }}
         submitText="Save FIFO Payment"
         cancelText="Cancel"
         submitVariant="success"
       >
         <div className="space-y-4">
           {/* Party Selection */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">Select Party *</label>
             <AppSelect
               value={selectedPartyForFIFO}
               onChange={(e) => setSelectedPartyForFIFO(e.target.value)}
               placeholder="-- Select Party --"
               aria-label="Select party"
               sheetTitle="Select party"
               options={[
                 { value: '', label: '-- Select Party --' },
                 ...Array.from(new Set(bills.map((bill) => bill.party || bill.partyId).filter(Boolean)))
                   .filter((partyId) => {
                     const partyBills = bills.filter(
                       (bill) =>
                         (bill.partyId === partyId || bill.party === partyId) && (bill.outstanding || 0) > 0
                     );
                     return partyBills.length > 0;
                   })
                   .map((partyId) => {
                     const party = parties.find((p) => p.id === partyId);
                     const partyBills = bills.filter(
                       (bill) =>
                         (bill.partyId === partyId || bill.party === partyId) && (bill.outstanding || 0) > 0
                     );
                     const totalOutstanding = partyBills.reduce((sum, bill) => sum + (bill.outstanding || 0), 0);
                     return {
                       value: partyId,
                       label: `${party ? party.firmName || party.name : 'Unknown Party'} — Outstanding: ${formatCurrency(totalOutstanding, companyCurrency)}`,
                     };
                   }),
               ]}
               triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-2 text-sm`}
             />
           </div>

           {/* Pending Invoices List (FIFO Order) */}
           {selectedPartyForFIFO && (
             <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
               <h4 className="font-semibold text-gray-800 mb-2 text-sm">Pending Invoices (FIFO Order)</h4>
               <div className="max-h-48 overflow-y-auto">
                 {bills
                   .filter(bill => 
                     (bill.partyId === selectedPartyForFIFO || bill.party === selectedPartyForFIFO) && 
                     (bill.outstanding || 0) > 0
                   )
                   .sort((a, b) => {
                     const dateA = new Date(a.invoiceDate || a.date || 0);
                     const dateB = new Date(b.invoiceDate || b.date || 0);
                     return dateA - dateB;
                   })
                   .map((bill, index) => (
                     <div key={bill.id} className="flex justify-between items-center py-2 px-2 border-b border-gray-200 last:border-b-0">
                       <div className="flex-1">
                         <div className="text-xs font-medium text-gray-700">
                           {index + 1}. {bill.number || bill.invoiceNumber} - {formatDateToCompanyFormat(bill.invoiceDate || bill.date, currentCompany)}
                         </div>
                         <div className="text-xs text-gray-500">
                           Total: {formatCurrency(bill.totalAmount || bill.amount || 0, companyCurrency)} | 
                           Paid: {formatCurrency(bill.totalPaid || 0, companyCurrency)}
                         </div>
                       </div>
                       <div className="text-xs font-semibold text-red-600 ml-2">
                         Outstanding: {formatCurrency(bill.outstanding || 0, companyCurrency)}
                       </div>
                     </div>
                   ))}
               </div>
               <div className="mt-2 pt-2 border-t border-gray-300">
                 <div className="flex justify-between items-center text-sm">
                   <span className="font-medium text-gray-700">Total Outstanding:</span>
                   <span className="font-bold text-red-600">
                     {formatCurrency(
                       bills
                         .filter(bill => 
                           (bill.partyId === selectedPartyForFIFO || bill.party === selectedPartyForFIFO) && 
                           (bill.outstanding || 0) > 0
                         )
                         .reduce((sum, bill) => sum + (bill.outstanding || 0), 0),
                       companyCurrency
                     )}
                   </span>
                 </div>
               </div>
             </div>
           )}

           {/* Payment Details */}
           <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Receipt Number *</label>
               <div className="flex gap-2">
                 <input
                   type="text"
                   value={fifoReceiptNumber}
                   onChange={(e) => setFifoReceiptNumber(e.target.value)}
                   className="flex-1 border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                   required
                 />
                 <button
                   type="button"
                   onClick={async () => {
                     const receiptNum = await generateReceiptNumber();
                     setFifoReceiptNumber(receiptNum);
                   }}
                   className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-xs"
                 >
                   Regenerate
                 </button>
               </div>
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Payment Date *</label>
               <input
                 type="date"
                 value={fifoPaymentDate}
                 onChange={(e) => setFifoPaymentDate(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                 required
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Amount *</label>
               <input
                 type="number"
                 value={fifoPaymentAmount}
                 onChange={(e) => setFifoPaymentAmount(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                 placeholder="Enter payment amount"
                 min="0"
                 step="0.01"
                 required
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Payment Mode *</label>
               <AppSelect
                 value={fifoPaymentMode}
                 onChange={(e) => setFifoPaymentMode(e.target.value)}
                 aria-label="Payment mode"
                 sheetTitle="Payment mode"
                 options={[
                   { value: 'Cash', label: 'Cash' },
                   { value: 'Bank Transfer', label: 'Bank Transfer' },
                   { value: 'UPI', label: 'UPI' },
                   { value: 'Cheque', label: 'Cheque' },
                   { value: 'Credit Card', label: 'Credit Card' },
                   { value: 'Debit Card', label: 'Debit Card' },
                   { value: 'Other', label: 'Other' },
                 ]}
                 triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md shadow-sm p-2 text-sm`}
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
               <input
                 type="text"
                 value={fifoPaymentReference}
                 onChange={(e) => setFifoPaymentReference(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                 placeholder="Cheque number, UPI reference, etc."
               />
             </div>

             <div>
               <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
               <textarea
                 value={fifoPaymentNotes}
                 onChange={(e) => setFifoPaymentNotes(e.target.value)}
                 className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm"
                 rows="2"
                 placeholder="Additional notes"
               />
             </div>
           </div>

           {selectedPartyForFIFO && (
             <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
               <div className="text-xs text-blue-800">
                 <strong>Note:</strong> This payment will be allocated to outstanding invoices using FIFO (First In, First Out) method - oldest invoices first.
               </div>
             </div>
           )}
         </div>
       </FormModal>

             {/* Payment Details Modal */}
       <StandardModal section={MODAL_SECTIONS.SALES}
         isOpen={showPaymentDetailsModal && selectedBillForReceipts}
         onClose={() => setShowPaymentDetailsModal(false)}
         title={t('paymentReceipts')}
         size="2xl"
         className="!w-[90vw] sm:!w-[60vw] !max-w-4xl"
       >
         <div className="mb-2">
           <p className="text-xs sm:text-sm text-gray-600 break-words">
             <span className="hidden sm:inline">{docTypeOptions.find(opt => opt.value === docType)?.label || 'Document'}: {selectedBillForReceipts?.number} | Party: {getPartyName(selectedBillForReceipts?.party)} | Total Amount: ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
             <span className="sm:hidden">{selectedBillForReceipts?.number} | ₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
           </p>
         </div>
         
         {/* Payment Summary */}
         <div className="bg-gray-50 p-2 sm:p-3 rounded-lg mb-2 sm:mb-3">
           <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 sm:gap-3 text-xs">
             <div className="text-center sm:text-left">
               <span className="font-medium text-gray-700">Total Bill:</span>
               <span className="ml-1 text-xs sm:text-sm font-semibold">₹{(selectedBillForReceipts?.amount || 0).toLocaleString('en-IN')}</span>
             </div>
             <div className="text-center sm:text-left">
               <span className="font-medium text-gray-700">Paid:</span>
               <span className="ml-1 text-xs sm:text-sm font-semibold text-green-600">₹{(selectedBillForReceipts?.paid || 0).toLocaleString('en-IN')}</span>
             </div>
             <div className="text-center sm:text-left">
               <span className="font-medium text-gray-700">Outstanding:</span>
               <span className="ml-1 text-xs sm:text-sm font-semibold text-red-600">₹{(selectedBillForReceipts?.outstanding || 0).toLocaleString('en-IN')}</span>
             </div>
           </div>
         </div>
         
         <div className="overflow-x-auto max-h-[400px]">
           {/* Desktop Table View */}
           <div className="hidden sm:block">
             <table className="w-full text-xs">
             <thead className="bg-gray-50">
               <tr>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     RECEIPT NO
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   AGAINST {docType.toUpperCase()}
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                     AMOUNT
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   DATE
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   MODE
                 </th>
                   <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                   ACTIONS
                 </th>
               </tr>
             </thead>
             <tbody className="bg-white divide-y divide-gray-200">
               {selectedBillForReceipts && getBillPayments(selectedBillForReceipts).map((payment) => {
                 const allocation = payment.allocations.find(a => a.billId === selectedBillForReceipts.id && a.billType === docType);
                 return (
                   <tr key={payment.id} className="hover:bg-gray-50">
                       <td className="px-2 py-2 whitespace-nowrap text-xs font-medium text-gray-900">
                       {payment.receiptNumber}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       {allocation?.billNumber || 'N/A'}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       ₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       {payment.paymentDate}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                       {payment.paymentMode}
                     </td>
                       <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">
                         <StandardButton
                           variant="info"
                           size="xs"
                           onClick={() => {
                             setSelectedPaymentForReceipt(payment);
                             setShowReceiptModal(true);
                             setShowPaymentDetailsModal(false);
                           }}
                         >
                           Preview
                         </StandardButton>
                     </td>
                   </tr>
                 );
               })}
             </tbody>
           </table>
           </div>

           {/* Mobile Card View */}
           <div className="sm:hidden space-y-2">
             {selectedBillForReceipts && getBillPayments(selectedBillForReceipts).map((payment) => {
               const allocation = payment.allocations.find(a => a.billId === selectedBillForReceipts.id && a.billType === docType);
               return (
                 <div key={payment.id} className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                   <div className="flex justify-between items-start mb-2">
                     <div className="flex-1">
                       <div className="text-xs font-medium text-gray-900">{payment.receiptNumber}</div>
                       <div className="text-xs text-gray-600">vs {allocation?.billNumber || 'N/A'}</div>
                     </div>
                     <div className="text-right">
                       <div className="text-xs font-semibold text-gray-900">₹{parseFloat(allocation?.allocatedAmount || 0).toLocaleString('en-IN')}</div>
                       <div className="text-xs text-gray-600">{payment.paymentMode}</div>
                     </div>
                   </div>
                   <div className="flex justify-between items-center">
                     <div className="text-xs text-gray-600">{payment.paymentDate}</div>
                     <StandardButton
                       variant="info"
                       size="xs"
                       onClick={() => {
                         setSelectedPaymentForReceipt(payment);
                         setShowReceiptModal(true);
                         setShowPaymentDetailsModal(false);
                       }}
                     >
                       Preview
                     </StandardButton>
                   </div>
                 </div>
               );
             })}
           </div>
         </div>
       </StandardModal>

             {/* Payment Receipt Preview Modal */}
       <PreviewModal section={MODAL_SECTIONS.SALES}
         isOpen={showReceiptModal && selectedPaymentForReceipt}
         onClose={() => setShowReceiptModal(false)}
         title={t('paymentReceiptPreview')}
         maxWidth="max-w-[90vw] sm:max-w-[60vw] lg:max-w-2xl"
         maxHeight="max-h-[70vh] sm:max-h-[80vh]"
         extraActions={
           <ShareButton
             onExportPDF={async () => {
               try {
                 const jsPDFLocal = window.jsPDF || (window.jspdf && window.jspdf.jsPDF);
                 if (!jsPDFLocal || !selectedPaymentForReceipt) return;
                 const doc = new jsPDFLocal();
                 let y = 15;
                 doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(31,41,55);
                 doc.text('PAYMENT RECEIPT', 105, y, { align: 'center' }); y += 12;
                 doc.setFontSize(12); doc.setTextColor(59,130,246); doc.setFont('helvetica', 'bold');
                 doc.text(`Receipt No: ${selectedPaymentForReceipt.receiptNumber}`, 105, y, { align: 'center' }); y += 12;
                 doc.setFontSize(14); doc.setTextColor(0,0,0); doc.setFont('helvetica', 'bold');
                 doc.text(company?.firmName || company?.name || 'Company Name', 105, y, { align: 'center' }); y += 10;
                 doc.setDrawColor(209,213,219); doc.line(25, y, 185, y); y += 10;
                 doc.setFontSize(12); doc.setFont('helvetica', 'normal');
                 const addKV = (k, v) => { doc.text(`${k}: ${v}`, 25, y); y += 8; };
                 addKV('Date', selectedPaymentForReceipt.paymentDate || new Date().toISOString().split('T')[0]);
                 addKV('Received From', selectedPaymentForReceipt.partyName || '');
                 addKV('Amount', `₹${parseFloat(selectedPaymentForReceipt.totalAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);
                 addKV('Payment Mode', selectedPaymentForReceipt.paymentMode || 'Cash');
                 addKV('Reference', selectedPaymentForReceipt.reference || '-');
                 if (selectedPaymentForReceipt.notes) addKV('Notes', selectedPaymentForReceipt.notes);
                 const blob = doc.output('blob');
                 const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`, { type: 'application/pdf' });
                 if (navigator.canShare && navigator.canShare({ files: [file] })) {
                   await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                 } else {
                   doc.save(`receipt-${selectedPaymentForReceipt.receiptNumber}.pdf`);
                 }
               } catch (e) { console.error('Share receipt PDF failed:', e); }
             }}
             onExportImage={async () => {
               try {
                 const el = document.querySelector('.receipt-container');
                 if (!el || !selectedPaymentForReceipt) return;
                 const html2canvas = await import('html2canvas');
                 const canvas = await html2canvas.default(el, { scale: 2, useCORS: true, allowTaint: false, backgroundColor: '#ffffff' });
                 const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
                 const file = new File([blob], `receipt-${selectedPaymentForReceipt.receiptNumber}.png`, { type: 'image/png' });
                 if (navigator.canShare && navigator.canShare({ files: [file] })) {
                   await navigator.share({ files: [file], title: `Receipt ${selectedPaymentForReceipt.receiptNumber}` });
                 } else {
                   const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `receipt-${selectedPaymentForReceipt.receiptNumber}.png`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
                 }
               } catch (e) { console.error('Share receipt image failed:', e); }
             }}
           />
         }
         showBackButton={true}
         onBack={() => {
           setShowReceiptModal(false);
           setShowPaymentDetailsModal(true);
         }}
         showPrintButton={true}
         onPrint={printReceipt}
         showPdfButton={true}
         onPdf={generatePDFReceipt}
         zIndex="z-50"
       >
         <div ref={receiptRef} className="flex justify-center">
           <ReceiptTemplate
             receipt={{
               totalAmount: selectedPaymentForReceipt?.totalAmount,
               date: selectedPaymentForReceipt?.paymentDate,
               mode: selectedPaymentForReceipt?.paymentMode,
               reference: selectedPaymentForReceipt?.reference,
               notes: selectedPaymentForReceipt?.notes,
               billReference: selectedPaymentForReceipt?.billId,
               allocations: selectedPaymentForReceipt?.allocations,
               advanceAllocations: selectedPaymentForReceipt?.advanceAllocations,
               fifoAllocationUsed: selectedPaymentForReceipt?.fifoAllocationUsed,
               remainingAmount: selectedPaymentForReceipt?.remainingAmount
             }}
             bill={{
               number: selectedPaymentForReceipt?.billNumber,
               invoiceNumber: selectedPaymentForReceipt?.billNumber,
               billNumber: selectedPaymentForReceipt?.billNumber
             }}
             company={company}
             party={{ firmName: selectedPaymentForReceipt?.partyName }}
             receiptNumber={selectedPaymentForReceipt?.receiptNumber}
             fifoAllocation={selectedPaymentForReceipt?.allocations}
             customImages={selectedImages}
           />
         </div>
       </PreviewModal>

      {/* ADD PARTY Modal */}
      <FormModal section={MODAL_SECTIONS.SALES}
        isOpen={showAddPartyModal}
        onClose={() => { setShowAddPartyModal(false); clearAddPartyForm(); }}
        title="Add New Party"
        size="2xl"
        onSubmit={(e) => {
          e.preventDefault();
          handleAddParty();
        }}
        submitText="Add Party"
        cancelText="Cancel"
        submitVariant="success"
      >
        {addPartyMessage && (
          <div className={`mb-4 p-3 rounded ${addPartyMessage.includes('successfully') ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {addPartyMessage}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Party Name *</label>
            <input
              type="text"
              value={newPartyName}
              onChange={(e) => setNewPartyName(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter party name"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">GST Type</label>
            <AppSelect
              value={newPartyGstType}
              onChange={(e) => setNewPartyGstType(e.target.value)}
              aria-label="GST type"
              sheetTitle="GST type"
              options={[
                { value: 'Regular', label: 'Regular' },
                { value: 'Composition', label: 'Composition' },
                { value: 'Unregistered', label: 'Unregistered' },
              ]}
              triggerClassName={`${APP_SELECT_FILTER_TRIGGER} rounded-md p-2`}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              GSTIN {newPartyGstType === 'Regular' && <span className="text-red-500">*</span>}
            </label>
            <input
              type="text"
              value={newPartyGstin}
              onChange={(e) => setNewPartyGstin(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={newPartyGstType === 'Regular' ? 'Enter GSTIN (required for Regular)' : 'Enter GSTIN (if applicable)'}
              required={newPartyGstType === 'Regular'}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="text"
              value={newPartyPhone}
              onChange={(e) => setNewPartyPhone(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter phone number"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={newPartyEmail}
              onChange={(e) => setNewPartyEmail(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter email address"
            />
          </div>
        </div>
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
          <textarea
            value={newPartyAddress}
            onChange={(e) => setNewPartyAddress(e.target.value)}
            className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
            rows="3"
            placeholder="Enter party address"
          />
        </div>
        
        {/* Advanced Fields Toggle */}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => setShowAdvancedFields(!showAdvancedFields)}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium text-sm"
          >
            <svg className={`w-4 h-4 transition-transform ${showAdvancedFields ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            {showAdvancedFields ? 'Hide' : 'Show'} Advanced Fields (Opening Balance, Credit Limit)
          </button>
        </div>
        
        {/* Advanced Fields */}
        {showAdvancedFields && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-700 mb-3">Advanced Settings (Optional)</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Opening Balance</label>
                <input
                  type="number"
                  value={newPartyOpeningBalance}
                  onChange={(e) => setNewPartyOpeningBalance(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">+ve: Customer owes you, -ve: You owe customer</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Limit (₹)</label>
                <input
                  type="number"
                  value={newPartyCreditLimit}
                  onChange={(e) => setNewPartyCreditLimit(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0.00"
                  step="0.01"
                />
                <p className="text-xs text-gray-500 mt-1">Maximum credit allowed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Credit Period (Days)</label>
                <input
                  type="number"
                  value={newPartyCreditPeriod}
                  onChange={(e) => setNewPartyCreditPeriod(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0"
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Payment terms in days</p>
              </div>
            </div>
          </div>
        )}
      </FormModal>

      {showServiceEditorModal &&
        createPortal(
          <div
            className="fixed inset-0 flex items-end sm:items-center justify-center bg-black/50 p-3 sm:p-4 pt-[max(0.75rem,env(safe-area-inset-top))] pb-[max(0.75rem,env(safe-area-inset-bottom))] pl-[max(0.75rem,env(safe-area-inset-left))] pr-[max(0.75rem,env(safe-area-inset-right))]"
            style={{ zIndex: 10050 }}
            data-modal-overlay="true"
            role="presentation"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeServiceEditor();
            }}
          >
            <div
              className="bg-white rounded-t-xl sm:rounded-lg shadow-xl w-full min-w-0 max-w-md max-h-[min(92dvh,calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom)-1.5rem))] sm:max-h-[min(90vh,720px)] flex flex-col overflow-hidden"
              role="dialog"
              aria-modal="true"
              aria-labelledby="service-details-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-3 sm:p-4 border-b shrink-0 gap-2">
                <h3 id="service-details-title" className="text-base sm:text-lg font-semibold text-gray-900 min-w-0 pr-2">
                  Tag details <span className="text-sm font-normal text-gray-500">(optional)</span>
                </h3>
                <button
                  onClick={closeServiceEditor}
                  className="shrink-0 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 inline-flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors touch-manipulation rounded-lg hover:bg-gray-100"
                  type="button"
                  aria-label="Close"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-4 sm:p-5 space-y-4 overflow-y-auto min-h-0 overscroll-contain [-webkit-overflow-scrolling:touch]">
                <p className="text-sm font-medium text-gray-800 capitalize">{serviceEditorForm.serviceName || serviceEditorContext.serviceName || 'Tag'}</p>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">Vendor</label>
                  <AppSelect
                    value={serviceEditorForm.vendorId || ''}
                    onChange={(e) => {
                      const vendorId = e.target.value;
                      const selectedVendor = parties.find((p) => p.id === vendorId);
                      setServiceEditorForm((prev) => ({ ...prev, vendorId, vendorName: selectedVendor?.firmName || '' }));
                    }}
                    placeholder="Select vendor"
                    aria-label="Vendor"
                    sheetTitle="Vendor"
                    options={[
                      { value: '', label: 'Select vendor' },
                      ...getServiceEditorVendorOptions(
                        serviceEditorForm.serviceName || serviceEditorContext.serviceName
                      ).map((p) => {
                        const tagMeta = findServiceTagMeta(
                          serviceTagMaster,
                          serviceEditorForm.serviceName || serviceEditorContext.serviceName
                        );
                        const prefIds = normalizePreferredVendorIds(tagMeta);
                        const rank = prefIds.indexOf(p.id);
                        const isFavorite = isPreferredVendorForTag(tagMeta, p.id);
                        return {
                          value: p.id,
                          label: `${isFavorite ? '★ ' : ''}${p.firmName}${rank === 0 ? ' (primary)' : rank > 0 ? ` (fav ${rank + 1})` : ''}`,
                        };
                      }),
                    ]}
                    triggerClassName="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400 text-left flex items-center justify-between"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Internal cost</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={serviceEditorForm.internalCost}
                      onChange={(e) => setServiceEditorForm((prev) => ({ ...prev, internalCost: e.target.value }))}
                      className="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                      placeholder="0"
                    />
                    {(() => {
                      const tagMeta = findServiceTagMeta(
                        serviceTagMaster,
                        serviceEditorForm.serviceName || serviceEditorContext.serviceName
                      );
                      const row = serviceEditorContext.rowIndex != null ? rows[serviceEditorContext.rowIndex] : null;
                      const suggested = tagMeta && row ? calculateInternalCostFromTag(tagMeta, row) : null;
                      if (suggested == null) return null;
                      return (
                        <p className="text-xs text-gray-500 mt-1">
                          Suggested from tag rate: {suggested} (editable)
                        </p>
                      );
                    })()}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Charge to customer</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={serviceEditorForm.customerCharge}
                      onChange={(e) => setServiceEditorForm((prev) => ({ ...prev, customerCharge: e.target.value }))}
                      className="w-full min-h-[44px] sm:min-h-0 border border-gray-300 rounded-lg px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/25 focus:border-blue-400"
                      placeholder="Optional"
                    />
                  </div>
                </div>
                <details className="rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2 text-sm">
                  <summary className="cursor-pointer font-medium text-gray-700">Billing &amp; status</summary>
                  <div className="mt-3 space-y-3">
                    <label className="inline-flex items-center gap-2 text-gray-700">
                      <input type="checkbox" checked={Boolean(serviceEditorForm.showOnInvoice)} onChange={(e) => setServiceEditorForm((prev) => ({ ...prev, showOnInvoice: e.target.checked }))} className="h-4 w-4 rounded" />
                      Show on invoice PDF
                    </label>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <AppSelect
                        value={serviceEditorForm.status || 'pending'}
                        onChange={(e) => setServiceEditorForm((prev) => ({ ...prev, status: e.target.value }))}
                        aria-label="Status"
                        sheetTitle="Status"
                        options={SERVICE_STATUS_OPTIONS.map((status) => ({
                          value: status,
                          label: status.charAt(0).toUpperCase() + status.slice(1),
                        }))}
                        triggerClassName="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white text-left min-h-[44px] flex items-center justify-between touch-manipulation"
                      />
                    </div>
                  </div>
                </details>
              </div>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 p-3 sm:p-4 border-t bg-gray-50 rounded-b-lg shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-4">
                <button
                  type="button"
                  onClick={closeServiceEditor}
                  className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-3 sm:py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 active:bg-gray-300 rounded-md touch-manipulation"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={saveServiceEditor}
                  className="w-full sm:w-auto min-h-[48px] sm:min-h-0 px-4 py-3 sm:py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-md touch-manipulation"
                >
                  Save
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <StandardModal section={MODAL_SECTIONS.SALES}
        isOpen={showManageTagsModal}
        onClose={() => setShowManageTagsModal(false)}
        title="Service tags"
        size="lg"
        zIndex="z-[10050]"
      >
        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          Create and manage tags for your company. Tags used on past bills still appear when filtering until you remove them from those bills.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 mb-5">
          <input
            type="text"
            value={manageTagsNewName}
            onChange={(e) => setManageTagsNewName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleManageAddTag();
            }}
            className="flex-1 min-h-[44px] sm:min-h-0 border border-gray-300 rounded-md px-3 py-2.5 sm:py-2 text-base sm:text-sm touch-manipulation"
            placeholder="New tag name"
          />
          <button
            type="button"
            onClick={() => void handleManageAddTag()}
            className="min-h-[44px] sm:min-h-0 px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 active:bg-blue-800 touch-manipulation sm:shrink-0"
          >
            Add
          </button>
        </div>
        <div className="border border-gray-200 rounded-xl max-h-[min(60dvh,22rem)] sm:max-h-80 overflow-y-auto overscroll-contain divide-y divide-gray-100 [-webkit-overflow-scrolling:touch] shadow-inner bg-gray-50/30">
          {[...serviceTagMaster]
            .sort((a, b) =>
              normalizeTagName(a.normalizedName || a.name).localeCompare(normalizeTagName(b.normalizedName || b.name))
            )
            .map((t) => {
              const label = normalizeTagName(t.normalizedName || t.name);
              const active = t.isActive !== false;
              return (
                <div
                  key={t.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 py-3 sm:py-2 text-sm min-w-0"
                >
                  <span className={`min-w-0 break-words ${active ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {label || t.id}
                  </span>
                  <div className="flex gap-2 shrink-0 self-end sm:self-auto">
                    {active ? (
                      <button
                        type="button"
                        onClick={() => void deactivateServiceTag(t)}
                        className="min-h-[40px] sm:min-h-0 px-2 py-2 sm:py-0 text-sm sm:text-xs text-red-600 hover:underline touch-manipulation"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => void restoreServiceTag(t)}
                        className="min-h-[40px] sm:min-h-0 px-2 py-2 sm:py-0 text-sm sm:text-xs text-blue-600 hover:underline touch-manipulation"
                      >
                        Restore
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          {serviceTagMaster.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">No registry tags yet. Add one above or add tags while billing.</div>
          )}
        </div>
      </StandardModal>

      <FormModal section={MODAL_SECTIONS.SALES}
        isOpen={showSubPartyModal}
        onClose={() => setShowSubPartyModal(false)}
        title="Sub parties"
        size="xl"
        className={SUB_PARTY_MODAL_SHELL_CLASS}
        onSubmit={(e) => {
          e.preventDefault();
          applySubPartyModal();
        }}
        submitText="Apply"
        cancelText="Cancel"
        submitVariant="primary"
      >
        <p className="text-xs sm:text-sm text-gray-600 mb-3 leading-relaxed flex flex-wrap items-start gap-1.5">
          <span>
            <strong>Select lines</strong> for each sub party — the <strong>amount defaults</strong> to the sum of those line totals (incl. GST where applicable). Sub party amounts must sum to the Grand Total.
          </span>
          <InfoHoverTip text={SUB_PARTY_MODAL_HELP} placement="bottom" className="mt-0.5" />
        </p>

        {subPartyLineRefs.length === 0 ? (
          <div className="flex flex-wrap items-start gap-2 p-3 rounded-md bg-amber-50 text-amber-800 text-sm mb-3">
            <span>Add at least one meaningful invoice line before creating a sub party breakdown.</span>
            <InfoHoverTip text={SUB_PARTY_NO_LINES_HELP} placement="bottom" />
          </div>
        ) : null}

        <div className={subPartyLineRefs.length === 0 ? 'hidden' : SUB_PARTY_SCROLL_LIST_CLASS}>
          {subPartyDraft.map((row, idx) => (
            <div key={idx} className={SUB_PARTY_CARD_CLASS}>
              <div className={`${SUB_PARTY_ROW_LAYOUT_CLASS} mb-3`}>
                <div className={SUB_PARTY_NAME_FIELD_CLASS}>
                  <label className="block text-xs text-gray-600 mb-0.5">Sub party name</label>
                  <input
                    type="text"
                    value={row.name}
                    onChange={(e) => updateSubPartyDraftRow(idx, 'name', e.target.value)}
                    className="w-full min-w-0 max-w-full p-2.5 border border-gray-300 rounded text-base md:text-sm bg-white touch-manipulation"
                    placeholder="e.g. end customer"
                    autoComplete="off"
                  />
                </div>
                <div className={SUB_PARTY_AMOUNT_FIELD_CLASS}>
                  <label className="flex flex-wrap items-center gap-1 text-xs text-gray-600 mb-0.5">
                    <span>Amount <span className="font-normal text-gray-400">(editable)</span></span>
                    <InfoHoverTip text={SUB_PARTY_AMOUNT_HELP} placement="bottom" />
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={row.amount}
                    onChange={(e) => updateSubPartyDraftRow(idx, 'amount', e.target.value)}
                    className="w-full min-w-0 p-2.5 border border-gray-300 rounded text-base md:text-sm tabular-nums bg-white touch-manipulation"
                    placeholder="From lines"
                  />
                  {Array.isArray(row.lineIndices) && row.lineIndices.length > 0 && (() => {
                    const lineSum = sumLineTotalsForSubPartyIndices(row.lineIndices, subPartyLineRefs, company.gstinType);
                    const adj = roundTo2Decimals(parseFloat(String(row._invoiceRoundOffAdj ?? '').replace(/,/g, '')) || 0);
                    const expectedDefault = roundTo2Decimals(lineSum + adj);
                    const entered = parseFloat(String(row.amount ?? '').replace(/,/g, ''));
                    const differs = !Number.isFinite(entered) || Math.abs(entered - expectedDefault) > 0.02;
                    return (
                      <div
                        className={`mt-1.5 space-y-0.5 text-[10px] leading-snug tabular-nums ${differs ? 'text-amber-800' : 'text-gray-500'}`}
                      >
                        <div className="flex flex-wrap items-center gap-1">
                          <span>
                            Line total (items): {formatCurrency(lineSum, companyCurrency)}
                          </span>
                          <InfoHoverTip text={SUB_PARTY_AMOUNT_DETAIL_HELP} placement="bottom" />
                        </div>
                        {Math.abs(adj) > 0.001 ? (
                          <div>+ invoice round-off: {formatCurrency(adj, companyCurrency)}</div>
                        ) : null}
                        <div>
                          Default amount: {formatCurrency(expectedDefault, companyCurrency)}
                          {differs ? (
                            <span className="font-normal"> — field differs; OK if intentional</span>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div className={SUB_PARTY_ACTIONS_CLASS}>
                  <div className={SUB_PARTY_SYNC_ROW_CLASS}>
                    <button
                      type="button"
                      onClick={() => fillSubPartyDraftAmountFromLines()}
                      className="min-h-[44px] lg:min-h-0 text-xs px-3 py-2.5 rounded border border-indigo-300 bg-white text-indigo-800 hover:bg-indigo-50 touch-manipulation text-center w-full"
                    >
                      Sync all from lines
                    </button>
                    <InfoHoverTip text={SUB_PARTY_SYNC_HELP} placement="bottom" />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeSubPartyDraftRow(idx)}
                    className="w-full min-h-[44px] lg:min-h-0 text-sm text-red-600 px-3 py-2.5 rounded border border-transparent hover:bg-red-50 shrink-0 disabled:opacity-40 touch-manipulation text-center"
                    disabled={subPartyDraft.length <= 1}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-gray-600 mb-2">
                <span>Assign lines (item · measurement)</span>
                <InfoHoverTip text={SUB_PARTY_ASSIGN_LINES_HELP} placement="bottom" />
              </div>
              {(() => {
                const visibleLineRefs = subPartyLineRefs.filter(({ cleanedIdx: ci }) =>
                  !subPartyDraft.some(
                    (r, i) => i !== idx && Array.isArray(r.lineIndices) && r.lineIndices.includes(ci)
                  )
                );
                const hiddenCount = subPartyLineRefs.length - visibleLineRefs.length;
                return (
                  <>
                    {hiddenCount > 0 ? (
                      <div className="flex flex-wrap items-start gap-1.5 text-[11px] text-gray-500 mb-2 leading-snug">
                        <span>
                          {hiddenCount} line{hiddenCount === 1 ? '' : 's'} assigned to other sub-parties — uncheck there first to assign here.
                        </span>
                        <InfoHoverTip text={subPartyHiddenLinesHelp(hiddenCount)} placement="bottom" />
                      </div>
                    ) : null}
                    <div className={SUB_PARTY_LINE_LIST_CLASS}>
                      {visibleLineRefs.length === 0 && subPartyLineRefs.length > 0 ? (
                        <div className="flex flex-wrap items-start gap-2 text-xs text-gray-600 p-2 rounded border border-dashed border-gray-300 bg-white leading-snug">
                          <span>
                            No lines available for this sub-party — every line is already assigned to another sub-party above.
                          </span>
                          <InfoHoverTip text={SUB_PARTY_NO_AVAILABLE_LINES_HELP} placement="bottom" />
                        </div>
                      ) : (
                        visibleLineRefs.map(({ row: invRow, cleanedIdx }) => {
                          const itemLabel = resolveBillLineItemName(invRow, items, '');
                          let measure = '';
                          if (invRow.qtyDisplay) measure = String(invRow.qtyDisplay);
                          else if (invRow.qtyExpr && String(invRow.qtyExpr).trim()) measure = String(invRow.qtyExpr).trim();
                          const q = parseFloat(invRow.qty) || 0;
                          const u = invRow.unit || invRow.quantityMeasurement || 'Nos';
                          const qtyBit = q > 0 ? `${q} ${u}` : '';
                          if (qtyBit && !measure) measure = qtyBit;
                          else if (qtyBit && measure) measure = `${measure} · ${qtyBit}`;
                          if (!measure) {
                            const n = parseFloat(invRow.nos) || 0;
                            const l = parseFloat(invRow.length) || 0;
                            const h = parseFloat(invRow.height) || 0;
                            if (n || l || h) measure = `${n}×${l || 1}×${h || 1}`;
                          }
                          if (!measure) measure = '—';
                          const lineTot = getLineTotalForSubParty(invRow, company.gstinType);
                          const checked = Array.isArray(row.lineIndices) && row.lineIndices.includes(cleanedIdx);
                          return (
                            <label
                              key={cleanedIdx}
                              className={`flex items-start gap-2 p-2 rounded border cursor-pointer text-xs sm:text-sm ${
                                checked ? 'border-indigo-400 bg-indigo-50/90' : 'border-gray-200 bg-white'
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="mt-0.5 shrink-0"
                                checked={checked}
                                onChange={() => toggleSubPartyDraftLine(idx, cleanedIdx)}
                              />
                              <span className="min-w-0 flex-1 break-words">
                                <span className="font-medium text-gray-900">{cleanedIdx + 1}. {itemLabel || '—'}</span>
                                <span className="text-gray-600"> · {measure}</span>
                                <span className="block sm:inline sm:ml-1 tabular-nums text-gray-800 font-medium break-words">
                                  {' '}
                                  ({formatCurrency(lineTot, companyCurrency)})
                                </span>
                              </span>
                            </label>
                          );
                        })
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addSubPartyDraftRow}
          className={`${subPartyLineRefs.length === 0 ? 'hidden' : 'mt-3 text-sm text-indigo-600 hover:text-indigo-800 font-medium'}`}
        >
          + Add sub party
        </button>

        <div
          className={`${subPartyLineRefs.length === 0 ? 'hidden' : 'mt-4 p-3 rounded-md text-sm'} ${
            subPartyDraftMatchesGrand || normalizeSubPartyBreakdownForSave(subPartyDraft).length === 0
              ? 'bg-slate-50 text-slate-800'
              : 'bg-amber-50 text-amber-900'
          }`}
        >
          <div className={SUB_PARTY_TOTALS_ROW_CLASS}>
            <span className="font-medium">Sub party total</span>
            <span className="font-semibold tabular-nums text-right">{formatCurrency(subPartyDraftSum, companyCurrency)}</span>
          </div>
          <div className={`${SUB_PARTY_TOTALS_ROW_CLASS} mt-1`}>
            <span className="text-xs">Invoice grand total</span>
            <span className="text-xs tabular-nums font-medium text-right">{formatCurrency(grandTotal, companyCurrency)}</span>
          </div>
          {Math.abs(roundOffAmount) > 0.001 && subPartyLineRefCount > 0 ? (
            <p className="flex flex-wrap items-start gap-1.5 text-[11px] mt-1.5 text-gray-600 leading-snug">
              <span>
                Invoice round-off ({formatCurrency(roundOffAmount, companyCurrency)}) is added to one sub-party when every line is assigned, so totals match the rounded grand total.
              </span>
              <InfoHoverTip text={SUB_PARTY_ROUND_OFF_HELP} placement="bottom" />
            </p>
          ) : null}
          {normalizeSubPartyBreakdownForSave(subPartyDraft).length > 0 && !subPartyDraftMatchesGrand && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs mt-1 font-medium">
              <span>
                Difference: {formatCurrency(roundTo2Decimals(grandTotal - subPartyDraftSum), companyCurrency)} (must be 0)
              </span>
              <InfoHoverTip text={SUB_PARTY_TOTAL_MISMATCH_HELP} placement="bottom" />
            </div>
          )}
        </div>
      </FormModal>

      <StandardModal
        section={MODAL_SECTIONS.SALES}
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        title="Add Note"
        size="2xl"
      >
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          className="w-full h-64 border border-slate-300 rounded-lg shadow-sm p-3 resize-none focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
          placeholder="Enter additional notes..."
          autoFocus
        />
        <div className="mt-2 text-right text-sm text-slate-500">{notes.length} characters</div>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 mt-2 border-t border-slate-100">
          <StandardButton
            variant="secondary"
            onClick={() => setShowNotesModal(false)}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
          >
            Cancel
          </StandardButton>
          <StandardButton
            onClick={() => setShowNotesModal(false)}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
          >
            Save Note
          </StandardButton>
        </div>
      </StandardModal>

      <StandardModal
        section={MODAL_SECTIONS.SALES}
        isOpen={showEwayBillModal}
        onClose={() => setShowEwayBillModal(false)}
        title="Add E-way Bill Details"
        size="2xl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">E-way Bill No</label>
            <input
              type="text"
              value={customFields.ewayBillNo}
              onChange={(e) => setCustomFields((f) => ({ ...f, ewayBillNo: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg shadow-sm p-3 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
              placeholder="Enter E-way Bill number"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">E-way Bill Date</label>
            <input
              type="date"
              value={customFields.ewayDate}
              onChange={(e) => setCustomFields((f) => ({ ...f, ewayDate: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg shadow-sm p-3 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-slate-700 mb-1">QR Code URL</label>
            <input
              type="text"
              value={customFields.ewayQr}
              onChange={(e) => setCustomFields((f) => ({ ...f, ewayQr: e.target.value }))}
              className="w-full border border-slate-300 rounded-lg shadow-sm p-3 focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25"
              placeholder="Enter QR code URL"
            />
          </div>
        </div>
        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 pt-4 mt-4 border-t border-slate-100">
          <StandardButton
            variant="secondary"
            onClick={() => setShowEwayBillModal(false)}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
          >
            Cancel
          </StandardButton>
          <StandardButton
            onClick={() => setShowEwayBillModal(false)}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0"
          >
            Save E-way Bill
          </StandardButton>
        </div>
      </StandardModal>


      <ConfirmationModal
        section={MODAL_SECTIONS.SALES}
        isOpen={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        title="Confirm Bulk Delete"
        message={`Are you sure you want to delete ${selectedForDeletion.size} selected invoice(s)? This action cannot be undone.`}
        onConfirm={handleBulkDelete}
        confirmText={isBulkDeleting ? 'Deleting...' : 'Delete Selected'}
        cancelText="Cancel"
        variant="danger"
        zIndex="z-[99999]"
        confirmDisabled={isBulkDeleting}
      />

      {/* Toast Notifications */}

      {/* 🚦 Subscription Limit Reached Modal */}
      <LimitReachedModal
        isOpen={limitReached}
        onClose={() => setLimitReached(false)}
        limitCheck={limitCheckData}
        onUpgrade={() => {
          setLimitReached(false);
          window.location.href = '/pricing';
        }}
      />

    </div>
    );
}

export default Sales;